import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { showSafeToast, showErrorToast, showSuccessToast, sanitizeError } from '../../utils/safeToast';
import { showLoadingOverlay, hideLoadingOverlay } from '../../utils/safeOverlay';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Image as ImageIcon, Loader2, Grid3x3, Grid2x2, Square, ChevronUp, ChevronDown, Trash2, User, CheckCircle, Sparkles, Wrench, Video, MoreHorizontal, Play, Star, Upload, Clapperboard, Mic, Download, Share2 } from 'lucide-react';
import ImageFullscreenViewer from './ImageFullscreenViewer';
import VideoActionSheet from './VideoActionSheet';
import { getShareSettings } from '../../utils/shareSettings';
import { absoluteUrl } from '../../utils/absoluteUrl';
import {
  getCachedGenerations, setCachedGenerations, isGenerationsCacheStale,
  getCachedVideos, setCachedVideos, isVideosCacheStale, removeFromCache,
  removeImageFromCache, prefetchIdCard, clearCache
} from '../../utils/galleryCache';

// ═══════════════════════════════════════════════════════════════
// LazyChipVideo — Auto-play when visible (Desktop + Mobile).
// Mobile nutzt 360p Proxy (mobileSrc), Desktop nutzt 480p Proxy (src).
// Videos werden nur geladen wenn im Sichtfeld → Performance.
// ═══════════════════════════════════════════════════════════════
const MOBILE_BREAKPOINT = 768;
const isMobileDevice = () => window.innerWidth <= MOBILE_BREAKPOINT || ('ontouchstart' in window);

const LazyChipVideo = React.memo(({ src, mobileSrc, poster, className, style }) => {
  const videoRef = useRef(null);
  const [isMobile] = useState(() => isMobileDevice());
  const effectiveSrc = isMobile ? (mobileSrc || src) : src;

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !effectiveSrc) return;

    // Sofort laden — Chip-Videos sind klein (proxy ~300KB)
    // IntersectionObserver funktioniert nicht zuverlässig in horizontalen Scroll-Containern
    el.src = effectiveSrc;
    el.load();

    const tryPlay = () => { el.play().catch(() => {}); };
    el.addEventListener('loadeddata', tryPlay, { once: true });
    el.addEventListener('canplay', tryPlay, { once: true });

    // Fallback: Nach 2s nochmal versuchen (Safari braucht manchmal länger)
    const fallback = setTimeout(tryPlay, 2000);

    return () => {
      el.removeEventListener('loadeddata', tryPlay);
      el.removeEventListener('canplay', tryPlay);
      clearTimeout(fallback);
    };
  }, [effectiveSrc]);

  return (
    <video
      ref={videoRef}
      poster={poster || undefined}
      preload="metadata"
      autoPlay
      loop
      muted
      playsInline
      webkit-playsinline="true"
      draggable={false}
      className={className}
      style={{ ...style, pointerEvents: 'none', userSelect: 'none' }}
    />
  );
});

// Sanitize URLs that are used as image sources to avoid dangerous schemes.
// CodeQL-compliant: Returns a NEW safe string, not the original input.
function sanitizeUrlForImage(url) {
  if (typeof url !== 'string') {
    return '';
  }
  const trimmed = url.trim();
  if (!trimmed) {
    return '';
  }
  try {
    const parsed = new URL(trimmed, window.location.origin);
    const scheme = parsed.protocol.toLowerCase();
    // Allow only http(s) and same-origin relative URLs.
    if (scheme === 'http:' || scheme === 'https:') {
      // Return the RECONSTRUCTED URL (not the original) - CodeQL sees this as sanitized
      return parsed.href;
    }
    return '';
  } catch (e) {
    // If it's a relative URL without protocol, validate and return safe version
    if (trimmed.startsWith('/') && !trimmed.includes('..') && !trimmed.includes('<') && !trimmed.includes('>')) {
      // Encode special characters to prevent injection
      return encodeURI(trimmed);
    }
    return '';
  }
}

// ═══════════════════════════════════════════════════════════════
// 👑 ADMIN USERS - Haben Zugriff auf Beta-Features (Snapshot Spezial)
// ═══════════════════════════════════════════════════════════════
const ADMIN_EMAILS = ['info@snapshotpro.ai', 'diana@snapshotpro.ai'];

const isAdminUser = () => {
  try {
    const user = JSON.parse(localStorage.getItem('snapshot_user') || '{}');
    return ADMIN_EMAILS.includes(user.email?.toLowerCase());
  } catch {
    return false;
  }
};

// ═══════════════════════════════════════════════════════════════
// 🎬 VIDEO_MODELS CONFIG - Alle verfügbaren Video-Modelle
// ═══════════════════════════════════════════════════════════════
const VIDEO_MODELS = {
  // === KINO MODUS (Standard) ===
  cinematic_video: {
    id: 'cinematic_video',
    label: 'Kino Modus',
    category: 'video',
    duration: { min: 5, max: 15, default: 8 },
    resolutions: ['720p'],
    aspectRatios: null,
    price: 'Premium',
    supportsAudio: false,
    supportsTalking: false,
    needsEndFrame: false,
    isDirect: true,
    description: '95% Face Match — Premium'
  },
  // === SHORTS ===
  grok: {
    id: 'xai/grok-imagine-video/image-to-video',
    label: 'Shorts',
    category: 'video',
    duration: { min: 1, max: 15, default: 7 },
    resolutions: ['480p', '720p'],
    aspectRatios: ['9:16', '16:9', '1:1'],
    price: '$0.07/s',
    supportsAudio: true,
    supportsTalking: true,
    needsEndFrame: false,
    description: 'Schnell & günstig + Audio'
  },
  // === GROK DIREKT (xAI API) - DEAKTIVIERT (CPU Face-Swap zu langsam) ===
  // Später wieder aktivieren für Voice/Audio Features
  // grok_direct: {
  //   id: 'grok-imagine-video',
  //   label: 'Test',
  //   category: 'video',
  //   duration: { min: 1, max: 15, default: 7 },
  //   resolutions: ['480p', '720p'],
  //   aspectRatios: ['9:16', '16:9', '1:1', '4:3', '3:4'],
  //   price: 'xAI direkt',
  //   supportsAudio: true,
  //   supportsTalking: true,
  //   needsEndFrame: false,
  //   description: '🧪 xAI Direct API',
  //   isDirect: true  // Flag für direkte xAI Calls
  // },
  // DEAKTIVIERT: Systematischer Test - nur Grok aktiv
  // kling: {
  //   id: 'fal-ai/kling-video/v3/standard/image-to-video',
  //   label: 'Kling 3.0',
  //   category: 'video',
  //   duration: { min: 5, max: 10, default: 5 },
  //   resolutions: ['720p', '1080p'],
  //   aspectRatios: ['9:16', '16:9', '1:1'],
  //   price: '3 SNAPs',
  //   supportsAudio: true,
  //   supportsTalking: true,
  //   needsEndFrame: false,
  //   description: 'Premium Qualität + Audio'
  // },
  // DEAKTIVIERT: Veo 3.1 - Content-Filter zu streng (2026-02-19)
  //   veo31: {
  //     id: 'fal-ai/veo3.1/image-to-video',
  //     label: '👑 Veo 3.1',
  //     category: 'video',
  //     duration: { options: [4, 6, 8], default: 8 },
  //     resolutions: ['720p', '1080p', '4k'],
  //     aspectRatios: ['9:16', '16:9'],
  //     price: '25 SNAPs',
  //     needsEndFrame: false,
  //     description: 'Google Premium + Audio'
  //   },
  // hailuo: DEAKTIVIERT - Timeout-Probleme bei Cold Start
  // {
  //   id: 'fal-ai/minimax/hailuo-2.3/pro/image-to-video',
  //   label: 'Hailuo 2.3',
  //   category: 'video',
  //   duration: { fixed: 6 },
  //   resolutions: ['720p'],
  //   aspectRatios: null,
  //   price: '$0.15',
  //   needsEndFrame: false,
  //   description: '🏆 Bestes Face-Match'
  // },
  // DEAKTIVIERT: Systematischer Test - nur Grok aktiv
  // pixverse: {
  //   id: 'fal-ai/pixverse/v5.6/image-to-video',
  //   label: 'PixVerse 5.6',
  //   category: 'video',
  //   duration: { options: [5, 8, 10], default: 5 },
  //   resolutions: ['360p', '540p', '720p', '1080p'],
  //   aspectRatios: ['9:16', '16:9', '4:3', '3:4', '1:1'],
  //   supportsAudio: true,
  //   supportsTalking: true,
  //   price: '3 SNAPs',
  //   needsEndFrame: false,
  //   description: 'Kreativ + Audio'
  // },
  
  // === PIXVERSE SPEZIAL ===
  pixverse_transition: {
    id: 'fal-ai/pixverse/v5.6/transition',
    label: 'Übergang',
    category: 'pixverse',
    duration: { options: [5, 8], default: 5 },  // NUR 5 oder 8 erlaubt!
    resolutions: ['720p', '1080p'],
    aspectRatios: ['9:16', '16:9', '4:3', '3:4', '1:1'],
    price: '3 SNAPs',
    needsEndFrame: true,
    description: 'Bild A wird zu Bild B'
  },
  pixverse_camera: {
    id: 'fal-ai/pixverse/v5.6/image-to-video',
    label: 'Kamerafahrt',
    category: 'pixverse',
    duration: { options: [5, 8, 10] },
    resolutions: ['720p', '1080p'],
    aspectRatios: ['9:16'],  // Fast nur Portrait
    price: '2 SNAPs',
    needsEndFrame: false,
    needsCameraMovement: true,
    description: 'Professionelle Kamerabewegung',
    cameraMovements: [
      { id: 'zoom_in', label: 'Zoom Rein', prompt: 'CAMERA ZOOMS IN SLOWLY, push in tight on face, dramatic close-up, the camera moves forward steadily, getting closer and closer to the subject' },
      { id: 'zoom_out', label: 'Zoom Raus', prompt: 'CAMERA ZOOMS OUT SLOWLY, pull back wide shot, the camera moves backward steadily, revealing more and more of the scene, epic wide reveal' },
      { id: 'smooth_zoom_in', label: 'Sanfter Zoom', prompt: 'VERY SLOW GENTLE ZOOM IN, extremely smooth forward drift, dreamy intimate push in, barely noticeable camera creep closer' },
      { id: 'quickly_zoom_in', label: 'Schneller Zoom Rein', prompt: 'FAST DRAMATIC ZOOM IN, rapid punch in to face, quick aggressive push forward, intense sudden close-up, high energy zoom' },
      { id: 'quickly_zoom_out', label: 'Schneller Zoom Raus', prompt: 'FAST DRAMATIC ZOOM OUT, rapid pull back, quick aggressive backward movement, sudden wide shot reveal, high energy zoom out' },
      { id: 'horizontal_left', label: 'Nach Links', prompt: 'CAMERA SLIDES LEFT, horizontal dolly movement to the left, tracking shot moving sideways left, the camera glides smoothly leftward' },
      { id: 'horizontal_right', label: 'Nach Rechts', prompt: 'CAMERA SLIDES RIGHT, horizontal dolly movement to the right, tracking shot moving sideways right, the camera glides smoothly rightward' },
      { id: 'vertical_up', label: 'Nach Oben', prompt: 'CAMERA MOVES UP, vertical tilt upward, the camera rises smoothly, ascending movement, looking up reveal' },
      { id: 'vertical_down', label: 'Nach Unten', prompt: 'CAMERA MOVES DOWN, vertical tilt downward, the camera descends smoothly, lowering movement, looking down reveal' },
      { id: 'pan_left', label: 'Schwenk Links', prompt: 'CAMERA PANS LEFT, rotating sweep to the left, horizontal rotation leftward, the camera turns left on its axis' },
      { id: 'pan_right', label: 'Schwenk Rechts', prompt: 'CAMERA PANS RIGHT, rotating sweep to the right, horizontal rotation rightward, the camera turns right on its axis' },
      { id: 'crane_up', label: 'Kranfahrt Hoch', prompt: 'CRANE SHOT RISING UP, camera elevates high into the air, dramatic upward crane movement, ascending majestically, bird eye view reveal' },
      { id: 'camera_rotation', label: 'Rotation', prompt: 'CAMERA ORBITS AROUND SUBJECT, 360 degree rotation, circling movement around the person, the camera spins around while subject stays centered' },
      { id: 'hitchcock', label: 'Hitchcock Effekt', prompt: 'DOLLY ZOOM VERTIGO EFFECT, zoom in while camera moves backward, Hitchcock style perspective distortion, background warps while subject stays same size' },
      { id: 'whip_pan', label: 'Schneller Schwenk', prompt: 'FAST WHIP PAN, extremely rapid camera sweep, motion blur speed pan, quick snap rotation, high speed horizontal swipe' },
      { id: 'super_dolly_out', label: 'Dolly Raus', prompt: 'DRAMATIC DOLLY OUT, camera pulls back on dolly track, epic backward tracking shot, massive wide reveal, the camera retreats dramatically' },
      { id: 'robo_arm', label: 'Roboter-Arm', prompt: 'ROBOTIC ARM CAMERA MOVEMENT, fluid mechanical multi-axis motion, dynamic swooping camera path, modern commercial style movement' },
      { id: 'left_follow', label: 'Links Folgen', prompt: 'CAMERA FOLLOWS LEFTWARD MOVEMENT, tracking alongside to the left, parallel pursuit shot moving left, the camera accompanies motion leftward' },
      { id: 'right_follow', label: 'Rechts Folgen', prompt: 'CAMERA FOLLOWS RIGHTWARD MOVEMENT, tracking alongside to the right, parallel pursuit shot moving right, the camera accompanies motion rightward' },
      { id: 'fix_bg', label: 'Fester Hintergrund', prompt: 'STATIC LOCKED CAMERA, fixed background, subject moves while camera stays completely still, no camera movement at all, stationary shot' }
    ]
  },
  pixverse_lipsync: {
    id: 'fal-ai/pixverse/lipsync',
    label: 'Sprechend',
    category: 'pixverse',
    duration: { min: 5, max: 30, default: 10 },
    resolutions: ['720p', '1080p'],
    aspectRatios: ['9:16', '16:9', '1:1'],
    price: '4 SNAPs',
    needsEndFrame: false,
    needsText: true,
    needsVoice: true,
    needsVideoInput: true,  // NEU: Braucht ein Video als Input!
    description: 'Wähle ein Video und lass deinen Avatar sprechen',
    voices: [
      { id: 'Auto', label: 'Automatisch' },
      // Favoriten zuerst
      { id: 'Ava', label: 'Malia (weiblich)' },
      { id: 'Julia', label: 'Julia (weiblich)' },
      { id: 'Oliver', label: 'Dion (männlich)' },
      { id: 'Adrian', label: 'Lian (männlich)' },
      { id: 'Jack', label: 'Sebastian (männlich)' },
      { id: 'James', label: 'Ben (männlich)' },
      // Rest
      { id: 'Emily', label: 'Luna (weiblich)' },
      { id: 'Isabella', label: 'Aria (weiblich)' },
      { id: 'Chloe', label: 'Zara (weiblich)' },
      { id: 'Harper', label: 'Leonie (weiblich)' },
      { id: 'Sophia', label: 'Nadia (weiblich)' },
      { id: 'Liam', label: 'Noah (männlich)' },
      { id: 'Mason', label: 'Felix (männlich)' },
      { id: 'Ethan', label: 'Leo (männlich)' }
    ]
  },
  pixverse_effects: {
    id: 'fal-ai/pixverse/v5/effects',
    label: 'TikTok Effekte',
    category: 'pixverse',
    duration: { options: [5, 8, 10], default: 5 },  // NUR diese Werte erlaubt!
    resolutions: ['720p', '1080p'],
    aspectRatios: ['9:16', '16:9', '4:3', '3:4', '1:1'],
    price: '3 SNAPs',
    needsEndFrame: false,
    needsEffect: true,
    description: '90+ virale Effekte',
    effects: [
      // 💋 Romantisch
      { id: 'Kiss Me AI', label: '💋 Kiss Me AI', category: 'Romantisch' },
      { id: 'Kiss', label: '💋 Kuss', category: 'Romantisch' },
      { id: 'Hug', label: '🤗 Umarmung', category: 'Romantisch' },
      { id: 'Warmth of Jesus', label: '✨ Jesus Wärme', category: 'Romantisch' },
      { id: 'Somber Embrace', label: '🫂 Traurige Umarmung', category: 'Romantisch' },
      { id: 'My Girlfriends', label: '👩‍❤️‍👩 Freundinnen', category: 'Romantisch' },
      { id: 'My Boyfriends', label: '👨‍❤️‍👨 Freunde', category: 'Romantisch' },
      // 💪 Transformation
      { id: 'Muscle Surge', label: '💪 Muskeln', category: 'Transformation' },
      { id: 'Baby Face', label: '👶 Baby Gesicht', category: 'Transformation' },
      { id: 'Bald Swipe', label: '👴 Glatze', category: 'Transformation' },
      { id: 'Long Hair Magic', label: '💇 Lange Haare', category: 'Transformation' },
      { id: 'Baby Arrived', label: '🍼 Baby Ankunft', category: 'Transformation' },
      { id: 'Huge Cutie', label: '🥰 Riesen Cutie', category: 'Transformation' },
      // 🐺 Fantasy
      { id: 'Werewolf Rage', label: '🐺 Werwolf', category: 'Fantasy' },
      { id: 'Zombie Mode', label: '🧟 Zombie', category: 'Fantasy' },
      { id: 'Dragon Evoker', label: '🐉 Drache', category: 'Fantasy' },
      { id: 'Thunder God', label: '⚡ Donnergott', category: 'Fantasy' },
      { id: 'Holy Wings', label: '😇 Engelsflügel', category: 'Fantasy' },
      { id: 'Creepy Devil Smile', label: '😈 Teufel', category: 'Fantasy' },
      { id: 'The Tiger Touch', label: '🐯 Tiger', category: 'Fantasy' },
      { id: 'Fin-tastic Mermaid', label: '🧜 Meerjungfrau', category: 'Fantasy' },
      { id: 'Flora Nymph Go', label: '🧚 Flora Nymphe', category: 'Fantasy' },
      { id: 'Beam me up', label: '🛸 Beam me up', category: 'Fantasy' },
      // 👻 Horror
      { id: 'GhostFace Terror', label: '👻 Geist', category: 'Horror' },
      { id: 'Skeletal Bae', label: '💀 Skelett', category: 'Horror' },
      { id: 'Summoning succubus', label: '😈 Succubus', category: 'Horror' },
      { id: 'Halloween Voodoo Doll', label: '🎃 Voodoo', category: 'Horror' },
      // 🔥 Viral
      { id: 'Squid Game', label: '🦑 Squid Game', category: 'Viral' },
      { id: 'Black Myth: Wukong', label: '🐵 Wukong', category: 'Viral' },
      { id: 'Eye Zoom Challenge', label: '👁️ Augen Zoom', category: 'Viral' },
      { id: 'Punch Face', label: '👊 Punch', category: 'Viral' },
      { id: 'BOOM DROP', label: '💥 Boom Drop', category: 'Viral' },
      { id: 'Anything', label: '✨ Anything', category: 'Viral' },
      { id: 'Microwave', label: '📻 Mikrowelle', category: 'Viral' },
      { id: "Who's Arrested?", label: '🚔 Verhaftet', category: 'Viral' },
      { id: 'Sharksnap!', label: '🦈 Hai Snap', category: 'Viral' },
      { id: 'Dust Me Away', label: '💨 Staub', category: 'Viral' },
      { id: 'I Quit', label: '🚪 Ich kündige', category: 'Viral' },
      { id: 'Hitchcock Dolly Zoom', label: '🎬 Dolly Zoom', category: 'Viral' },
      { id: 'Smell the Lens', label: '👃 Riech die Linse', category: 'Viral' },
      { id: 'I believe I can fly', label: '🦅 Fliegen', category: 'Viral' },
      { id: 'Art Zoom Challenge', label: '🎨 Art Zoom', category: 'Viral' },
      { id: 'Earth Zoom', label: '🌍 Erde Zoom', category: 'Viral' },
      { id: 'Hands up', label: '🙌 Hände hoch', category: 'Viral' },
      { id: 'Hand', label: '✋ Hand', category: 'Viral' },
      // 🎨 Kreativ
      { id: '3D Figurine Factor', label: '🎨 3D Figur', category: 'Kreativ' },
      { id: '3D Figurine Factory', label: '🏭 3D Fabrik', category: 'Kreativ' },
      { id: 'Liquid Metal', label: '🪞 Flüssig Metall', category: 'Kreativ' },
      { id: 'Robot', label: '🤖 Roboter', category: 'Kreativ' },
      { id: 'Pixel World', label: '👾 Pixel Welt', category: 'Kreativ' },
      { id: 'Mint in Box', label: '📦 Mint in Box', category: 'Kreativ' },
      { id: 'Tree doll', label: '🌳 Baum Puppe', category: 'Kreativ' },
      // 👗 Mode
      { id: 'Bikini Up', label: '👙 Bikini', category: 'Mode' },
      { id: 'Flora Nymph Gown', label: '🌸 Blumenkleid', category: 'Mode' },
      { id: 'London Elite Vibe', label: '🎩 London Style', category: 'Mode' },
      { id: 'Suit Swagger', label: '🤵 Anzug', category: 'Mode' },
      { id: 'The Exclusive First Class', label: '✈️ First Class', category: 'Mode' },
      // 💪 Fitness
      { id: 'The Bicep Flex', label: '💪 Bizeps Flex', category: 'Fitness' },
      { id: 'Come Feel My Abs', label: '🏋️ Sixpack', category: 'Fitness' },
      { id: 'Leggy Run', label: '🦵 Lauf', category: 'Fitness' },
      // 💃 Tanz
      { id: 'Pole Dance', label: '💃 Pole Dance', category: 'Tanz' },
      { id: 'Vroom Dance', label: '🕺 Vroom Tanz', category: 'Tanz' },
      { id: 'Subject 3 Fever', label: '🎶 Subject 3', category: 'Tanz' },
      { id: 'Strikout Dance', label: '⚾ Strike Tanz', category: 'Tanz' },
      // 🎄 Weihnachten
      { id: 'Christmas Costume', label: '🎅 Weihnachtskostüm', category: 'Weihnachten' },
      { id: "It's Snowy", label: '❄️ Schnee', category: 'Weihnachten' },
      { id: 'Reindeer Cruiser', label: '🦌 Rentier', category: 'Weihnachten' },
      { id: 'Snow Globe Maker', label: '🔮 Schneekugel', category: 'Weihnachten' },
      { id: 'Pet Christmas Outfit', label: '🐕 Tier Outfit', category: 'Weihnachten' },
      { id: 'Adopt a Polar Pal', label: '🐻‍❄️ Eisbär', category: 'Weihnachten' },
      { id: 'Cat Christmas Box', label: '🐱 Katzen Box', category: 'Weihnachten' },
      { id: 'Starlight Gift Box', label: '🎁 Geschenk Box', category: 'Weihnachten' },
      { id: 'Xmas Poster', label: '🖼️ Xmas Poster', category: 'Weihnachten' },
      { id: 'Pet Christmas Tree', label: '🎄 Tier Baum', category: 'Weihnachten' },
      { id: 'City Santa Hat', label: '🎅 Nikolausmütze', category: 'Weihnachten' },
      { id: 'Stocking Sweetie', label: '🧦 Strumpf', category: 'Weihnachten' },
      { id: 'Christmas Night', label: '🌙 Weihnachtsnacht', category: 'Weihnachten' },
      { id: 'Xmas Front Page Karma', label: '📰 Xmas Frontpage', category: 'Weihnachten' },
      { id: "Grinch's Xmas Hijack", label: '💚 Grinch', category: 'Weihnachten' },
      // 📺 Werbung
      { id: '3D Naked-Eye AD', label: '👁️ 3D Naked-Eye', category: 'Werbung' },
      { id: 'Package Explosion', label: '📦 Paket Explosion', category: 'Werbung' },
      { id: 'Dishes Served', label: '🍽️ Serviert', category: 'Werbung' },
      { id: 'Ocean ad', label: '🌊 Ocean AD', category: 'Werbung' },
      { id: 'Supermarket AD', label: '🛒 Supermarkt AD', category: 'Werbung' },
      { id: 'Giant Product', label: '🏢 Riesen Produkt', category: 'Werbung' },
      { id: 'Truck Fashion Shoot', label: '🚚 Truck Fashion', category: 'Werbung' },
      { id: 'Beach AD', label: '🏖️ Beach AD', category: 'Werbung' },
      { id: 'Shoal Surround', label: '🐟 Fischschwarm', category: 'Werbung' },
      { id: 'Mechanical Assembly', label: '⚙️ Mechanik', category: 'Werbung' },
      { id: 'Lighting AD', label: '💡 Licht AD', category: 'Werbung' },
      { id: 'Billboard AD', label: '🪧 Billboard', category: 'Werbung' },
      { id: 'Product close-up', label: '🔍 Produkt Close-up', category: 'Werbung' },
      { id: 'Parachute Delivery', label: '🪂 Fallschirm', category: 'Werbung' },
      { id: 'Dreamlike Cloud', label: '☁️ Traumwolke', category: 'Werbung' },
      { id: 'Macaron Machine', label: '🍪 Macaron', category: 'Werbung' },
      { id: 'Poster AD', label: '📋 Poster AD', category: 'Werbung' },
      { id: 'Truck AD', label: '🚛 Truck AD', category: 'Werbung' },
      { id: 'Graffiti AD', label: '🎨 Graffiti AD', category: 'Werbung' }
    ]
  },
  pixverse_swap: {
    id: 'fal-ai/pixverse/swap',
    label: 'Face-Swap',
    category: 'pixverse',
    duration: { fixed: null },  // Video-Länge vom Input
    resolutions: ['720p'],
    aspectRatios: null,  // Übernimmt vom Input-Video
    price: '3 SNAPs',
    needsEndFrame: false,
    needsVideoUrl: true,
    description: 'Dein Gesicht in jedem Video',
    swapModes: [
      { id: 'person', label: '👤 Gesicht tauschen' },
      { id: 'object', label: '📦 Objekt tauschen' },
      { id: 'background', label: '🏞️ Hintergrund tauschen' }
    ]
  }
};

// 🚀 MEMOIZED VIDEO CARD - Verhindert unnötige Re-Renders
const MemoizedVideoCard = React.memo(({ videoData, posterUrl, onPlay, onSelect, isSelected, isFavorited, onMore }) => (
  <div className="break-inside-avoid mb-1">
    <div className="relative rounded-xl overflow-hidden group">
      {posterUrl && <img src={posterUrl} alt="" className="w-full h-auto" loading="lazy" />}
      {/* Checkbox */}
      <div
        onClick={(e) => { e.stopPropagation(); onSelect(); }}
        className={`absolute top-2 left-2 w-8 h-8 rounded-full flex items-center justify-center cursor-pointer z-20 transition-all ${
          isSelected ? 'bg-red-500 border-2 border-red-500' : 'bg-black/50 border-2 border-white/40'
        }`}
      >
        {isSelected && (
          <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        )}
      </div>
      {/* Stern */}
      {isFavorited && (
        <div className="absolute top-2.5 left-12">
          <Star className="w-5 h-5 text-yellow-400 fill-yellow-400 drop-shadow-lg" />
        </div>
      )}
      {/* Klickbereich für Video öffnen - KEIN Play-Button */}
      <button onClick={onPlay} className="absolute inset-0" />
      {/* Mehr Button */}
      <button
        onClick={(e) => { e.stopPropagation(); onMore(); }}
        className="absolute bottom-2 right-2 p-2 bg-black/50 hover:bg-black/70 rounded-full opacity-70 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity z-10"
      >
        <MoreHorizontal className="w-5 h-5 text-white" />
      </button>
    </div>
  </div>
), (prev, next) =>
  prev.videoData.id === next.videoData.id &&
  prev.isSelected === next.isSelected &&
  prev.isFavorited === next.isFavorited
);

// ========== PENDING GENERATION CARD - Spinner + Prozentzahl ==========
const PendingGenerationCard = ({ startTime, sourceImageUrl, cardId = null }) => {
  const [statusMsg, setStatusMsg] = useState('Prompt wird erstellt...');

  useEffect(() => {
    const start = startTime || Date.now();

    const updateStatus = () => {
      const elapsed = Date.now() - start;
      if (elapsed < 15000) {
        setStatusMsg('Prompt wird erstellt...');
      } else if (elapsed < 40000) {
        setStatusMsg('Bild wird optimiert...');
      } else {
        setStatusMsg('Bild wird generiert...');
      }
    };

    updateStatus();
    const interval = setInterval(updateStatus, 1000);
    return () => clearInterval(interval);
  }, [startTime]);

  return (
    <div id={cardId || undefined} className="break-inside-avoid mb-1">
      <div className="relative rounded-xl overflow-hidden aspect-square">
        {sourceImageUrl ? (
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url(${sourceImageUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              filter: 'blur(12px)',
              transform: 'scale(1.1)'
            }}
          />
        ) : (
          <div className="absolute inset-0 bg-[#1a1a2e]" />
        )}

        <div className="absolute inset-0 bg-black/40" />

        <div
          className="absolute inset-0 pointer-events-none animate-pulse"
          style={{
            backgroundImage: `radial-gradient(circle, rgba(255,255,255,0.35) 1px, transparent 1px)`,
            backgroundSize: '5px 5px'
          }}
        />

        <div className="absolute inset-0 flex flex-col items-center justify-center z-10">
          <div className="w-12 h-12 mb-3 relative">
            <div className="absolute inset-0 border-2 border-white/30 rounded-full" />
            <div
              className="absolute inset-0 border-2 border-t-white border-r-transparent border-b-transparent border-l-transparent rounded-full animate-spin"
              style={{ animationDuration: '1s' }}
            />
          </div>
          <p className="text-white text-sm font-medium drop-shadow-lg">{statusMsg}</p>
        </div>
      </div>
    </div>
  );
};

// ========== VIDEO PROGRESS OVERLAY (klassisches Layout) ==========
const VideoProgressOverlay = ({ startTime }) => {
  const [progress, setProgress] = useState(0);
  const TOTAL_DURATION = 60000;

  useEffect(() => {
    const start = startTime || Date.now();
    const updateProgress = () => {
      const elapsed = Date.now() - start;
      let pct;
      if (elapsed < TOTAL_DURATION) {
        pct = (elapsed / TOTAL_DURATION) * 90;
      } else {
        pct = 90 + Math.min(9, (elapsed - TOTAL_DURATION) / 5000);
      }
      setProgress(Math.round(pct));
    };
    updateProgress();
    const interval = setInterval(updateProgress, 300);
    return () => clearInterval(interval);
  }, [startTime]);

  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center z-40">
      <div className="w-16 h-16 mb-4 relative">
        <div className="absolute inset-0 border-3 border-white/30 rounded-full" />
        <div
          className="absolute inset-0 border-3 border-t-white border-r-transparent border-b-transparent border-l-transparent rounded-full animate-spin"
          style={{ animationDuration: '1s' }}
        />
      </div>
      <p className="text-white font-bold text-lg drop-shadow-lg">Video wird erstellt...</p>
    </div>
  );
};

// ========== PENDING VIDEO CARD (identisch zu PendingGenerationCard, aber 9:16) ==========
const PendingVideoCard = ({ startTime, sourceImageUrl, cardId = null, aspect = '9:16' }) => {
  const [progress, setProgress] = useState(0);
  const [statusText, setStatusText] = useState('Video wird erstellt...');
  const TOTAL_DURATION = 60000;

  useEffect(() => {
    const start = startTime || Date.now();
    const updateProgress = () => {
      const elapsed = Date.now() - start;
      let pct;
      if (elapsed < TOTAL_DURATION) {
        pct = (elapsed / TOTAL_DURATION) * 90;
      } else {
        pct = 90 + Math.min(9, (elapsed - TOTAL_DURATION) / 5000);
      }
      setProgress(Math.round(pct));
    };
    updateProgress();
    const interval = setInterval(updateProgress, 300);
    return () => clearInterval(interval);
  }, [startTime]);

  // Kino-Pipeline Status-Updates empfangen
  useEffect(() => {
    const handleStatusUpdate = (e) => {
      if (e.detail?.text) setStatusText(e.detail.text);
    };
    window.addEventListener('pendingVideoStatus', handleStatusUpdate);
    return () => window.removeEventListener('pendingVideoStatus', handleStatusUpdate);
  }, []);

  return (
    <div id={cardId || undefined} className="break-inside-avoid mb-1">
      <div className="relative rounded-xl overflow-hidden" style={{ aspectRatio: String(aspect || '9:16').replace(':', '/') }}>
        {sourceImageUrl ? (
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url(${sourceImageUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              filter: 'blur(12px)',
              transform: 'scale(1.1)'
            }}
          />
        ) : (
          <div className="absolute inset-0 bg-[#1a1a2e]" />
        )}
        <div className="absolute inset-0 bg-black/40" />
        <div
          className="absolute inset-0 pointer-events-none animate-pulse"
          style={{
            backgroundImage: `radial-gradient(circle, rgba(255,255,255,0.35) 1px, transparent 1px)`,
            backgroundSize: '5px 5px'
          }}
        />
        <div className="absolute inset-0 flex flex-col items-center justify-center z-10">
          <div className="w-12 h-12 mb-3 relative">
            <div className="absolute inset-0 border-2 border-white/30 rounded-full" />
            <div
              className="absolute inset-0 border-2 border-t-white border-r-transparent border-b-transparent border-l-transparent rounded-full animate-spin"
              style={{ animationDuration: '1s' }}
            />
          </div>
          <p className="text-white text-sm font-medium drop-shadow-lg text-center px-3">{statusText}</p>
        </div>
      </div>
    </div>
  );
};

// ========== VIDEO FULLSCREEN PLAYER (Clean Fullscreen) ==========
const VideoFullscreenPlayer = ({ videoUrl, onClose }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true);

  const togglePlay = () => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play().catch(() => {});
        setIsPlaying(true);
      } else {
        videoRef.current.pause();
        setIsPlaying(false);
      }
    }
  };

  return createPortal(
    <div 
      className="fixed inset-0 bg-black z-[100000]"
      style={{ touchAction: 'none' }}
      onClick={togglePlay}
    >
      {/* Header mit Zurück-Button */}
      <div 
        className="absolute top-4 left-4 z-20" 
        style={{ marginTop: 'env(safe-area-inset-top)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="w-12 h-12 rounded-full bg-black/50 backdrop-blur flex items-center justify-center"
        >
          <span className="text-white text-xl">←</span>
        </button>
      </div>
      
      {/* Video zentriert - freistehend, tap anywhere to play/pause */}
      <div className="absolute inset-0 flex items-center justify-center">
        <video
          ref={videoRef}
          src={videoUrl}
          autoPlay
          loop
          playsInline
          muted
          className="w-full h-full object-cover"
        />
      </div>
    </div>,
    document.body
  );
};

// ========== VIDEO GENERATOR FULLSCREEN (GROK-Style Pixel-Reveal) ==========
// existingVideo: Optional - wenn gesetzt, zeigt Modal direkt das fertige Video
const VideoGeneratorFullscreen = ({ image, onClose, onGenerate, avatarId, existingVideo, onToggleFavorite, onDelete, onVideoCompleted, onVideoReady, preloadedPhotos = [], inline = false, preselectedPrompt = null, preselectedMotionTags = null, preselectedVideoSettings = null, preselectedModel = null, onPromptConsumed = null, preselectedImageFirst = null, demoPromptText = null, disableAutofocus = false, hideModelSelector = false }) => {
  const [selectedMotions, setSelectedMotions] = useState([]);
  const [previewFullscreen, setPreviewFullscreen] = useState(null); // {id, label, videoUrl, category}
  const [previewMuted, setPreviewMuted] = useState(true); // Sound im Preview Fullscreen
  // Template-Prompt als Initial-Wert wenn vorhanden
  const [customPrompt, setCustomPrompt] = useState(image?.templatePrompt || existingVideo?.video_prompt || '');
  const [isGenerating, setIsGenerating] = useState(false);
  // Wenn existingVideo, starte direkt mit dem Video
  const [generatedVideoUrl, setGeneratedVideoUrl] = useState(existingVideo?.video_url || null);
  const [error, setError] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [isFavorited, setIsFavorited] = useState(false);
  const [isVideoFullscreen, setIsVideoFullscreen] = useState(false);

  // Video Settings
  const [selectedModel, setSelectedModel] = useState('cinematic_video');
  const [videoDuration, setVideoDuration] = useState(7);
  const [videoResolution, setVideoResolution] = useState('480p');
  const [videoAspectRatio, setVideoAspectRatio] = useState('9:16');
  const [endFrameUrl, setEndFrameUrl] = useState(null);
  const [endFramePreview, setEndFramePreview] = useState(null);
  const [isUploadingEndFrame, setIsUploadingEndFrame] = useState(false);
  const [isUploadingCustomImage, setIsUploadingCustomImage] = useState(false);
  const [uploadedImageUrl, setUploadedImageUrl] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [activePrompt, setActivePrompt] = useState(''); // Prompt der gerade generiert wird
  
  // PixVerse Spezial-Features
  const [selectedCameraMovement, setSelectedCameraMovement] = useState('zoom_in');
  const [selectedVoice, setSelectedVoice] = useState('Auto');
  const [lipsyncText, setLipsyncText] = useState('');
  const [selectedEffect, setSelectedEffect] = useState('Kiss Me AI');
  const [selectedSwapMode, setSelectedSwapMode] = useState('person');
  const [videoInputUrl, setVideoInputUrl] = useState('');
  const [availableVideos, setAvailableVideos] = useState([]); // Videos für Lipsync-Picker
  // Direkt von preloadedPhotos abgeleitet — kein State, kein async Delay
  // → Slider ist IMMER synchron wenn preloadedPhotos sich ändert (z.B. nach Löschen)
  const availablePhotos = useMemo(() => {
    if (!preloadedPhotos || preloadedPhotos.length === 0) return [];
    if (!preselectedImageFirst) return preloadedPhotos;
    const idx = preloadedPhotos.findIndex(p => p.url === preselectedImageFirst);
    if (idx <= 0) return preloadedPhotos;
    return [preloadedPhotos[idx], ...preloadedPhotos.slice(0, idx), ...preloadedPhotos.slice(idx + 1)];
  }, [preloadedPhotos, preselectedImageFirst]);
  const [selectedSourceImage, setSelectedSourceImage] = useState(image?.url || null); // Überschreibt image.url wenn User anderes Foto wählt
  const [imageAspectWarning, setImageAspectWarning] = useState(false); // Warnung wenn Bild nicht 9:16
  const [landscapePhotos, setLandscapePhotos] = useState(new Set()); // Breitbild-Fotos tracken
  const [playingVoice, setPlayingVoice] = useState(null); // Aktuell spielende Stimmen-Vorschau
  const [talkingText, setTalkingText] = useState(''); // Text für Talking-Chip (Grok spricht diesen Text)
  const [isOptimizingTTS, setIsOptimizingTTS] = useState(false); // Loading state für TTS-Optimierung
  const voiceAudioRef = useRef(null); // Audio-Element für Stimmen-Preview
  const photoSliderRef = useRef(null); // Ref für Photo-Slider Auto-Scroll
  const kinoTextareaRef = useRef(null); // Ref für Drehbuch-Textfeld Auto-Fokus

  // Wenn image prop sich ändert (z.B. User wählt neues Bild aus Galerie), selectedSourceImage aktualisieren
  useEffect(() => {
    if (image?.url) {
      setSelectedSourceImage(image.url);
    }
  }, [image?.url]);

// Kino Modus: Drehbuch-Textfeld automatisch fokussieren
  useEffect(() => {
    if (disableAutofocus) return undefined;
    if (selectedModel === 'cinematic_video') {
      setTimeout(() => kinoTextareaRef.current?.focus({ preventScroll: true }), 200);
    }
    return undefined;
  }, [disableAutofocus, selectedModel]);

  // Vorausgewählter Prompt + Chips + Settings aus History übernehmen
  useEffect(() => {
    if (preselectedPrompt) {
      setCustomPrompt(preselectedPrompt);
      // Auch Kino-Textfeld befüllen falls Kino-Modus vorgewählt wird
      if (preselectedModel === 'cinematic_video') {
        setKinoText(preselectedPrompt);
      }
      if (preselectedMotionTags && preselectedMotionTags.length > 0) {
        setSelectedMotions(preselectedMotionTags);
      }
      if (preselectedVideoSettings) {
        if (preselectedVideoSettings.duration) setVideoDuration(preselectedVideoSettings.duration);
        if (preselectedVideoSettings.resolution) setVideoResolution(preselectedVideoSettings.resolution);
        if (preselectedVideoSettings.aspectRatio) setVideoAspectRatio(preselectedVideoSettings.aspectRatio);
      }
      // Video-Modus setzen (Kino oder Basic)
      if (preselectedModel && VIDEO_MODELS[preselectedModel]) {
        handleModelChange(preselectedModel);
      }
      if (onPromptConsumed) onPromptConsumed();
    }
  }, [preselectedPrompt, preselectedMotionTags, preselectedVideoSettings, preselectedModel, onPromptConsumed]);

  // Eigenständiger Modell-Sync: greift auch dann, wenn kein Prompt vorgewählt
  // ist (typisch beim Tab-Wechsel im Picker oben — Modell muss umschalten,
  // aber Drehbuch/Prompt sollen unangetastet bleiben).
  useEffect(() => {
    if (preselectedModel && VIDEO_MODELS[preselectedModel] && preselectedModel !== selectedModel) {
      handleModelChange(preselectedModel);
    }
  }, [preselectedModel]);

  useEffect(() => {
    if (demoPromptText === null) return;
    if (selectedModel === 'cinematic_video') {
      setKinoText(demoPromptText);
    } else {
      setCustomPrompt(demoPromptText);
    }
  }, [demoPromptText, selectedModel]);

  // 🎬 KINO-MODUS
  const [cinemaGenres, setCinemaGenres] = useState([]);
  const [cinemaLoading, setCinemaLoading] = useState(false);
  const [selectedCinemaGenre, setSelectedCinemaGenre] = useState(null);

  // 🎬 KINO-MODUS: LipSync Pipeline (ehemals Sprechender Avatar)
  const [kinoText, setKinoText] = useState('');
  const [kinoGenerating, setKinoGenerating] = useState(false);
  const [kinoDisplayProgress, setKinoDisplayProgress] = useState(0);
  const [kinoJob, setKinoJob] = useState(null);
  const [kinoError, setKinoError] = useState(null);
  const kinoPollingRef = useRef(null);

  // 🎬 DYNAMISCHE VIDEO-TEMPLATES (aus Admin Panel)
  const [videoTemplates, setVideoTemplates] = useState({
    motion: [],
    trend: [],
    camera: [],
    global_prompt: '',
    base_motion: '',
    talking_lipsync_prompt: '',
    models: {}
  });
  const [sectionTitles, setSectionTitles] = useState({
    motion: 'Bewegung',
    camera: 'Kamerafahrt', 
    trends: '🔥 Trend Effekte',
    models: 'KI-Modell'
  });
  const [promptSettings, setPromptSettings] = useState({
    label: 'Dein Prompt',
    placeholder: 'Beschreibe dein Video...'
  });
  const [templatesLoaded, setTemplatesLoaded] = useState(false);

  // Auto-Scroll: Nur wenn User aktiv ein neues Bild auswählt (NICHT bei availablePhotos-Änderung!)
  // availablePhotos entfernt aus Dependencies — sonst scrollt die Seite bei jedem Polling-Update
  const prevSelectedRef = useRef(selectedSourceImage);
  useEffect(() => {
    // Nur scrollen wenn sich die Auswahl tatsächlich geändert hat (User-Klick)
    if (prevSelectedRef.current === selectedSourceImage) return;
    prevSelectedRef.current = selectedSourceImage;
    if (!selectedSourceImage || !photoSliderRef.current || isGenerating) return;

    // Nur horizontaler Inline-Scroll im Slider — KEIN Page-Scroll!
    const timer = setTimeout(() => {
      const container = photoSliderRef.current;
      if (!container) return;
      const selectedBtn = container.querySelector('[data-selected="true"]');
      if (selectedBtn) {
        // scrollLeft statt scrollIntoView → verhindert Page-Scroll nach oben
        const scrollTarget = selectedBtn.offsetLeft - container.offsetWidth / 2 + selectedBtn.offsetWidth / 2;
        container.scrollTo({ left: scrollTarget, behavior: 'smooth' });
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [selectedSourceImage, isGenerating]);

  // 🎬 Templates aus Admin Panel laden
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const response = await fetch('/api/video-templates');
        if (response.ok) {
          const data = await response.json();
          setVideoTemplates(data);
          setTemplatesLoaded(true);
          console.log('🎬 Video templates loaded from Admin Panel:', data.motion?.length, 'motion,', data.trend?.length, 'trend,', data.camera?.length, 'camera,', Object.keys(data.models || {}).length, 'models');
        }
      } catch (error) {
        console.error('Error loading video templates:', error);
      }
    };
    loadTemplates();
    
    // Lade auch Section Titles
    const loadSiteSettings = async () => {
      try {
        const response = await fetch("/api/site-settings");
        if (response.ok) {
          const data = await response.json();
          if (data.section_titles) setSectionTitles(data.section_titles);
          if (data.prompt_settings) setPromptSettings(data.prompt_settings);
          console.log("📝 Site settings loaded:", data);
        }
      } catch (error) {
        console.error("Error loading site settings:", error);
      }
    };
    loadSiteSettings();

    // 🎬 Kino-Modus Genres laden
    const loadCinemaGenres = async () => {
      try {
        const res = await fetch('/api/admin/cinema/public/genres');
        if (res.ok) {
          const data = await res.json();
          setCinemaGenres((data.genres || []).filter(g => g.is_active));
        }
      } catch (err) {}
    };
    loadCinemaGenres();
  }, []);

  // 🎬 Kino-Modus: Bild analysieren und cinematischen Prompt generieren
  const handleCinemaAnalyze = async (genre) => {
    const imageUrl = selectedSourceImage || image?.url;
    if (!imageUrl) return;

    setCinemaLoading(true);
    setSelectedCinemaGenre(genre);
    try {
      const token = getToken();
      const res = await fetch('/api/beatleap/analyze-cinema', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ image_url: imageUrl, genre })
      });
      const data = await res.json();
      if (data.success) {
        setCustomPrompt(data.cinema_prompt);
      }
    } catch (err) {
      console.error('Cinema analyze error:', err);
    } finally {
      setCinemaLoading(false);
      setSelectedCinemaGenre(null);
    }
  };

  // 🎬 KINO-MODUS: LipSync Pipeline Cleanup
  useEffect(() => {
    return () => { if (kinoPollingRef.current) clearInterval(kinoPollingRef.current); };
  }, []);

  const startKinoPolling = (jobId) => {
    if (kinoPollingRef.current) clearInterval(kinoPollingRef.current);
    kinoPollingRef.current = setInterval(async () => {
      try {
        const token = getToken();
        const res = await fetch(`/api/lipsync/${jobId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setKinoJob(data);
          // Status-Text an Pending-Card in der Galerie senden
          const statusMsg = data.status_message || (data.status === 'analyzing' ? 'Bild wird analysiert...' : data.status === 'prompting' ? 'Prompt wird erstellt...' : data.status === 'generating' ? 'Video wird generiert...' : 'Wird verarbeitet...');
          window.dispatchEvent(new CustomEvent('pendingVideoStatus', { detail: { text: statusMsg } }));
          if (data.status === 'completed' && data.video_url) {
            clearInterval(kinoPollingRef.current);
            kinoPollingRef.current = null;
            setKinoGenerating(false);
            // Fertiges Video-Objekt mitschicken für nahtlosen Eintausch
            const completedVideo = {
              id: data.id || data.lipsync_id,
              video_url: data.video_url,
              video_prompt: data.generated_script || '',
              source_image_url: selectedSourceImage || image?.url,
              created_at: new Date().toISOString(),
              cost_snaps: data.cost_snaps,
              status: 'completed',
              video_model: 'kino_modus',
              source_generation_id: data.source_generation_id,
              image_index: data.image_index || 0,
            };
            window.dispatchEvent(new CustomEvent('lipsyncVideoCompleted', { detail: completedVideo }));

            // Galerie aktualisieren (wie Basic-Modus)
            if (onVideoCompleted) onVideoCompleted();

            // VideoActionSheet öffnen (wie Basic-Modus)
            if (onVideoReady) {
              setTimeout(() => {
                onVideoReady({
                  id: data.id || data.lipsync_id,
                  video_url: data.video_url,
                  video_prompt: data.generated_script || '',
                  user_prompt: data.text || '',
                  source_image_url: selectedSourceImage || image?.url,
                  created_at: new Date().toISOString(),
                  cost_snaps: data.cost_snaps,
                  video_aspect_ratio: videoAspectRatio,
                  video_resolution: videoResolution,
                  motion_tags: ['Kino Modus']
                });
              }, 50);
            }

            // Felder leeren fuer naechste Generation
            setKinoJob(null);
            setKinoText('');
          } else if (data.status === 'failed') {
            clearInterval(kinoPollingRef.current);
            kinoPollingRef.current = null;
            setKinoGenerating(false);
            setKinoError(data.error || 'Fehlgeschlagen');
          }
        }
      } catch (e) { /* ignore */ }
    }, 3000);
  };

  const handleKinoGenerate = async () => {
    const imageUrl = selectedSourceImage || image?.url;
    if (!imageUrl || !kinoText.trim()) return;
    setKinoGenerating(true);
    setKinoError(null);
    setKinoJob(null);

    // SOFORT: Galerie scrollt zum Video-Tab + Pending-Animation
    if (onGenerate) onGenerate();

    // Button nach 2s wieder freigeben
    setTimeout(() => {
      setKinoGenerating(false);
    }, 2000);

    try {
      // Aspect Ratio erkennen (mit Timeout, damit UI nicht blockiert)
      let aspectRatio = '16:9';
      try {
        const img = new window.Image();
        await Promise.race([
          new Promise((resolve) => { img.onload = resolve; img.onerror = resolve; img.src = imageUrl; }),
          new Promise((resolve) => setTimeout(resolve, 500))
        ]);
        if (img.width && img.height) aspectRatio = img.height > img.width * 1.2 ? '9:16' : '16:9';
      } catch (e) {}

      // generationId aus availablePhotos finden
      const matchingPhoto = availablePhotos.find(p => p.url === imageUrl);
      const generationId = matchingPhoto?.id?.split('_')[0] || null;
      const imageIndex = matchingPhoto?.id ? parseInt(matchingPhoto.id.split('_')[1] || '0') : 0;

      const token = getToken();
      const bodyData = {
        text: kinoText.trim(),
        aspect_ratio: aspectRatio,
      };
      // Hochgeladenes Bild: direkte URL statt generation_id
      // Robust erkennen: entweder Match mit uploadedImageUrl ODER URL ist eine Reference-Image-URL
      const isUploadedImage =
        (uploadedImageUrl && imageUrl === uploadedImageUrl) ||
        /\/api\/reference-images\//.test(imageUrl);
      if (isUploadedImage) {
        bodyData.source_image_url = imageUrl;
        bodyData.generation_id = '__uploaded__';
        bodyData.image_index = 0;
      } else if (generationId) {
        bodyData.generation_id = generationId;
        bodyData.image_index = imageIndex;
      } else {
        // Fallback: kein Generation-Match gefunden → als direkte URL senden
        bodyData.source_image_url = imageUrl;
        bodyData.generation_id = '__uploaded__';
        bodyData.image_index = 0;
      }

      const res = await fetch('/api/video-pipeline/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(bodyData)
      });

      if (!res.ok) {
        const errData = await res.json();
        const detail = errData?.detail || 'Fehlgeschlagen';
        setKinoError(typeof detail === 'string' ? detail : 'Fehlgeschlagen');
        setKinoGenerating(false);
        return;
      }

      const data = await res.json();
      setKinoJob(data);
      startKinoPolling(data.id || data.lipsync_id);

      // Textfeld sofort leeren — User kann gleich die naechste Generation starten
      setKinoText('');
    } catch (err) {
      setKinoError('Verbindungsfehler. Bitte versuche es erneut.');
      setKinoGenerating(false);
    }
  };

  // 📤 Eigenes Bild hochladen für Kino Modus
  const handleCustomImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.match(/^image\/(jpeg|png|webp|heic|heif)/i)) {
      setKinoError('Bitte ein Bild im Format JPG, PNG oder WebP hochladen.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setKinoError('Das Bild darf maximal 10 MB groß sein.');
      return;
    }
    setIsUploadingCustomImage(true);
    setKinoError(null);
    try {
      const token = getToken();
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch('/api/reference-image/upload', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (!res.ok) throw new Error('Upload fehlgeschlagen');
      const data = await res.json();
      if (data.success && data.url) {
        setUploadedImageUrl(data.url);
        setSelectedSourceImage(data.url);
      }
    } catch (err) {
      setKinoError('Upload fehlgeschlagen. Bitte versuche es erneut.');
    } finally {
      setIsUploadingCustomImage(false);
    }
  };

  // 🎥 Merge Admin Panel Modell-Config mit hardcoded VIDEO_MODELS
  const getModelConfig = (modelKey) => {
    const baseConfig = VIDEO_MODELS[modelKey];
    const adminConfig = videoTemplates.models?.[modelKey];
    if (!baseConfig) return null;
    if (!adminConfig) return baseConfig;
    // Admin Panel überschreibt: enabled, label, price, description
    return {
      ...baseConfig,
      label: adminConfig.label || baseConfig.label,
      price: adminConfig.price || baseConfig.price,
      description: adminConfig.description || baseConfig.description,
      enabled: adminConfig.enabled !== false
    };
  };
  
  // Aktive Modelle (nur enabled)
  const activeModels = Object.keys(VIDEO_MODELS).filter(key => {
    const config = getModelConfig(key);
    return config && config.enabled !== false;
  });
  
  // Aktives Modell aus Config (mit Admin Panel Overrides)
  const activeModel = getModelConfig(selectedModel) || VIDEO_MODELS[selectedModel];
  
  // Format time helper (0:00)
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Model-Wechsel Handler - aktualisiert Duration/Resolution automatisch
  const handleModelChange = (modelKey) => {
    // Prompt zwischen Basic und Kino übertragen
    if (modelKey === 'cinematic_video' && selectedModel !== 'cinematic_video') {
      // Basic → Kino: customPrompt in kinoText übernehmen
      if (customPrompt && !kinoText) setKinoText(customPrompt);
    } else if (modelKey !== 'cinematic_video' && selectedModel === 'cinematic_video') {
      // Kino → Basic: kinoText in customPrompt übernehmen
      if (kinoText && !customPrompt) setCustomPrompt(kinoText);
    }
    setSelectedModel(modelKey);
    const model = VIDEO_MODELS[modelKey];
    
    // Duration anpassen - je nach Typ
    if (model.duration.fixed !== undefined) {
      setVideoDuration(model.duration.fixed || 5);
    } else if (model.duration.options) {
      // Bei options: Default oder erster Wert
      setVideoDuration(model.duration.default || model.duration.options[0]);
    } else if (model.duration.default) {
      setVideoDuration(model.duration.default);
    }
    
    // Resolution auf erstes verfügbares setzen
    setVideoResolution(model.resolutions[0]);
    
    // Aspect Ratio auf erstes verfügbares setzen (oder auto als Default)
    if (model.aspectRatios && model.aspectRatios.length > 0) {
      setVideoAspectRatio(model.aspectRatios[0]);
    } else {
      setVideoAspectRatio('auto');
    }
    
    // End-Frame nur für Transition behalten
    if (!model.needsEndFrame) {
      setEndFrameUrl(null);
      setEndFramePreview(null);
    }
    
    // Defaults für PixVerse Spezial-Features setzen
    if (model.cameraMovements) {
      setSelectedCameraMovement(model.cameraMovements[0].id);
    }
    if (model.voices) {
      setSelectedVoice(model.voices[0].id);
    }
    if (model.effects) {
      setSelectedEffect(model.effects[0].id);
    }
    if (model.swapModes) {
      setSelectedSwapMode(model.swapModes[0].id);
    }

    // Erstes passendes Foto vorauswählen (Portrait für Basic, Landscape für Kino)
    const filtered = availablePhotos.filter(p =>
      modelKey === 'cinematic_video' ? landscapePhotos.has(p.url) : !landscapePhotos.has(p.url)
    );
    if (filtered.length > 0) {
      setSelectedSourceImage(filtered[0].url);
    }

    // Kino Modus: Drehbuch-Textfeld fokussieren (ohne Scroll-Sprung)
    if (modelKey === 'cinematic_video') {
      setTimeout(() => kinoTextareaRef.current?.focus({ preventScroll: true }), 150);
    }
  };
  
  // End-Frame Upload Handler (für PixVerse Transition)
  const handleEndFrameUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setIsUploadingEndFrame(true);
    
    // Preview sofort zeigen
    const previewUrl = URL.createObjectURL(file);
    setEndFramePreview(previewUrl);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const token = getToken();
      const response = await fetch('/api/upload/temp', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      const data = await response.json();
      if (data.url) {
        setEndFrameUrl(data.url);
      } else {
        throw new Error('Upload fehlgeschlagen');
      }
    } catch (err) {
      console.error('End-Frame Upload Fehler:', err);
      setEndFramePreview(null);
      setStatusMessage('Bild-Upload fehlgeschlagen');
    } finally {
      setIsUploadingEndFrame(false);
    }
  };

  // Load favorite status for existingVideo
  useEffect(() => {
    if (existingVideo?.id) {
      const checkFavorite = async () => {
        try {
          const token = getToken();
          const response = await fetch('/api/favorites/list?item_type=video', {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const data = await response.json();
            console.log('🌟 Check favorite for video:', existingVideo.id, 'in', data.videos);
            setIsFavorited(data.videos?.includes(existingVideo.id) || false);
          }
        } catch (e) {
          console.error('Failed to check favorite:', e);
        }
      };
      checkFavorite();
    }
  }, [existingVideo?.id]);
  const [playbackRate, setPlaybackRate] = useState(existingVideo?.original_speed || 1);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isSavingSpeed, setIsSavingSpeed] = useState(false);
  const [isMuted, setIsMuted] = useState(true); // Startet muted für Autoplay, wird nach Play sofort unmuted
  const videoRef = useRef(null);
  const pollIntervalRef = useRef(null);
  
  // Videos für Lipsync laden wenn pixverse_lipsync ausgewählt
  useEffect(() => {
    if (selectedModel === 'pixverse_lipsync' && avatarId) {
      const fetchVideos = async () => {
        try {
          const token = getToken();
          const response = await fetch(`/api/videos/list?avatar_id=${avatarId}&limit=500`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const data = await response.json();
            setAvailableVideos(data.videos?.filter(v => v.status === 'completed' && v.video_url) || []);
          }
        } catch (e) {
          console.error('Failed to fetch videos for lipsync:', e);
        }
      };
      fetchVideos();
    }
  }, [selectedModel, avatarId]);

  // Fotos für Standard-Modelle - SOFORT aus preloadedPhotos wenn vorhanden
  // Wenn preloadedPhotos sich ändert: selectedSourceImage prüfen + Landscape-Erkennung
  useEffect(() => {
    if (preloadedPhotos && preloadedPhotos.length > 0) {
      // Prüfe ob das aktuell ausgewählte Bild noch existiert (nach Löschung)
      if (selectedSourceImage) {
        const stillExists = preloadedPhotos.some(p => p.url === selectedSourceImage);
        if (!stillExists) {
          setSelectedSourceImage(preloadedPhotos[0]?.url || null);
        }
      }

      // Orientierung aller Fotos vorab erkennen, dann erstes passendes auswählen
      const detectedLandscape = new Set();
      let loaded = 0;
      const total = preloadedPhotos.length;

      preloadedPhotos.forEach((photo) => {
        const img = new window.Image();
        img.onload = () => {
          if (img.naturalWidth > img.naturalHeight) {
            detectedLandscape.add(photo.url);
            setLandscapePhotos(prev => { const next = new Set(prev); next.add(photo.url); return next; });
          }
          loaded++;
          // Wenn alle geladen: erstes passendes Foto wählen
          if (loaded === total && !image?.url && !selectedSourceImage) {
            const isKino = selectedModel === 'cinematic_video';
            if (isKino) {
              const landscapePhotos = preloadedPhotos.filter(p => detectedLandscape.has(p.url));
              setSelectedSourceImage(landscapePhotos.length > 0 ? landscapePhotos[0].url : preloadedPhotos[0].url);
            } else {
              setSelectedSourceImage(preloadedPhotos[0].url);
            }
          }
        };
        img.onerror = () => { loaded++; };
        img.src = photo.url;
      });
    } else if (preloadedPhotos && preloadedPhotos.length === 0) {
      setSelectedSourceImage(null);
    }
  }, [preloadedPhotos]);

  // Format-Prüfung: Warnung wenn Bild nicht 9:16
  useEffect(() => {
    const imageUrl = selectedSourceImage || image?.url;
    if (!imageUrl) {
      setImageAspectWarning(false);
      return;
    }
    
    const img = new Image();
    img.onload = () => {
      const ratio = img.width / img.height;
      // 9:16 = 0.5625, Toleranz ±15%
      const is9x16 = ratio >= 0.48 && ratio <= 0.65;
      setImageAspectWarning(!is9x16);
    };
    img.onerror = () => setImageAspectWarning(false);
    img.src = imageUrl;
  }, [selectedSourceImage, image?.url]);

  // KI-OPTIMIERER States
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [optimizedPrompt, setOptimizedPrompt] = useState('');
  const [imageAnalysis, setImageAnalysis] = useState(existingVideo?.image_analysis || '');
  const hasOptimizedRef = useRef(false);

  // ORIGINAL ENGLISCH - wird IMMER an FAL.ai gesendet!
  const [promptEnglish, setPromptEnglish] = useState('');
  const [combinedPromptEN, setCombinedPromptEN] = useState(''); // Englische Version
  const [combinedPromptDE, setCombinedPromptDE] = useState(''); // Deutsche Version
  const [combinedPromptLang, setCombinedPromptLang] = useState('en'); // Aktuelle Anzeige: 'en' oder 'de'
  const [analysisEnglish, setAnalysisEnglish] = useState('');
  const [isTranslated, setIsTranslated] = useState(false); // true = DE angezeigt

  // KI-OPTIMIERER: NICHT automatisch - User muss Button drücken!
  // (Alter Auto-Start entfernt auf Sebastian's Wunsch 2026-02-16)

  const runKiOptimizer = async () => {
    setIsOptimizing(true);
    setStatusMessage('KI analysiert Bild...');
    setImageAnalysis('');
    setCustomPrompt('');

    try {
      const token = getToken();
      const response = await fetch('/api/beatleap/analyze-image', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          image_url: selectedSourceImage || image?.url
        })
      });

      const data = await response.json();

      if (data.success) {
        // BEIDE Felder setzen!
        setImageAnalysis(data.image_analysis);
        setOptimizedPrompt(data.movement_prompt_en);
        setCustomPrompt(data.movement_prompt_en);

        // ORIGINAL ENGLISCH speichern - für FAL.ai!
        setPromptEnglish(data.movement_prompt_en);
        setAnalysisEnglish(data.image_analysis);
        setIsTranslated(false); // Zeigt aktuell Englisch

        setStatusMessage(`Analyse: ${data.analysis_words} Wörter | Bewegung: ${data.movement_words} Wörter`);
      } else {
        setStatusMessage('KI-Optimierer nicht verfügbar');
      }
    } catch (err) {
      console.error('KI-Optimierer Fehler:', err);
      setStatusMessage('Fehler bei der Analyse');
    } finally {
      setIsOptimizing(false);
    }
  };

  // 🎬 DYNAMISCHE MOTION TEMPLATES (aus Admin Panel)
  const motionTemplates = videoTemplates.motion?.length > 0 ? videoTemplates.motion : [
    { id: 'talking', label: '🎤 Talking', prompt: 'PERSON SPEAKING AND TALKING DIRECTLY TO CAMERA', isTalking: true },
    { id: 'smile', label: 'Lächeln', prompt: 'GENTLE WARM SMILE FORMING NATURALLY' },
  ];

  // 🔥 DYNAMISCHE TREND EFFEKTE (aus Admin Panel)
  const trendEffects = videoTemplates.trend?.length > 0 ? videoTemplates.trend : [
    { id: 'glitter_rain', label: 'Glitter Regen', prompt: 'GOLDEN GLITTER PARTICLES raining down' },
  ];

  // 🎥 DYNAMISCHE KAMERA TEMPLATES (aus Admin Panel)
  const cameraTemplates = videoTemplates.camera?.length > 0 ? videoTemplates.camera : [
    { id: 'zoom_in', label: 'Zoom Rein', prompt: 'CAMERA ZOOMS IN SLOWLY' },
  ];

  // 🛡️ GLOBALE PROMPTS aus Admin Panel
  const BASE_MOTION = videoTemplates.base_motion ?? '';  // Leer wenn im Admin Panel leer gesetzt
  const FACE_PROTECTION = videoTemplates.global_prompt ?? '';  // Leer wenn im Admin Panel leer gesetzt
  const TALKING_LIPSYNC = videoTemplates.talking_lipsync_prompt || 'PERSON SPEAKING DIRECTLY TO CAMERA WITH CLEAR LIP SYNC, mouth moving naturally forming each word, lips perfectly synchronized with speech audio, visible articulation of every syllable, jaw and mouth movements matching spoken words, realistic talking head animation';

  const toggleMotion = (id) => {
    setSelectedMotions(prev =>
      prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id]
    );
  };

  const getFullPrompt = (overrideEnglishPrompt = null) => {
    // ═══════════════════════════════════════════════════════════════
    // GOLDENE REIHENFOLGE (2026 Best Practice):
    // 1. Talking + Text (ZUERST - für Lippensync!)
    // 2. Bewegung (Lächeln, Nicken, etc.)
    // 3. Kamera (Zoom, Pan, etc.)
    // 4. Ambient (Umgebungsgeräusche)
    // 5. BASE_MOTION + FACE_PROTECTION
    // ═══════════════════════════════════════════════════════════════
    
    const parts = [];
    
    // Bei Snapshot Spezial Kamerafahrt: Eigene Logik
    const isCamera = activeModel?.needsCameraMovement;
    if (isCamera && selectedCameraMovement) {
      const camPrompt = activeModel.cameraMovements?.find(c => c.id === selectedCameraMovement)?.prompt;
      if (camPrompt) parts.push(camPrompt);
    }
    
    // STANDARD MODELLE: Goldene Reihenfolge
    if (!isCamera) {
      // PRIO 1: Talking STIL (Text geht SEPARAT über talking_text ans Backend - bleibt Deutsch!)
      if (selectedMotions.includes('talking')) {
        // NUR Lipsync-Stil aus Admin Panel, OHNE den deutschen Text (der geht separat!)
        parts.push(TALKING_LIPSYNC);
      }
      
      // PRIO 3: Trend-Effekte (30 Premium TikTok-Style)
      const trendIds = trendEffects.map(t => t.id);
      const trendPrompts = selectedMotions
        .filter(id => trendIds.includes(id))
        .map(id => trendEffects.find(t => t.id === id)?.prompt)
        .filter(Boolean);
      if (trendPrompts.length > 0) {
        parts.push(trendPrompts.join(', '));
      }
      
      // PRIO 4: Bewegungs-Chips (NUR Mimik/Gestik, OHNE talking/ambient/kamera)
      const motionOnlyIds = ['soft', 'smile', 'nod', 'turn', 'blink', 'wind', 'look', 'relax'];
      const motionPrompts = selectedMotions
        .filter(id => motionOnlyIds.includes(id))
        .map(id => motionTemplates.find(m => m.id === id)?.prompt)
        .filter(Boolean);
      if (motionPrompts.length > 0) {
        parts.push(motionPrompts.join(', '));
      }
      
      // PRIO 5: Kamera-Chips (aus cameraTemplates)
      const cameraIds = cameraTemplates.map(c => c.id);
      const cameraPrompts = selectedMotions
        .filter(id => cameraIds.includes(id))
        .map(id => cameraTemplates.find(c => c.id === id)?.prompt)
        .filter(Boolean);
      if (cameraPrompts.length > 0) {
        parts.push(cameraPrompts.join(', '));
      }
      
      // PRIO 5: Ambient (Umgebungsgeräusche)
      if (selectedMotions.includes('ambient')) {
        const ambientPrompt = motionTemplates.find(m => m.id === 'ambient')?.prompt;
        if (ambientPrompt) parts.push(ambientPrompt);
      }
    }
    
    // Custom Prompt (falls vorhanden) - Override hat Priorität für Auto-Translation
    const promptToUse = overrideEnglishPrompt || promptEnglish || customPrompt;
    if (promptToUse?.trim()) {
      parts.push(promptToUse);
    }
    
    // BASE_MOTION + FACE_PROTECTION (nur wenn nicht leer)
    if (BASE_MOTION) parts.push(BASE_MOTION);
    if (FACE_PROTECTION) parts.push(FACE_PROTECTION);
    
    return parts.filter(Boolean).join(', ') || 'subtle movement, gentle expression, minimal movement, maintain facial features';
  };

  // Anzeige-Prompt für User (zeigt Snapshot Spezial Einstellungen)
  const getDisplayPrompt = () => {
    const parts = [];
    
    // Snapshot Spezial: Zeige spezifische Einstellungen
    if (activeModel?.needsEffect && selectedEffect) {
      const effectLabel = activeModel.effects?.find(e => e.id === selectedEffect)?.label || selectedEffect;
      parts.push(`✨ Effekt: ${effectLabel}`);
    }
    if (activeModel?.needsCameraMovement && selectedCameraMovement) {
      const camData = activeModel.cameraMovements?.find(c => c.id === selectedCameraMovement);
      const camPrompt = camData?.prompt || camData?.label || selectedCameraMovement;
      parts.push(`🎥 ${camPrompt}`);
    }
    if (activeModel?.needsText && lipsyncText) {
      parts.push(`🗣️ Text: "${lipsyncText.slice(0, 50)}${lipsyncText.length > 50 ? '...' : ''}"`);
      if (selectedVoice) {
        const voiceLabel = activeModel.voices?.find(v => v.id === selectedVoice)?.label || selectedVoice;
        parts.push(`🎤 Stimme: ${voiceLabel}`);
      }
    }
    if (activeModel?.needsVideoUrl && videoInputUrl) {
      const modeLabel = activeModel.swapModes?.find(m => m.id === selectedSwapMode)?.label || selectedSwapMode;
      parts.push(`🎭 ${modeLabel}`);
    }
    
    // Falls Snapshot Spezial Einstellungen, zeige diese
    if (parts.length > 0) {
      // Füge auch Motion-Tags hinzu wenn vorhanden
      if (selectedMotions.length > 0) {
        const motionLabels = selectedMotions.map(id => motionTemplates.find(m => m.id === id)?.label || cameraTemplates.find(c => c.id === id)?.label).filter(Boolean);
        parts.push(`Bewegung: ${motionLabels.join(', ')}`);
      }
      return parts.join(' • ');
    }
    
    // Standard: Zeige den technischen Prompt
    return getFullPrompt();
  };

  // Auto-Start wenn von Vorlage kommend
  const autoStartRef = useRef(false);

  // Live-Update des Anzeigefeldes wenn Kamera/Effekt gewählt wird
  useEffect(() => {
    if (!isGenerating && !existingVideo && !generatedVideoUrl) {
      setActivePrompt(getDisplayPrompt());
    }
  }, [selectedCameraMovement, selectedEffect, selectedMotions, selectedModel, lipsyncText, selectedVoice]);

  // Cleanup on unmount + Body Scroll Lock
  useEffect(() => {
    if (inline) return; // Kein scroll-lock im Inline-Modus

    // Lock body scroll when fullscreen is open
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.top = `-${window.scrollY}px`;

    // Auto-Start wenn Vorlage gewählt (nur einmal!)
    if (image.autoStart && !autoStartRef.current) {
      autoStartRef.current = true;
      setTimeout(() => handleGenerate(), 100);
    }

    return () => {
      // Restore scroll
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      window.scrollTo(0, parseInt(scrollY || '0') * -1);

      // Cleanup EventSource or Interval
      if (pollIntervalRef.current) {
        if (pollIntervalRef.current.close) pollIntervalRef.current.close();
        else clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  // Smarter Progress: Backend-Wert + sanfte Interpolation
  const [backendProgress, setBackendProgress] = useState(0);
  const [displayProgress, setDisplayProgress] = useState(0);

  // SSE + sanftes Ticken (fal.ai gibt während Generierung keinen Progress)
  useEffect(() => {
    if (!isGenerating) {
      setDisplayProgress(0);
      setBackendProgress(0);
      return;
    }

    const interval = setInterval(() => {
      setDisplayProgress(prev => {
        if (prev >= 100) return 100;

        // Progress geht NUR vorwärts, wird aber immer langsamer
        let increment;

        if (prev < 30) {
          increment = 0.4;
        } else if (prev < 60) {
          increment = 0.25;
        } else if (prev < 80) {
          increment = 0.1;
        } else if (prev < 90) {
          increment = 0.03;
        } else {
          // Ab 90%: Ultra-langsam aber IMMER vorwärts
          increment = 0.008;
        }

        // Max 98% - die letzten 2% kommen erst wenn Video wirklich da
        return Math.min(98, prev + increment);
      });
    }, 100);

    return () => clearInterval(interval);
  }, [isGenerating, backendProgress]);

  // Kino-Modus Fortschritt (client-seitig, ~90s Gesamtdauer)
  useEffect(() => {
    if (!kinoGenerating) {
      setKinoDisplayProgress(0);
      return;
    }
    const start = Date.now();
    const TOTAL = 90000; // 90s typisch für Kino-Pipeline
    const interval = setInterval(() => {
      const elapsed = Date.now() - start;
      let pct;
      if (elapsed < TOTAL) {
        pct = (elapsed / TOTAL) * 90;
      } else {
        pct = 90 + Math.min(8, (elapsed - TOTAL) / 10000);
      }
      setKinoDisplayProgress(Math.min(98, pct));
    }, 100);
    return () => clearInterval(interval);
  }, [kinoGenerating]);

  // Dynamische Status-Nachrichten während Generierung
  useEffect(() => {
    if (!isGenerating) {
      setStatusMessage('');
      return;
    }

    const messages = [
      { min: 0, max: 15, texts: ['Starte Video-Generierung...', 'Bereite KI vor...', 'Initialisiere...'] },
      { min: 15, max: 35, texts: ['Analysiere Bild...', 'Erkenne Gesichtszüge...', 'Berechne Bewegung...'] },
      { min: 35, max: 55, texts: ['KI generiert Frames...', 'Erstelle Animation...', 'Rendere Bewegung...'] },
      { min: 55, max: 75, texts: ['Video nimmt Form an...', 'Optimiere Details...', 'Verfeinere Übergänge...'] },
      { min: 75, max: 90, texts: ['Fast geschafft...', 'Finalisiere Video...', 'Letzte Optimierungen...'] },
      { min: 90, max: 100, texts: ['Gleich fertig...', 'Video wird geladen...', 'Noch einen Moment...'] }
    ];

    const updateMessage = () => {
      const phase = messages.find(m => displayProgress >= m.min && displayProgress < m.max);
      if (phase) {
        const randomText = phase.texts[Math.floor(Math.random() * phase.texts.length)];
        setStatusMessage(randomText);
      }
    };

    updateMessage();
    const interval = setInterval(updateMessage, 3000);
    return () => clearInterval(interval);
  }, [isGenerating, displayProgress]);

  const handleGenerate = async () => {
    // SOFORT visuelles Feedback — Button reagiert instant
    setIsGenerating(true);
    setDisplayProgress(1);
    setBackendProgress(1);
    setStatusMessage('Video wird erstellt...');
    setError(null);
    setActivePrompt(getDisplayPrompt());

    // SOFORT: Galerie scrollt zum Video-Tab + Pending-Spinner zeigt sich
    if (onGenerate) onGenerate();
    // Zusätzlich Event dispatchen — fängt alle Einstiegspunkte ab (VideoStudioSection + Fullscreen-Modal)
    try { window.dispatchEvent(new CustomEvent('galleryScrollToVideos')); } catch (_) {}

    // Button nach 2s wieder freigeben — User kann sofort nächstes Video starten
    setTimeout(() => {
      setIsGenerating(false);
      setDisplayProgress(0);
      setBackendProgress(0);
      setStatusMessage('');
    }, 2000);

    // AUTO-TRANSLATE: Falls User auf Deutsch ist, übersetze NUR den User-Text ins Englische
    let englishUserPrompt = combinedPromptEN || null;
    if (combinedPromptLang === 'de' && combinedPromptDE && !combinedPromptEN) {
      try {
        setStatusMessage('Übersetze Prompt...');
        const token = getToken();
        const translateRes = await fetch('/api/translate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ text: combinedPromptDE, source_lang: 'de', target_lang: 'en' })
        });
        const translateData = await translateRes.json();
        if (translateData.translated_text || translateData.translated) {
          englishUserPrompt = translateData.translated_text || translateData.translated;
          setCombinedPromptEN(englishUserPrompt);
          console.log('🌐 Auto-translated to EN:', englishUserPrompt);
        }
      } catch (e) {
        console.warn('Translation failed, using DE:', e);
        englishUserPrompt = combinedPromptDE; // Fallback
      }
    }

    // IMMER getFullPrompt() nutzen — baut Chips + User-Text zusammen
    let finalPrompt = getFullPrompt(englishUserPrompt);

    // Fallback
    let translatedPrompt = finalPrompt;

    // Intelligente Vorlage oder normale Animation?
    const isTemplate = image.templatePrompt && image.templateName;
    
    // xAI Direct API? (Test-Button)
    const isXaiDirect = activeModel?.isDirect === true;
    const isCinematic = selectedModel === 'cinematic_video';
    const endpoint = isCinematic
      ? '/api/video/generate'
      : isXaiDirect
        ? '/api/xai/video/generate'
        : (isTemplate ? '/api/beatleap/animate-template' : '/api/beatleap/animate');

    // Alle Werte für den API-Call JETZT merken, bevor wir das Form resetten
    const sendSourceImage = selectedSourceImage || image?.url;
    const sendCustomPrompt = customPrompt || combinedPromptDE || '';
    const sendAnalysis = analysisEnglish || imageAnalysis || '';
    const sendDuration = videoDuration;
    const sendResolution = videoResolution;
    const sendAspectRatio = videoAspectRatio;
    const sendModel = selectedModel;
    const sendEndFrameUrl = endFrameUrl;
    const sendMotions = [...selectedMotions];
    const sendTalkingText = selectedMotions.includes('talking') ? talkingText : null;
    const sendCameraMovement = activeModel?.needsCameraMovement ? selectedCameraMovement : null;
    const sendVoice = activeModel?.needsVoice ? selectedVoice : null;
    const sendLipsyncText = activeModel?.needsText ? lipsyncText : null;
    const sendEffect = activeModel?.needsEffect ? selectedEffect : null;
    const sendSwapMode = activeModel?.needsVideoUrl ? selectedSwapMode : null;
    const sendVideoInputUrl = (activeModel?.needsVideoUrl || activeModel?.needsVideoInput) ? videoInputUrl : null;

    // SOFORT Form leeren — User kann nächstes Video vorbereiten
    setSelectedMotions([]);
    setCustomPrompt('');
    setCombinedPromptDE('');
    setCombinedPromptEN('');
    setSelectedModel('cinematic_video');
    setVideoDuration(8);
    setVideoResolution('720p');
    setVideoAspectRatio(null);
    setEndFrameUrl(null);
    setEndFramePreview(null);
    setSelectedCameraMovement('zoom_in');
    setTalkingText('');
    setLipsyncText('');
    if (availablePhotos.length > 0) {
      setSelectedSourceImage(availablePhotos[0].url);
    }

    try {
      const token = getToken();

      // Cinematic Video: Astria Direct
      if (isCinematic) {
        const cinematicResponse = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            source_generation_id: image?.data?.id || image?.id,
            video_model: 'cinematic_video',
            video_prompt: finalPrompt || getFullPrompt(),
            display_prompt: sendCustomPrompt,
            image_index: image?.imageIndex || 0,
            avatar_id: avatarId,
            aspect_ratio: sendAspectRatio || '16:9'
          })
        });

        const cinematicData = await cinematicResponse.json();
        if (!cinematicResponse.ok) {
          throw new Error(cinematicData.detail || 'Cinematic Video Fehler');
        }

        const videoGenId = cinematicData.video_generation_id;
        setStatusMessage('Cinematic Video wird generiert (kann 10-15 Min dauern)...');

        // Polling
        const pollCinematic = async () => {
          try {
            const statusRes = await fetch(`/api/video/${videoGenId}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            const statusData = await statusRes.json();

            if (statusData.status === 'completed' && statusData.video_url) {
              clearInterval(pollIntervalRef.current);
              setIsGenerating(false);
              setDisplayProgress(100);
              setStatusMessage('Cinematic Video fertig!');
              if (onVideoGenerated) onVideoGenerated(statusData);
              return;
            }
            if (statusData.status === 'failed') {
              clearInterval(pollIntervalRef.current);
              setIsGenerating(false);
              setError(statusData.error || 'Video-Generierung fehlgeschlagen');
              return;
            }
            // Progress update
            setBackendProgress(prev => Math.min(prev + 1, 95));
          } catch (e) { /* ignore polling errors */ }
        };

        pollIntervalRef.current = setInterval(pollCinematic, 10000); // Alle 10s (dauert lang)
        pollCinematic();
        return;
      }

      // xAI Direct: Einfacher Payload
      if (isXaiDirect) {
        const xaiResponse = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            image_url: sendSourceImage,
            prompt: finalPrompt || getFullPrompt(),
            display_prompt: sendCustomPrompt,
            duration: sendDuration,
            aspect_ratio: sendAspectRatio || '9:16',
            resolution: sendResolution,
            avatar_id: avatarId
          })
        });
        
        const xaiData = await xaiResponse.json();
        if (!xaiResponse.ok) {
          throw new Error(xaiData.detail || 'Video-Generierung fehlgeschlagen');
        }
        
        const jobId = xaiData.job_id;
        setStatusMessage('Video wird generiert...');
        
        // Polling für xAI Jobs
        const pollXaiStatus = async () => {
          try {
            const statusResponse = await fetch(`/api/xai/job/${jobId}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            const statusData = await statusResponse.json();
            
            if (statusData.status === 'failed') {
              clearInterval(pollIntervalRef.current);
              setError(sanitizeError(statusData.error || 'Video-Generierung fehlgeschlagen'));
              setIsGenerating(false);
              return;
            }
            
            if (statusData.status === 'completed' && statusData.video_url) {
              clearInterval(pollIntervalRef.current);
              setDisplayProgress(100);
              setStatusMessage('Fertig!');
              setIsGenerating(false);
              
              // Refresh gallery
              if (typeof fetchUserVideos === 'function') {
                await fetchUserVideos();
              }
              
              // 🎬 Direkt ins VideoActionSheet! (wie Standard-Flow)
              if (onVideoReady) {
                const videoData = {
                  id: statusData.video_id || 'xai-' + Date.now(),
                  video_url: statusData.video_url,
                  video_prompt: finalPrompt || customPrompt || activePrompt || '',
                  source_image_url: selectedSourceImage || image?.url,
                  created_at: new Date().toISOString(),
                  motion_tags: ['Video']
                };
                
                setTimeout(() => {
                  onVideoReady(videoData);
                }, 50);
              } else {
                // Fallback: Tab wechseln
                setShowVideoGenerator(false);
                setActiveTab('videos');
              }
              
              // Toast
              if (typeof addToast === 'function') {
                addToast('🎬 Video fertig!', 'success');
              }
              return;
            }
            
            // Still pending - update progress
            setDisplayProgress(prev => Math.min(prev + 5, 90));
          } catch (err) {
            console.error('xAI Poll error:', err);
          }
        };
        
        // Start polling every 5 seconds
        pollIntervalRef.current = setInterval(pollXaiStatus, 5000);
        pollXaiStatus(); // Initial check
        return;
      }
      
      // Standard fal.ai Flow
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: isTemplate ? JSON.stringify({
          image_url: sendSourceImage,
          style_template: image.templatePrompt,
          template_name: image.templateName,
          duration: sendDuration,
          resolution: sendResolution,
          model: sendModel,
          avatar_id: avatarId
        }) : JSON.stringify({
          image_url: sendSourceImage,
          clip_id: image.id,
          auto_mode: false,
          user_prompt: sendCustomPrompt,
          full_prompt_override: finalPrompt,
          language: 'de',
          image_analysis: sendAnalysis,
          duration: sendDuration,
          resolution: sendResolution,
          aspect_ratio: sendAspectRatio,
          model: sendModel,
          end_frame_url: sendEndFrameUrl,
          motion_tags: sendMotions,
          talking_text: sendTalkingText,
          camera_movement: sendCameraMovement,
          voice_id: sendVoice,
          lipsync_text: sendLipsyncText,
          effect: sendEffect,
          swap_mode: sendSwapMode,
          video_input_url: sendVideoInputUrl,
          avatar_id: avatarId
        })
      });

      const data = await response.json();
      if (!response.ok) {
        const errorMsg = typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail || data);
        throw new Error(errorMsg);
      }

      // POLLING statt SSE - zuverlässiger!
      const jobId = data.jobId || data.job_id;

      const pollStatus = async () => {
        try {
          const statusResponse = await fetch(`/api/beatleap/animate-status/${jobId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const statusData = await statusResponse.json();
          console.log('🎬 Poll status:', statusData);

          if (statusData.error || statusData.status === 'error' || statusData.status === 'failed') {
            clearInterval(pollIntervalRef.current);
            setError(sanitizeError(statusData.error || statusData.message || 'Video-Generierung fehlgeschlagen'));
            setIsGenerating(false);
            return;
          }

          // Backend Progress setzen (Interpolation macht den Rest!)
          if (statusData.progress !== undefined) {
            setBackendProgress(statusData.progress);
          }

          // Status Message
          if (statusData.message) {
            setStatusMessage(statusData.message);
          }

          const videoUrl = statusData.videoUrl || statusData.video_url;

          if (statusData.status === 'completed' && videoUrl) {
            console.log('🎬 VIDEO COMPLETED! URL:', videoUrl);
            clearInterval(pollIntervalRef.current);
            setBackendProgress(100);
            setDisplayProgress(100);
            setIsGenerating(false);
            
            // 🎬 Galerie sofort aktualisieren!
            if (onVideoCompleted) {
              onVideoCompleted();
            }
            
            // 🎬 Direkt ins VideoActionSheet!
            if (onVideoReady) {
              // Chip-Labels sammeln
              const chipLabels = selectedMotions.map(id => {
                const motion = motionTemplates.find(m => m.id === id);
                const camera = cameraTemplates.find(c => c.id === id);
                return motion?.label || camera?.label || id;
              }).filter(Boolean);
              
              const videoData = {
                id: statusData.video_id || `temp-${Date.now()}`,
                video_url: videoUrl,
                video_prompt: customPrompt || activePrompt || '',
                user_prompt: customPrompt || activePrompt || '',
                source_image_url: selectedSourceImage || image?.url,
                created_at: new Date().toISOString(),
                video_aspect_ratio: videoAspectRatio,
                video_resolution: videoResolution,
                motion_tags: chipLabels
              };
              
              // Kurzer Delay damit React fertig rendern kann, dann VideoActionSheet öffnen
              setTimeout(() => {
                onVideoReady(videoData);
              }, 50);
            } else {
              // Fallback: Altes Verhalten
              setGeneratedVideoUrl(videoUrl);
            }
          } else if (statusData.status === 'failed') {
            clearInterval(pollIntervalRef.current);
            setError(sanitizeError(statusData.message || 'Video-Generierung fehlgeschlagen'));
            setIsGenerating(false);
          }
        } catch (pollErr) {
          console.error('Poll error:', pollErr);
          // Bei Netzwerk-Fehler nicht abbrechen, weiter pollen
        }
      };

      // Sofort ersten Poll, dann alle 2 Sekunden
      pollStatus();
      pollIntervalRef.current = setInterval(pollStatus, 2000);

    } catch (err) {
      setError(sanitizeError(err.message));
      setIsGenerating(false);
      // Auto-clear error after 15 seconds so it doesn't feel stuck
      setTimeout(() => setError(prev => prev === err.message ? null : prev), 15000);
    }
  };

  // GROK-Style: KONSTANTE kleine Dots, Opacity fadet!
  const dotSize = 2; // Konstant 2px wie GROK!
  const dotSpacing = 8; // Konstant 8px Abstand
  // Blur: Startet bei 15px, wird klarer mit Progress
  const blurAmount = isGenerating
    ? Math.max(0, 15 - (displayProgress / 100) * 15)
    : 0;
  // Dot Opacity: 0.6 → 0 (fadet aus während Progress)
  const dotOpacity = isGenerating
    ? Math.max(0, 0.6 - (displayProgress / 100) * 0.6)
    : 0;

  // BOMBENFEST FINAL: Kein Wackeln, kein Verschieben
  const modalRef = useRef(null);
  const scrollYRef = useRef(0);
  
  useEffect(() => {
    if (inline) return; // Kein scroll-lock im Inline-Modus
    const modal = modalRef.current;
    if (!modal) return;

    scrollYRef.current = window.scrollY;

    // Block ALLE touch moves außer in .modal-scroll-area
    const blockTouch = (e) => {
      const scrollArea = e.target.closest('.modal-scroll-area');
      if (!scrollArea) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };
    
    // Block auf WINDOW level
    window.addEventListener('touchmove', blockTouch, { passive: false });
    
    // FINAL LOCK: html + body komplett einfrieren
    document.documentElement.style.cssText = `
      overflow: hidden !important;
      overflow-x: hidden !important;
      overflow-y: hidden !important;
      position: fixed !important;
      width: 100% !important;
      height: 100% !important;
      margin: 0 !important;
      padding: 0 !important;
      touch-action: none !important;
      overscroll-behavior: none !important;
    `;
    document.body.style.cssText = `
      overflow: hidden !important;
      overflow-x: hidden !important;
      overflow-y: hidden !important;
      position: fixed !important;
      top: -${scrollYRef.current}px !important;
      left: 0 !important;
      right: 0 !important;
      width: 100% !important;
      height: 100% !important;
      margin: 0 !important;
      padding: 0 !important;
      touch-action: none !important;
      overscroll-behavior: none !important;
      -webkit-overflow-scrolling: auto !important;
    `;
    
    return () => {
      window.removeEventListener('touchmove', blockTouch);
      document.documentElement.style.cssText = '';
      document.body.style.cssText = '';
      window.scrollTo(0, scrollYRef.current);
    };
  }, []);

  // ═══ INLINE MODUS: Direkt im Accordion rendern ═══
  if (inline) {
    // Fixer Modus-Hintergrund (statt User-Foto) — passt zum gewaehlten Modus.
    const modeBg = selectedModel === 'cinematic_video' ? '/mode-cards/bg-mode-kino.png'
                  : selectedModel === 'grok'           ? '/mode-cards/bg-mode-shorts.png'
                  : null;
    return (
      <div className="relative bg-black overflow-hidden">
        {/* Fester Modus-Hintergrund, volle Breite, oben starten */}
        {modeBg && (
          <div className="absolute top-0 left-0 right-0 overflow-hidden" style={{ zIndex: 0 }}>
            <img
              src={modeBg}
              alt=""
              aria-hidden
              className="w-full h-auto block object-cover"
            />
            {/* Gradient — schnell abdunkeln für Lesbarkeit der Form-Elemente */}
            <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.5) 120px, rgba(0,0,0,0.78) 220px, rgba(0,0,0,0.92) 320px, black 420px)' }} />
          </div>
        )}

        {/* Controls — über dem Bild */}
        {(!isGenerating || inline) && !generatedVideoUrl && !image?.templatePrompt && (
          <div className="relative space-y-3 px-3 sm:px-4 pb-4" style={{ zIndex: 2, paddingTop: selectedSourceImage ? '50px' : '1rem' }}>

            {/* Modus-Wahlschalter — ausgeblendet wenn extern via Picker bestimmt */}
            {!hideModelSelector && (
            <div id="demo-video-mode-toggle" className="grid grid-cols-2 gap-3">
              <button
                id="demo-video-mode-cinematic"
                onClick={() => handleModelChange('cinematic_video')}
                className={`relative flex items-center justify-center gap-2.5 py-3.5 px-4 rounded-2xl border-2 transition-all duration-200 ${
                  selectedModel === 'cinematic_video'
                    ? 'bg-gradient-to-br from-amber-500/20 to-amber-600/10 border-amber-400 shadow-lg shadow-amber-500/15'
                    : 'bg-white/5 border-white/15 hover:bg-white/10 hover:border-white/30'
                }`}
              >
                <span className="text-xl">🎬</span>
                <span className={`text-sm font-bold tracking-wide ${selectedModel === 'cinematic_video' ? 'text-amber-300' : 'text-white/60'}`}>Kino Modus</span>
              </button>
              <button
                id="demo-video-mode-shorts"
                onClick={() => handleModelChange('grok')}
                className={`relative flex items-center justify-center gap-2.5 py-3.5 px-4 rounded-2xl border-2 transition-all duration-200 ${
                  selectedModel === 'grok'
                    ? 'bg-gradient-to-br from-purple-500/20 to-purple-600/10 border-purple-400 shadow-lg shadow-purple-500/15'
                    : 'bg-white/5 border-white/15 hover:bg-white/10 hover:border-white/30'
                }`}
              >
                <span className="text-xl">⚡</span>
                <span className={`text-sm font-bold tracking-wide ${selectedModel === 'grok' ? 'text-purple-300' : 'text-white/60'}`}>Shorts</span>
              </button>
            </div>
            )}

            {/* Foto-Galerie + Upload */}
            {(!activeModel?.category || activeModel?.category === 'video') && (
              <div className="space-y-2">
                <span className="text-white/60 text-xs font-medium uppercase tracking-wider">
                  📸 Foto wählen
                </span>
                {availablePhotos.length >= 1 && (
                <div
                  id="demo-video-source-slider"
                  ref={photoSliderRef}
                  className="flex gap-3 overflow-x-auto pb-3 snap-x snap-mandatory"
                  style={{ WebkitOverflowScrolling: 'touch', cursor: 'grab' }}
                  onMouseDown={(e) => { const el = e.currentTarget; el.dataset.dragging = '1'; el.dataset.moved = '0'; el.dataset.startX = e.pageX; el.dataset.scrollLeft = el.scrollLeft; el.style.cursor = 'grabbing'; el.style.userSelect = 'none'; }}
                  onMouseMove={(e) => { const el = e.currentTarget; if (el.dataset.dragging !== '1') return; e.preventDefault(); const dx = e.pageX - parseFloat(el.dataset.startX); if (Math.abs(dx) > 5) el.dataset.moved = '1'; el.scrollLeft = parseFloat(el.dataset.scrollLeft) - dx; }}
                  onMouseUp={(e) => { const el = e.currentTarget; el.dataset.dragging = '0'; el.style.cursor = 'grab'; el.style.userSelect = ''; }}
                  onMouseLeave={(e) => { const el = e.currentTarget; el.dataset.dragging = '0'; el.style.cursor = 'grab'; el.style.userSelect = ''; }}
                  onClickCapture={(e) => { if (e.currentTarget.dataset.moved === '1') { e.stopPropagation(); e.preventDefault(); e.currentTarget.dataset.moved = '0'; } }}
                >
                  {availablePhotos.map((photo, idx) => {
                    const isSelected = selectedSourceImage === photo.url || (!selectedSourceImage && photo.url === image?.url);
                    const isLandscape = landscapePhotos.has(photo.url);
                    return (
                      <button
                        key={photo.id}
                        data-selected={isSelected ? 'true' : 'false'}
                        onClick={() => setSelectedSourceImage(photo.url)}
                        className={`relative flex-shrink-0 h-28 sm:h-32 rounded-lg sm:rounded-xl overflow-hidden transition-all snap-start ${
                          isSelected
                            ? 'border-2 border-[#10B981] shadow-lg shadow-[#10B981]/30'
                            : 'border-2 border-white/20 active:border-white/50'
                        }`}
                        style={{ width: isLandscape ? '10rem' : '5rem' }}
                      >
                        <img src={photo.url} alt="" loading={idx < 8 ? 'eager' : 'lazy'} draggable={false} className="w-full h-full object-cover pointer-events-none select-none" />
                        {isSelected && (
                          <div className="absolute top-1 right-1 w-4 h-4 sm:w-5 sm:h-5 bg-[#10B981] rounded-full flex items-center justify-center">
                            <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
                )}
                {/* Oder-Trenner + Upload */}
                <div className="flex items-center gap-3 py-1">
                  <div className="flex-1 h-px bg-white/10" />
                  <span className="text-white/30 text-xs font-medium">oder</span>
                  <div className="flex-1 h-px bg-white/10" />
                </div>
                <label className={`flex items-center justify-center gap-2 py-3 px-4 rounded-xl border-2 border-dashed transition-all cursor-pointer ${
                  uploadedImageUrl && selectedSourceImage === uploadedImageUrl
                    ? 'border-[#10B981] bg-[#10B981]/10'
                    : 'border-white/20 bg-white/5 hover:bg-white/10 hover:border-white/30'
                }`}>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                    className="hidden"
                    onChange={handleCustomImageUpload}
                    disabled={isUploadingCustomImage}
                  />
                  {isUploadingCustomImage ? (
                    <span className="flex items-center gap-2 text-white/50 text-sm">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Wird hochgeladen...
                    </span>
                  ) : uploadedImageUrl && selectedSourceImage === uploadedImageUrl ? (
                    <span className="flex items-center gap-2 text-[#10B981] text-sm font-medium">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      Eigenes Bild ausgewählt
                    </span>
                  ) : (
                    <span className="flex items-center gap-2 text-white/50 text-sm">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                      Eigenes Bild hochladen
                    </span>
                  )}
                </label>
              </div>
            )}

            {/* Kino Modus: Drehbuch */}
            {selectedModel === 'cinematic_video' && (
              <div className="space-y-3 sm:space-y-4">
                <div className="space-y-2">
                  <div className="space-y-1">
                    <span className="text-white/80 text-sm font-bold">Du bist der Regisseur</span>
                    <p className="text-white/40 text-xs leading-relaxed flex items-start gap-1.5">
                      <span className="flex-shrink-0 mt-px">💡</span>
                      <span>Das Bild zeigt bereits die Szene — beschreibe jetzt, was passiert. Wie bewegt sich dein Charakter? Was sagt er? Schreibe dein Drehbuch so, als würdest du einem Schauspieler Anweisungen geben.</span>
                    </p>
                  </div>
                  <textarea
                    id="demo-video-kino-textarea"
                    ref={kinoTextareaRef}
                    value={kinoText}
                    onChange={(e) => { if (e.target.value.length <= 5000) setKinoText(e.target.value); }}
                    placeholder={"Beispiel: Er schaut in die Kamera, grinst und sagt: \"Mann, ist das heiß heute!\" Er wischt sich über die Stirn, steht auf und geht zum Kühlschrank. Er öffnet die Tür, holt eine Dose raus und knackt sie auf. Nimmt einen großen Schluck, atmet erleichtert aus und lacht: \"Schon viel besser!\""}
                    className="w-full min-h-[140px] bg-black/30 border-2 border-[#10B981]/50 focus:border-[#10B981] rounded-xl p-3 text-white placeholder-white/30 text-sm transition-all resize-none outline-none ring-1 ring-[#10B981]/20 focus:ring-[#10B981]/40"
                  />
                </div>
                {kinoError && (
                  <div className="bg-red-500/20 border border-red-500/50 rounded-xl p-3 flex items-center gap-2">
                    <span className="text-red-400 text-sm flex-1">{kinoError}</span>
                    <button onClick={() => setKinoError(null)} className="text-red-400/60 hover:text-red-400 text-lg">×</button>
                  </div>
                )}
                <button
                  id="demo-video-generate-button"
                  onClick={() => {
                    const hasImg = selectedSourceImage || image?.url;
                    if (hasImg && kinoText.trim()) {
                      handleKinoGenerate();
                    } else if (!hasImg && photoSliderRef.current) {
                      photoSliderRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                  }}
                  className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${
                    (selectedSourceImage || image?.url) && kinoText.trim()
                      ? 'bg-[#10B981]/70 hover:bg-[#10B981]/80 text-white'
                      : 'bg-white/20 text-white/30'
                  }`}
                >
                  {kinoGenerating ? (
                    <span className="flex items-center justify-center gap-3">
                      <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Video wird erstellt...
                    </span>
                  ) : !(selectedSourceImage || image?.url) ? 'Foto wählen' : !kinoText.trim() ? 'Text eingeben' : 'Video erstellen'}
                </button>

                {/* Kino-Modus Fortschritt: wird in der Galerie als Pending-Video-Karte angezeigt */}
              </div>
            )}

            {/* Basic Modus: Beschreibung + Chips */}
            {selectedModel !== 'cinematic_video' && (
              <>
                {/* Genre Auswahl */}
                {cinemaGenres.length > 0 && (
                  <div className="space-y-2">
                    <span className="text-white/60 text-xs font-medium uppercase tracking-wider">Genre Auswahl</span>
                    <div className="flex flex-wrap gap-2">
                      {cinemaGenres.map((genre) => (
                        <button key={genre.key} onClick={() => handleCinemaAnalyze(genre.key)} disabled={cinemaLoading}
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${selectedCinemaGenre === genre.key ? 'bg-emerald-500 text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'} ${cinemaLoading ? 'opacity-50' : ''}`}
                        >
                          {cinemaLoading && selectedCinemaGenre === genre.key ? 'Analysiere...' : genre.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Beschreibung */}
                <div className="space-y-1">
                  <span className="text-white/60 text-xs font-medium uppercase tracking-wider">{promptSettings.label || "Beschreibung"}</span>
                  <textarea
                    id="video-prompt-textarea"
                    value={customPrompt}
                    onChange={(e) => setCustomPrompt(e.target.value)}
                    placeholder={promptSettings.placeholder || "Was soll im Video passieren?"}
                    rows={2}
                    className="w-full px-3 py-2 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/40 focus:border-[#10B981] focus:outline-none resize-none text-sm"
                  />
                </div>

                {/* Bewegung Chips mit Video-Previews */}
                {motionTemplates.length > 0 && (
                  <div className="space-y-2">
                    <span className="text-white/60 text-xs font-medium uppercase tracking-wider">{sectionTitles.motion || "Bewegung"}</span>
                    <div className="flex gap-3 overflow-x-auto py-2 px-1 snap-x" style={{ WebkitOverflowScrolling: 'touch', cursor: 'grab' }} onMouseDown={(e) => { const el = e.currentTarget; el.dataset.dragging = '1'; el.dataset.moved = '0'; el.dataset.startX = e.pageX; el.dataset.scrollLeft = el.scrollLeft; el.style.cursor = 'grabbing'; el.style.userSelect = 'none'; }} onMouseMove={(e) => { const el = e.currentTarget; if (el.dataset.dragging !== '1') return; e.preventDefault(); const dx = e.pageX - parseFloat(el.dataset.startX); if (Math.abs(dx) > 5) el.dataset.moved = '1'; el.scrollLeft = parseFloat(el.dataset.scrollLeft) - dx; }} onMouseUp={(e) => { const el = e.currentTarget; el.dataset.dragging = '0'; el.style.cursor = 'grab'; el.style.userSelect = ''; }} onMouseLeave={(e) => { const el = e.currentTarget; el.dataset.dragging = '0'; el.style.cursor = 'grab'; el.style.userSelect = ''; }} onClickCapture={(e) => { if (e.currentTarget.dataset.moved === '1') { e.stopPropagation(); e.preventDefault(); e.currentTarget.dataset.moved = '0'; } }}>
                      {motionTemplates.map((motion) => {
                        const isSelected = selectedMotions.includes(motion.id);
                        const hasPreview = motion.preview_video_url;
                        const thumbUrl = motion.preview_thumbnail_url || (hasPreview ? motion.preview_video_url.replace('.mp4', '_thumb.jpg') : null);
                        if (hasPreview) {
                          return (
                            <div key={motion.id} onClick={() => toggleMotion(motion.id)}
                              className={`flex-shrink-0 w-20 sm:w-24 snap-start rounded-lg sm:rounded-xl overflow-hidden cursor-pointer transition-all active:scale-95 ${isSelected ? 'ring-2 ring-[#10B981] ring-offset-1 sm:ring-offset-2 ring-offset-black' : 'ring-1 ring-white/20'}`}>
                              <div className="relative h-28 sm:h-32 bg-black/50">
                                <LazyChipVideo src={motion.preview_proxy_url || motion.preview_video_url} mobileSrc={motion.preview_mobile_url} poster={thumbUrl} className="w-full h-full object-cover" />
                                {isSelected && <div className="absolute top-1 right-1 bg-[#10B981] text-black text-[10px] font-bold px-1 sm:px-1.5 py-0.5 rounded">✓</div>}
                              </div>
                              <div className={`p-1.5 sm:p-2 text-center ${isSelected ? 'bg-[#10B981]/20' : 'bg-white/5'}`}>
                                <p className="text-white text-[11px] sm:text-xs font-medium truncate">{motion.label}</p>
                              </div>
                            </div>
                          );
                        }
                        return (
                          <button key={motion.id} onClick={() => toggleMotion(motion.id)}
                            className={`flex-shrink-0 px-3 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${isSelected ? 'bg-[#10B981]/30 border border-[#10B981] text-white' : 'bg-white/10 border border-white/20 text-white/60 hover:text-white'}`}
                          >{motion.label}</button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Kamerafahrt Chips mit Video-Previews */}
                {cameraTemplates.length > 0 && (
                  <div className="space-y-2">
                    <span className="text-white/60 text-xs font-medium uppercase tracking-wider">{sectionTitles.camera || "Kamerafahrt"}</span>
                    <div className="flex gap-3 overflow-x-auto py-2 px-1 snap-x" style={{ WebkitOverflowScrolling: 'touch', cursor: 'grab' }} onMouseDown={(e) => { const el = e.currentTarget; el.dataset.dragging = '1'; el.dataset.moved = '0'; el.dataset.startX = e.pageX; el.dataset.scrollLeft = el.scrollLeft; el.style.cursor = 'grabbing'; el.style.userSelect = 'none'; }} onMouseMove={(e) => { const el = e.currentTarget; if (el.dataset.dragging !== '1') return; e.preventDefault(); const dx = e.pageX - parseFloat(el.dataset.startX); if (Math.abs(dx) > 5) el.dataset.moved = '1'; el.scrollLeft = parseFloat(el.dataset.scrollLeft) - dx; }} onMouseUp={(e) => { const el = e.currentTarget; el.dataset.dragging = '0'; el.style.cursor = 'grab'; el.style.userSelect = ''; }} onMouseLeave={(e) => { const el = e.currentTarget; el.dataset.dragging = '0'; el.style.cursor = 'grab'; el.style.userSelect = ''; }} onClickCapture={(e) => { if (e.currentTarget.dataset.moved === '1') { e.stopPropagation(); e.preventDefault(); e.currentTarget.dataset.moved = '0'; } }}>
                      {cameraTemplates.map((cam) => {
                        const isSelected = selectedMotions.includes(cam.id);
                        const hasPreview = cam.preview_video_url;
                        const thumbUrl = cam.preview_thumbnail_url || (hasPreview ? cam.preview_video_url.replace('.mp4', '_thumb.jpg') : null);
                        if (hasPreview) {
                          return (
                            <div key={cam.id} onClick={() => toggleMotion(cam.id)}
                              className={`flex-shrink-0 w-20 sm:w-24 snap-start rounded-lg sm:rounded-xl overflow-hidden cursor-pointer transition-all active:scale-95 ${isSelected ? 'ring-2 ring-[#10B981] ring-offset-1 sm:ring-offset-2 ring-offset-black' : 'ring-1 ring-white/20'}`}>
                              <div className="relative h-28 sm:h-32 bg-black/50">
                                <LazyChipVideo src={cam.preview_proxy_url || cam.preview_video_url} mobileSrc={cam.preview_mobile_url} poster={thumbUrl} className="w-full h-full object-cover" />
                                {isSelected && <div className="absolute top-1 right-1 bg-[#10B981] text-black text-[10px] font-bold px-1 sm:px-1.5 py-0.5 rounded">✓</div>}
                              </div>
                              <div className={`p-1.5 sm:p-2 text-center ${isSelected ? 'bg-[#10B981]/20' : 'bg-white/5'}`}>
                                <p className="text-white text-[11px] sm:text-xs font-medium truncate">{cam.label}</p>
                              </div>
                            </div>
                          );
                        }
                        return (
                          <button key={cam.id} onClick={() => toggleMotion(cam.id)}
                            className={`flex-shrink-0 px-3 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${isSelected ? 'bg-[#10B981]/30 border border-[#10B981] text-white' : 'bg-white/10 border border-white/20 text-white/60 hover:text-white'}`}
                          >{cam.label}</button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Trend Effekte mit Video-Previews */}
                {trendEffects.length > 0 && (
                  <div className="space-y-2">
                    <span className="text-white/60 text-xs font-medium uppercase tracking-wider">{sectionTitles.trends || "🔥 Trend Effekte"}</span>
                    <div className="flex gap-3 overflow-x-auto py-2 px-1 snap-x" style={{ WebkitOverflowScrolling: 'touch', cursor: 'grab' }} onMouseDown={(e) => { const el = e.currentTarget; el.dataset.dragging = '1'; el.dataset.moved = '0'; el.dataset.startX = e.pageX; el.dataset.scrollLeft = el.scrollLeft; el.style.cursor = 'grabbing'; el.style.userSelect = 'none'; }} onMouseMove={(e) => { const el = e.currentTarget; if (el.dataset.dragging !== '1') return; e.preventDefault(); const dx = e.pageX - parseFloat(el.dataset.startX); if (Math.abs(dx) > 5) el.dataset.moved = '1'; el.scrollLeft = parseFloat(el.dataset.scrollLeft) - dx; }} onMouseUp={(e) => { const el = e.currentTarget; el.dataset.dragging = '0'; el.style.cursor = 'grab'; el.style.userSelect = ''; }} onMouseLeave={(e) => { const el = e.currentTarget; el.dataset.dragging = '0'; el.style.cursor = 'grab'; el.style.userSelect = ''; }} onClickCapture={(e) => { if (e.currentTarget.dataset.moved === '1') { e.stopPropagation(); e.preventDefault(); e.currentTarget.dataset.moved = '0'; } }}>
                      {trendEffects.map((effect) => {
                        const isSelected = selectedMotions.includes(effect.id);
                        const hasPreview = effect.preview_video_url;
                        const thumbUrl = effect.preview_thumbnail_url || (hasPreview ? effect.preview_video_url.replace('.mp4', '_thumb.jpg') : null);
                        if (hasPreview) {
                          return (
                            <div key={effect.id} onClick={() => toggleMotion(effect.id)}
                              className={`flex-shrink-0 w-20 sm:w-24 snap-start rounded-lg sm:rounded-xl overflow-hidden cursor-pointer transition-all active:scale-95 ${isSelected ? 'ring-2 ring-[#F59E0B] ring-offset-1 sm:ring-offset-2 ring-offset-black' : 'ring-1 ring-white/20'}`}>
                              <div className="relative h-28 sm:h-32 bg-black/50">
                                <LazyChipVideo src={effect.preview_proxy_url || effect.preview_video_url} mobileSrc={effect.preview_mobile_url} poster={thumbUrl} className="w-full h-full object-cover" />
                                {isSelected && <div className="absolute top-1 right-1 bg-[#F59E0B] text-black text-[10px] font-bold px-1 sm:px-1.5 py-0.5 rounded">✓</div>}
                              </div>
                              <div className={`p-1.5 sm:p-2 text-center ${isSelected ? 'bg-[#F59E0B]/20' : 'bg-white/5'}`}>
                                <p className="text-white text-[11px] sm:text-xs font-medium truncate">{effect.label}</p>
                              </div>
                            </div>
                          );
                        }
                        return (
                          <button key={effect.id} onClick={() => toggleMotion(effect.id)}
                            className={`flex-shrink-0 px-3 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${isSelected ? 'bg-[#F59E0B]/30 border border-[#F59E0B] text-white' : 'bg-white/10 border border-white/20 text-white/60 hover:text-white'}`}
                          >{effect.label}</button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Dauer */}
                <div className="bg-white/5 rounded-xl p-3">
                  <label className="text-white/60 text-xs mb-2 block">Dauer</label>
                  {activeModel?.duration?.fixed !== undefined ? (
                    <div className="text-white font-medium text-sm">{activeModel.duration.fixed || '-'}s (fest)</div>
                  ) : activeModel?.duration?.options ? (
                    <div className="flex gap-1">
                      {activeModel.duration.options.map((opt) => (
                        <button key={opt} onClick={() => setVideoDuration(opt)}
                          className={`flex-1 py-1.5 rounded-lg text-sm font-medium transition-all ${videoDuration === opt ? 'bg-[#10B981]/30 border border-[#10B981] text-white' : 'bg-white/10 border border-white/20 text-white/70'}`}
                        >{opt}s</button>
                      ))}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <input type="range" min={activeModel?.duration?.min || 1} max={activeModel?.duration?.max || 15} value={videoDuration}
                        onChange={(e) => setVideoDuration(parseInt(e.target.value))}
                        className="flex-1 h-1 bg-white/20 rounded-lg appearance-none cursor-pointer accent-[#10B981]"
                      />
                      <span className="text-white font-medium text-sm w-8">{videoDuration}s</span>
                    </div>
                  )}
                </div>

                {/* Auflösung — Chips */}
                <div className="bg-white/5 rounded-xl p-3">
                  <label className="text-white/60 text-xs mb-2 block">Auflösung</label>
                  <div className="flex gap-2">
                    {(activeModel?.resolutions || ['480p', '720p']).map((res) => (
                      <button key={res} onClick={() => setVideoResolution(res)}
                        className={`flex-1 py-2 rounded-xl text-sm font-medium transition-all flex flex-col items-center gap-0.5 ${
                          videoResolution === res
                            ? 'bg-[#10B981]/30 border border-[#10B981] text-white'
                            : 'bg-white/10 border border-white/20 text-white/70'
                        }`}
                      >
                        <span className="font-bold">{res}</span>
                        <span className="text-[10px] text-white/50">{res === '480p' ? 'Schnell' : res === '720p' ? 'HD' : res === '1080p' ? 'Full HD' : ''}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Videoformat — wie bei Bildformat */}
                <div className="bg-white/5 rounded-xl p-3">
                  <label className="text-white/60 text-xs mb-2 block">Videoformat</label>
                  <div className="flex gap-2">
                    {[
                      { ratio: '9:16', label: 'Story', w: 'w-3', h: 'h-5' },
                      { ratio: '16:9', label: 'Quer', w: 'w-5', h: 'h-3' },
                      { ratio: '1:1', label: 'Feed', w: 'w-4', h: 'h-4' }
                    ].map(({ ratio, label, w, h }) => (
                      <button key={ratio} onClick={() => setVideoAspectRatio(ratio)}
                        className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition-all flex flex-col items-center gap-1 ${
                          videoAspectRatio === ratio
                            ? 'bg-[#10B981] border border-[#10B981] text-black'
                            : 'bg-white/10 border border-white/20 text-white/70'
                        }`}
                      >
                        <div className={`${w} ${h} border-2 rounded-sm ${videoAspectRatio === ratio ? 'border-black/60' : 'border-current'}`} />
                        <span className="font-bold text-xs">{ratio}</span>
                        <span className={`text-[10px] ${videoAspectRatio === ratio ? 'text-black/60' : 'text-white/40'}`}>{label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Video erstellen Button */}
                {(() => {
                  const hasImage = selectedSourceImage || image?.url;
                  const hasPromptOrChips = selectedMotions.length > 0 || customPrompt;
                  const isValid = hasImage && hasPromptOrChips;

                  let buttonText = 'Video erstellen';
                  let missingTarget = null;
                  if (!hasImage) { buttonText = 'Foto wählen'; missingTarget = 'photo-slider'; }
                  else if (!hasPromptOrChips) { buttonText = 'Regieanweisung eingeben'; missingTarget = 'prompt-area'; }

                  return (
                    <button
                      onClick={() => {
                        if (isValid && !isGenerating) {
                          handleGenerate();
                        } else if (missingTarget === 'photo-slider' && photoSliderRef.current) {
                          photoSliderRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        } else if (missingTarget === 'prompt-area') {
                          const promptEl = document.getElementById('video-prompt-textarea');
                          if (promptEl) {
                            promptEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            promptEl.focus();
                          }
                        }
                      }}
                      className={`w-full py-3.5 sm:py-4 rounded-xl text-white font-bold text-base sm:text-lg transition-all active:scale-[0.98] ${
                        isValid && !isGenerating ? 'bg-[#10B981]/70 hover:bg-[#10B981]/80 active:bg-[#10B981]' : 'bg-white/20'
                      }`}
                    >
                      {isGenerating ? (
                        <span className="flex items-center justify-center gap-3">
                          <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Video wird erstellt...
                        </span>
                      ) : buttonText}
                    </button>
                  );
                })()}

                {/* Basic-Modus Fortschrittsbalken */}
                {isGenerating && (
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-3">
                      <div className="flex-1 h-1.5 bg-white/20 rounded-full overflow-hidden">
                        <div
                          className={`h-full bg-[#10B981] transition-all duration-200 ease-out ${displayProgress >= 95 ? 'animate-pulse' : ''}`}
                          style={{ width: `${Math.round(displayProgress)}%` }}
                        />
                      </div>
                      <span className={`text-emerald-400 font-bold text-sm w-12 text-right ${displayProgress >= 95 ? 'animate-pulse' : ''}`}>
                        {Math.round(displayProgress)}%
                      </span>
                    </div>
                    {statusMessage && (
                      <p className="text-white/50 text-xs text-center">{statusMessage}</p>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Generierung läuft */}
        {isGenerating && !inline && (
          <div className="p-6 flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-2 border-[#10B981]/30 border-t-[#10B981] rounded-full animate-spin" />
            <p className="text-white font-medium">{statusMessage || 'Video wird generiert...'}</p>
            <div className="w-full bg-white/10 rounded-full h-2">
              <div className="bg-[#10B981] h-2 rounded-full transition-all" style={{ width: `${displayProgress}%` }} />
            </div>
          </div>
        )}

        {/* Fertiges Video */}
        {generatedVideoUrl && !isGenerating && (
          <div className="p-4 space-y-3">
            <div className="rounded-xl overflow-hidden border border-[#10B981]/30">
              <video ref={videoRef} src={generatedVideoUrl} controls autoPlay className="w-full h-auto" playsInline />
            </div>
            <div className="flex gap-2">
              <a href={generatedVideoUrl} download className="flex-1 py-3 bg-[#10B981] hover:bg-[#059669] text-white font-bold rounded-xl flex items-center justify-center gap-2 transition-colors">Download</a>
              <button onClick={() => { setGeneratedVideoUrl(null); setDisplayProgress(0); setBackendProgress(0); }} className="py-3 px-4 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-medium rounded-xl transition-all">Neues Video</button>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="p-4">
            <div className="bg-red-500/20 border border-red-500/50 rounded-xl p-3 text-red-400 text-sm">
              <div className="flex items-center justify-between">
                <span>{error}</span>
                <button onClick={() => setError(null)} className="ml-2 text-red-400/60 hover:text-red-400">×</button>
              </div>
              {error.toLowerCase().includes('snap') && (
                <a href="/credits" className="mt-2 inline-block text-[#10B981] font-bold text-sm hover:underline">
                  → SNAPs jetzt aufladen
                </a>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  return createPortal(
    <div
      ref={modalRef}
      className="fixed z-[99999] bg-black"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100dvh',
        margin: 0,
        padding: 0,
        overflow: 'hidden',
        overflowX: 'hidden',
        overflowY: 'hidden',
        boxSizing: 'border-box',
        touchAction: 'pan-y',
        WebkitUserSelect: 'none',
        WebkitTouchCallout: 'none',
        userSelect: 'none',
        overscrollBehavior: 'none',
        WebkitOverflowScrolling: 'auto'
      }}
    >
      {/* VIDEO FULLSCREEN OVERLAY */}
      {isVideoFullscreen && generatedVideoUrl && (
        <VideoFullscreenPlayer 
          videoUrl={generatedVideoUrl}
          onClose={() => setIsVideoFullscreen(false)}
        />
      )}

      {/* FULLSCREEN Blurred Image Background - NUR bei Generierung (nicht bei fertigem Video!) */}
      {/* Bei Lipsync (videoInputUrl gesetzt): Zeige Poster vom ausgewählten Video */}
      {!existingVideo && !generatedVideoUrl && (
        <>
          {/* Schwarzer Hintergrund (immer) */}
          <div className="absolute inset-0 bg-black" />
          {/* Blur-Bild nur wenn vorhanden */}
          {(selectedSourceImage || image?.url) && (
            <div
              className="absolute inset-0"
              style={{
                backgroundImage: `url(${
                  videoInputUrl 
                    ? (availableVideos.find(v => v.video_url === videoInputUrl)?.source_image_url || selectedSourceImage || image?.url)
                    : (selectedSourceImage || image?.url)
                })`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                filter: `blur(${blurAmount}px)`,
                transform: 'scale(1.1)',
                transition: 'all 0.3s ease'
              }}
            />
          )}
        </>
      )}
      
      {/* Schwarzer Hintergrund für fertiges Video (existingVideo ODER neu generiert) */}
      {(existingVideo || generatedVideoUrl) && (
        <div className="absolute inset-0 bg-black" />
      )}

      {/* GROK-Style Fullscreen Dot Grid Overlay - KONSTANTE kleine Dots! */}
      {isGenerating && dotOpacity > 0 && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `radial-gradient(circle, rgba(255,255,255,${dotOpacity}) ${dotSize}px, transparent ${dotSize}px)`,
            backgroundSize: `${dotSpacing}px ${dotSpacing}px`,
            transition: 'opacity 0.5s ease'
          }}
        />
      )}

      {/* Header - Zurück-Button IMMER sichtbar */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between p-4 z-50">
        <button
          onClick={() => {
            // Bei existingVideo: Direkt schließen (kein Zurück zur Prompt-Eingabe)
            if (existingVideo) {
              onClose();
            } else if (generatedVideoUrl) {
              setGeneratedVideoUrl(null);
              setDisplayProgress(0);
              setBackendProgress(0);
            } else {
              onClose();
            }
          }}
          className="w-10 h-10 rounded-full bg-black/50 backdrop-blur flex items-center justify-center"
        >
          <span className="text-white text-xl">{generatedVideoUrl ? '×' : '←'}</span>
        </button>
        
        {/* Format Badge - nur bei existingVideo */}
        {existingVideo && (
          <span className="px-3 py-1.5 rounded-full bg-black/50 backdrop-blur text-white/90 text-xs font-medium">
            {existingVideo.aspect_ratio || '9:16'}
          </span>
        )}
        
        {!existingVideo && <div></div>}
        {/* Speichern Button oben rechts - nur wenn Speed geändert */}
        {existingVideo?.id && playbackRate !== (existingVideo?.original_speed || 1) && (
          <button
            onClick={async () => {
              if (isSavingSpeed) return;
              setIsSavingSpeed(true);
              try {
                const token = getToken();
                const response = await fetch('/api/beatleap/change-speed', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                  },
                  body: JSON.stringify({
                    video_id: existingVideo.id,
                    speed: playbackRate
                  })
                });
                const data = await response.json();
                if (data.success) {
                  const toast = document.createElement('div');
                  toast.textContent = `✅ ${playbackRate}x gespeichert!`;
                  toast.className = 'fixed top-20 left-1/2 transform -translate-x-1/2 bg-[#10B981] text-white px-6 py-3 rounded-xl font-bold shadow-xl z-[999999]';
                  document.body.appendChild(toast);
                  setTimeout(() => toast.remove(), 3000);
                  onClose();
                } else {
                  throw new Error(data.detail || 'Fehler');
                }
              } catch (err) {
                alert('Fehler: ' + err.message);
              } finally {
                setIsSavingSpeed(false);
              }
            }}
            disabled={isSavingSpeed}
            className="w-10 h-10 rounded-full bg-black/50 backdrop-blur flex items-center justify-center"
          >
            {isSavingSpeed ? (
              <Loader2 className="w-5 h-5 text-[#10B981] animate-spin" />
            ) : (
              <svg className="w-5 h-5 text-[#10B981]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            )}
          </button>
        )}
        {/* Platzhalter wenn kein Speichern-Button */}
        {!(existingVideo?.id && playbackRate !== (existingVideo?.original_speed || 1)) && <div className="w-10"></div>}
      </div>

      {/* Center Content - Video after completion */}
      {generatedVideoUrl && (
        <div className="absolute inset-0 flex items-center justify-center z-10 p-4">
          <video
            ref={videoRef}
            src={generatedVideoUrl}
            autoPlay
            loop
            muted={isMuted}
            playsInline
            preload="auto"
            className="w-full h-full object-contain"
            onLoadedMetadata={() => {
              if (videoRef.current) {
                setDuration(videoRef.current.duration);
                videoRef.current.playbackRate = playbackRate;
              }
            }}
            onPlay={() => {
              // Nach Autoplay-Start sofort Sound aktivieren
              if (videoRef.current && videoRef.current.muted && isMuted) {
                try {
                  videoRef.current.muted = false;
                  setIsMuted(false);
                } catch (e) {
                  // Safari blockiert — bleibt muted, User kann manuell aktivieren
                }
              }
            }}
            onTimeUpdate={() => {
              if (videoRef.current) {
                setCurrentTime(videoRef.current.currentTime);
              }
            }}
          />
        </div>
      )}

      {/* Bottom Controls - Fixed at bottom */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/90 to-transparent z-10 pb-8 pt-4">
        {/* Inner container - full width with padding */}
        <div 
          className="space-y-4 px-4"
          style={{ 
            touchAction: 'auto',
            width: '100%',
            boxSizing: 'border-box'
          }}
        >
        {/* GROK-Style: Edit Prompt WÄHREND Generierung! */}
        {isGenerating && (
          <div className="space-y-3">
            {/* Aktueller Prompt - zur Kontrolle */}
            {activePrompt && (
              <div className="bg-white/5 border border-white/10 rounded-xl p-3">
                <span className="text-white/40 text-xs uppercase tracking-wider">Bewegungs-Prompt:</span>
                <p className="text-white/70 text-sm mt-1 line-clamp-3">{activePrompt}</p>
              </div>
            )}
            
            {/* Progress Bar (ohne Prozentzahl) */}
            <div className="flex items-center gap-3">
              <div className="flex-1 h-1.5 bg-white/20 rounded-full overflow-hidden">
                <div
                  className={`h-full bg-[#10B981] transition-all duration-200 ease-out ${
                    displayProgress >= 95 ? 'animate-pulse' : ''
                  }`}
                  style={{ width: `${Math.round(displayProgress)}%` }}
                />
              </div>
            </div>

            {/* Spinner zentriert */}
            <div className="flex justify-center">
              <Loader2 className="w-4 h-4 animate-spin text-[#10B981]" />
            </div>
          </div>
        )}

        {/* TEMPLATE MODE - Prompt Preview mit DE/EN Flagge */}
        {(!isGenerating || inline) && !generatedVideoUrl && image.templatePrompt && (
          <div className="space-y-3">
            {/* Template Name Badge */}
            <div className="flex items-center justify-center gap-2">
              <span className="px-3 py-1 bg-[#10B981]/20 rounded-full text-[#10B981] text-sm">
                {image.templateName || 'Vorlage'}
              </span>
            </div>

            {/* Prompt mit DE/EN Flagge */}
            <div className="relative">
              <textarea
                value={customPrompt || image.templatePrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                placeholder="Bewegungsbeschreibung..."
                rows={3}
                className="w-full px-4 py-3 pr-14 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/40 focus:border-[#10B981] focus:outline-none resize-none text-sm"
              />
              {/* DE/EN Flagge */}
              <button
                onClick={async () => {
                  const currentText = customPrompt || image.templatePrompt;
                  // Toggle DE/EN - wenn deutsch, übersetze zu Englisch und umgekehrt
                  const isGerman = /[äöüß]|und|der|die|das|ist|mit/i.test(currentText);
                  try {
                    const token = getToken();
                    const response = await fetch('/api/translate', {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                      },
                      body: JSON.stringify({
                        text: currentText,
                        source_lang: isGerman ? 'de' : 'en',
                        target_lang: isGerman ? 'en' : 'de'
                      })
                    });
                    const data = await response.json();
                    if (data.translated_text) {
                      setCustomPrompt(data.translated_text);
                    }
                  } catch (err) {
                    console.error('Translation error:', err);
                  }
                }}
                className="absolute right-3 top-3 w-8 h-6 rounded overflow-hidden border border-white/30 hover:border-white/50 transition-colors"
              >
                <span className="text-xs">🇩🇪🇬🇧</span>
              </button>
            </div>

            <p className="text-white/40 text-xs text-center">
              Prompt bearbeiten oder direkt starten
            </p>

            {/* Generate Button */}
            <button
              onClick={handleGenerate}
              className="w-full py-4 rounded-xl bg-[#10B981]/70 text-white font-bold text-lg backdrop-blur"
            >
              Video erstellen
            </button>
          </div>
        )}

        {/* KI-OPTIMIERER MODUS - KOMPLETT NEU MIT MOTION-TAGS + MODELL-AUSWAHL */}
        {(!isGenerating || inline) && !generatedVideoUrl && !image?.templatePrompt && (
          <div className="modal-scroll-area space-y-3 max-h-[70vh] overflow-y-auto overflow-x-hidden overscroll-y-contain relative z-20" style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-y', overscrollBehaviorX: 'none', boxSizing: 'border-box' }}>
            
            {/* ═══ 📸 FOTO-GALERIE - GANZ OBEN, wie bei Sprechend ═══ */}
            {availablePhotos.length >= 1 && (!activeModel?.category || activeModel?.category === 'video') && (
              <div className="space-y-2">
                <span className="text-white/60 text-xs font-medium uppercase tracking-wider">
                  📸 Foto wählen
                </span>
                <div
                  className="flex gap-3 overflow-x-auto pb-3 snap-x snap-mandatory"
                  style={{
                    WebkitOverflowScrolling: 'touch',
                    cursor: 'grab'
                  }}
                  onMouseDown={(e) => { const el = e.currentTarget; el.dataset.dragging = '1'; el.dataset.moved = '0'; el.dataset.startX = e.pageX; el.dataset.scrollLeft = el.scrollLeft; el.style.cursor = 'grabbing'; el.style.userSelect = 'none'; }}
                  onMouseMove={(e) => { const el = e.currentTarget; if (el.dataset.dragging !== '1') return; e.preventDefault(); const dx = e.pageX - parseFloat(el.dataset.startX); if (Math.abs(dx) > 5) el.dataset.moved = '1'; el.scrollLeft = parseFloat(el.dataset.scrollLeft) - dx; }}
                  onMouseUp={(e) => { const el = e.currentTarget; el.dataset.dragging = '0'; el.style.cursor = 'grab'; el.style.userSelect = ''; }}
                  onMouseLeave={(e) => { const el = e.currentTarget; el.dataset.dragging = '0'; el.style.cursor = 'grab'; el.style.userSelect = ''; }}
                  onClickCapture={(e) => { if (e.currentTarget.dataset.moved === '1') { e.stopPropagation(); e.preventDefault(); e.currentTarget.dataset.moved = '0'; } }}
                >
                  {availablePhotos.map((photo) => {
                    const isSelected = selectedSourceImage === photo.url || (!selectedSourceImage && photo.url === image?.url);
                    const isLandscape = landscapePhotos.has(photo.url);
                    return (
                      <button
                        key={photo.id}
                        onClick={() => setSelectedSourceImage(photo.url)}
                        className={`relative flex-shrink-0 h-36 rounded-xl overflow-hidden transition-all snap-start ${
                          isSelected
                            ? 'border-2 border-[#10B981] shadow-lg shadow-[#10B981]/30'
                            : 'border-2 border-white/20 hover:border-white/40'
                        }`}
                        style={{ width: isLandscape ? '12rem' : '6rem' }}
                      >
                        <img
                          src={photo.url}
                          alt=""
                          loading="lazy"
                          draggable={false}
                          className="w-full h-full object-cover pointer-events-none select-none"
                          onLoad={(e) => {
                            const { naturalWidth, naturalHeight } = e.target;
                            if (naturalWidth > naturalHeight) {
                              setLandscapePhotos(prev => { const next = new Set(prev); next.add(photo.url); return next; });
                            }
                          }}
                        />
                        {isSelected && (
                          <div className="absolute top-2 right-2 w-5 h-5 bg-[#10B981] rounded-full flex items-center justify-center">
                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
                {/* Format-Info: Alle Formate erlaubt */}
              </div>
            )}

            {/* ═══ 1. MODELLE — versteckt wenn der Modus schon vorgegeben wurde (Picker-Flow) ═══ */}
            {!hideModelSelector && (
            <div className="space-y-2">
              <span className="text-white/60 text-xs font-medium uppercase tracking-wider">
                Modelle
              </span>
              <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
                {Object.entries(VIDEO_MODELS).filter(([_, m]) => { const uid = JSON.parse(localStorage.getItem('snapshot_user')||'{}').id; if (m.allowedUsers && !m.allowedUsers.includes(uid)) return false; return m.category === 'video' || !m.category; }).map(([key, model]) => (
                  <button
                    key={key}
                    onClick={() => handleModelChange(key)}
                    className={`flex-shrink-0 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                      selectedModel === key
                        ? 'bg-[#10B981]/30 border border-[#10B981] text-white'
                        : 'bg-white/10 border border-white/20 text-white/70 hover:bg-white/20'
                    }`}
                  >
                    {model.label}
                  </button>
                ))}
                {/* Platzhalter entfernt — cinematic_video ist jetzt in VIDEO_MODELS */}
                {false && <button className="hidden">Premium</button>}
              </div>
            </div>
            )}

            {/* ═══ 🎬 KINO-MODUS LIPSYNC PIPELINE (bei cinematic_video) ═══ */}
            {selectedModel === 'cinematic_video' && (
              <div className="space-y-4">
                {/* Text-Eingabe */}
                <div className="space-y-2">
                  <span className="text-white/60 text-xs font-medium uppercase tracking-wider">
                    Was soll passieren?
                  </span>
                  <textarea
                    value={kinoText}
                    onChange={(e) => { if (e.target.value.length <= 5000) setKinoText(e.target.value); }}
                    placeholder={"Beschreibe die Szene oder was dein Avatar sagen soll.\n\nBeispiele:\n- \"Strandszene, die Frau stellt sich vor\"\n- Die Frau sagt: \"Hallo, willkommen!\"\n- \"Professionelles Vorstellungsvideo\""}
                    rows={4}
                    className="w-full bg-white/5 border border-white/20 focus:border-[#10B981] rounded-xl p-3 text-white placeholder-white/30 text-sm transition-all resize-none outline-none"
                  />
                  <div className="flex items-center justify-between">
                    <p className="text-white/30 text-xs">KI erkennt automatisch ob du eine Szene beschreibst oder einen Sprechtext vorgibst.</p>
                    <span className={`text-xs ${kinoText.length >= 5000 ? 'text-red-400' : 'text-white/30'}`}>{kinoText.length}/5000</span>
                  </div>
                </div>

                {/* Error */}
                {kinoError && (
                  <div className="bg-red-500/20 border border-red-500/50 rounded-xl p-3 flex items-center gap-2">
                    <span className="text-red-400 text-sm flex-1">{kinoError}</span>
                    <button onClick={() => setKinoError(null)} className="text-red-400/60 hover:text-red-400 text-lg">×</button>
                  </div>
                )}

                {/* Generate Button */}
                <button
                  onClick={() => {
                    const hasImg = selectedSourceImage || image?.url;
                    if (hasImg && kinoText.trim()) {
                      handleKinoGenerate();
                    } else if (!hasImg && photoSliderRef.current) {
                      photoSliderRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                  }}
                  className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${
                    (selectedSourceImage || image?.url) && kinoText.trim()
                      ? 'bg-[#10B981]/70 hover:bg-[#10B981]/80 text-white'
                      : 'bg-white/20 text-white/30'
                  }`}
                >
                  {kinoGenerating ? (
                    <span className="flex items-center justify-center gap-3">
                      <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Video wird erstellt...
                    </span>
                  ) : !(selectedSourceImage || image?.url) ? 'Foto wählen' : !kinoText.trim() ? 'Text eingeben' : 'Video erstellen'}
                </button>
              </div>
            )}

            {/* ═══ 🎬 BASIC MODUS: Genres, Beschreibung, Chips (nur wenn NICHT cinematic_video) ═══ */}
            {selectedModel !== 'cinematic_video' && cinemaGenres.length > 0 && (
              <div className="space-y-2">
                <span className="text-white/60 text-xs font-medium uppercase tracking-wider">
                  Genre Auswahl
                </span>
                <div className="flex flex-wrap gap-2">
                  {cinemaGenres.map((genre) => (
                    <button
                      key={genre.key}
                      onClick={() => handleCinemaAnalyze(genre.key)}
                      disabled={cinemaLoading}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                        selectedCinemaGenre === genre.key
                          ? 'bg-emerald-500 text-white'
                          : 'bg-white/10 text-white/80 hover:bg-white/20'
                      } ${cinemaLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {cinemaLoading && selectedCinemaGenre === genre.key ? (
                        <span className="flex items-center gap-2">
                          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                          </svg>
                          Analysiere...
                        </span>
                      ) : (
                        <span>{genre.label}</span>
                      )}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-white/40">
                  Klicke auf ein Genre für automatische Szenen-Analyse
                </p>
              </div>
            )}

            {/* ═══ BESCHREIBUNG (zwischen Modell und Bewegung) — nur Basic ═══ */}
            {selectedModel !== 'cinematic_video' && (
            <div className="space-y-1">
              <span className="text-white/60 text-xs font-medium uppercase tracking-wider">
                {promptSettings.label || "Beschreibung"}
              </span>
              <textarea
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                placeholder={promptSettings.placeholder || "Was soll im Video passieren?"}
                rows={2}
                className="w-full px-3 py-2 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/40 focus:border-[#10B981] focus:outline-none resize-none text-sm"
              />
            </div>
            )}

            {/* ═══ PREVIEW FULLSCREEN MODAL - EXAKT wie VideoActionSheet ═══ */}
            {previewFullscreen && (() => {
              const allPreviewChips = [
                ...motionTemplates.filter(m => m.preview_video_url),
                ...cameraTemplates.filter(c => c.preview_video_url),
                ...trendEffects.filter(t => t.preview_video_url)
              ];
              const currentIndex = allPreviewChips.findIndex(c => c.id === previewFullscreen.id);
              const canGoPrev = currentIndex > 0;
              const canGoNext = currentIndex < allPreviewChips.length - 1;
              
              const goToPrev = () => {
                if (canGoPrev) {
                  const prev = allPreviewChips[currentIndex - 1];
                  setPreviewFullscreen({
                    id: prev.id, label: prev.label, videoUrl: prev.preview_720p_url || prev.preview_video_url,
                    category: motionTemplates.find(m => m.id === prev.id) ? 'motion' : 'camera'
                  });
                }
              };
              
              const goToNext = () => {
                if (canGoNext) {
                  const next = allPreviewChips[currentIndex + 1];
                  setPreviewFullscreen({
                    id: next.id, label: next.label, videoUrl: next.preview_720p_url || next.preview_video_url,
                    category: motionTemplates.find(m => m.id === next.id) ? 'motion' : 'camera'
                  });
                }
              };
              
              return createPortal(
                <div 
                  className="fixed inset-0 bg-black"
                  style={{
                    zIndex: 999999,
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    WebkitTouchCallout: 'none',
                  }}
                >
                  {/* VIDEO - Fullscreen */}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <video
                      id="preview-fullscreen-video"
                      key={previewFullscreen.videoUrl}
                      src={previewFullscreen.videoUrl}
                      autoPlay loop muted={previewMuted} playsInline
                      className="max-w-full max-h-full object-contain select-none"
                      style={{ pointerEvents: 'none' }}
                    />
                  </div>
                  
                  {/* Navigation Pfeile - Links/Rechts Mitte */}
                  {canGoPrev && (
                    <div
                      onClick={goToPrev}
                      className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center cursor-pointer active:scale-95"
                      style={{ zIndex: 10 }}
                    >
                      <svg className="w-6 h-6 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </div>
                  )}
                  {canGoNext && (
                    <div
                      onClick={goToNext}
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center cursor-pointer active:scale-95"
                      style={{ zIndex: 10 }}
                    >
                      <svg className="w-6 h-6 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  )}
                  
                  {/* Sound Button - rechts */}
                  <button
                    onClick={() => setPreviewMuted(!previewMuted)}
                    className={`absolute bottom-32 right-4 w-10 h-10 rounded-full backdrop-blur-sm flex items-center justify-center ${previewMuted ? "bg-black/30" : "bg-[#10B981]"}`}
                    style={{ zIndex: 10 }}
                  >
                    {previewMuted ? (
                      <svg className="w-5 h-5 text-white/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707A1 1 0 0112 5v14a1 1 0 01-1.707.707L5.586 15z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M3 3l18 18" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072M18.364 5.636a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707A1 1 0 0112 5v14a1 1 0 01-1.707.707L5.586 15z" />
                      </svg>
                    )}
                  </button>
                  
                  {/* UI OVERLAY */}
                  <div className="absolute inset-0 flex flex-col pointer-events-none">
                    {/* Header */}
                    <div className="p-3 pt-safe flex items-center justify-between pointer-events-auto">
                      <button
                        onClick={() => setPreviewFullscreen(null)}
                        className="w-10 h-10 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center active:scale-95"
                      >
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      <div className="flex-1" />
                    </div>
                    
                    <div className="flex-1" />
                    
                    {/* Footer - Label + Glass Button */}
                    <div className="p-4 pb-8 pointer-events-auto">
                      {/* Label */}
                      <p className="text-white/80 text-center text-sm mb-4">{previewFullscreen.label}</p>
                      
                      {/* Glass Buttons Row */}
                      <div className="flex justify-center items-center gap-3">
                        {/* Übernehmen Glass Button */}
                        <button
                          onClick={() => {
                            toggleMotion(previewFullscreen.id);
                            setPreviewFullscreen(null);
                          }}
                          className="px-5 py-2 rounded-full bg-white/10 backdrop-blur-sm flex items-center gap-1.5 active:scale-95"
                        >
                          <span className="text-white text-sm font-medium">
                            {selectedMotions.includes(previewFullscreen.id) ? '✓ Aktiv' : 'Übernehmen'}
                          </span>
                        </button>
                        
                        {/* Counter */}
                        <div className="px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-sm">
                          <span className="text-white text-sm font-medium">{currentIndex + 1} / {allPreviewChips.length}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              , document.body);
            })()}

            {/* ═══ 2. BEWEGUNG (Motion-Tags) mit Video-Previews ═══ */}
            {selectedModel !== 'cinematic_video' && (!activeModel?.category || activeModel?.category === 'video') && !activeModel?.needsCameraMovement && (
            <div className="space-y-2">
              <span className="text-white/60 text-xs font-medium uppercase tracking-wider">
                {sectionTitles.motion || "Bewegung"}
              </span>
              <div className="flex gap-3 overflow-x-auto py-2 px-1 snap-x" style={{ WebkitOverflowScrolling: 'touch', cursor: 'grab' }} onMouseDown={(e) => { const el = e.currentTarget; el.dataset.dragging = '1'; el.dataset.moved = '0'; el.dataset.startX = e.pageX; el.dataset.scrollLeft = el.scrollLeft; el.style.cursor = 'grabbing'; el.style.userSelect = 'none'; }} onMouseMove={(e) => { const el = e.currentTarget; if (el.dataset.dragging !== '1') return; e.preventDefault(); const dx = e.pageX - parseFloat(el.dataset.startX); if (Math.abs(dx) > 5) el.dataset.moved = '1'; el.scrollLeft = parseFloat(el.dataset.scrollLeft) - dx; }} onMouseUp={(e) => { const el = e.currentTarget; el.dataset.dragging = '0'; el.style.cursor = 'grab'; el.style.userSelect = ''; }} onMouseLeave={(e) => { const el = e.currentTarget; el.dataset.dragging = '0'; el.style.cursor = 'grab'; el.style.userSelect = ''; }} onClickCapture={(e) => { if (e.currentTarget.dataset.moved === '1') { e.stopPropagation(); e.preventDefault(); e.currentTarget.dataset.moved = '0'; } }}>
                {motionTemplates.map((motion) => {
                  const isSelected = selectedMotions.includes(motion.id);
                  const hasPreview = motion.preview_video_url;
                  const thumbUrl = motion.preview_thumbnail_url || (hasPreview ? motion.preview_video_url.replace('.mp4', '_thumb.jpg') : null);
                  
                  // Video-Card wenn Preview vorhanden
                  if (hasPreview) {
                    return (
                      <div
                        key={motion.id}
                        onClick={() => toggleMotion(motion.id)}
                        className={`flex-shrink-0 w-24 snap-start rounded-xl overflow-hidden cursor-pointer transition-all active:scale-95 ${
                          isSelected
                            ? 'ring-2 ring-[#10B981] ring-offset-2 ring-offset-black'
                            : 'ring-1 ring-white/20 hover:ring-white/40'
                        }`}
                      >
                        <div className="relative h-32 bg-black/50">
                          <LazyChipVideo
                            src={motion.preview_proxy_url || motion.preview_video_url}
                            mobileSrc={motion.preview_mobile_url}
                            poster={thumbUrl}
                            className="w-full h-full object-cover"
                          />
                          {isSelected && (
                            <div className="absolute top-1 right-1 bg-[#10B981] text-black text-[10px] font-bold px-1.5 py-0.5 rounded">✓</div>
                          )}
                        </div>
                        <div className={`p-2 text-center ${isSelected ? 'bg-[#10B981]/20' : 'bg-white/5'}`}>
                          <p className="text-white text-xs font-medium truncate">{motion.label}</p>
                        </div>
                      </div>
                    );
                  }
                  
                  // Fallback: Text-Button wenn kein Preview
                  return (
                    <button
                      key={motion.id}
                      onClick={() => toggleMotion(motion.id)}
                      className={`flex-shrink-0 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                        isSelected
                          ? 'bg-[#10B981]/30 border border-[#10B981] text-white'
                          : 'bg-white/10 border border-white/20 text-white/70 hover:bg-white/20'
                      }`}
                    >
                      {motion.label}
                    </button>
                  );
                })}
              </div>
            </div>
            )}

            {/* ═══ 3. KAMERAFAHRT mit Video-Previews ═══ */}
            {selectedModel !== 'cinematic_video' && (!activeModel?.category || activeModel?.category === 'video') && !activeModel?.needsCameraMovement && (
            <div className="space-y-2">
              <span className="text-white/60 text-xs font-medium uppercase tracking-wider">
                {sectionTitles.camera || "🎥 Kamerafahrt"}
              </span>
              <div className="flex gap-3 overflow-x-auto py-2 px-1 snap-x" style={{ WebkitOverflowScrolling: 'touch', cursor: 'grab' }} onMouseDown={(e) => { const el = e.currentTarget; el.dataset.dragging = '1'; el.dataset.moved = '0'; el.dataset.startX = e.pageX; el.dataset.scrollLeft = el.scrollLeft; el.style.cursor = 'grabbing'; el.style.userSelect = 'none'; }} onMouseMove={(e) => { const el = e.currentTarget; if (el.dataset.dragging !== '1') return; e.preventDefault(); const dx = e.pageX - parseFloat(el.dataset.startX); if (Math.abs(dx) > 5) el.dataset.moved = '1'; el.scrollLeft = parseFloat(el.dataset.scrollLeft) - dx; }} onMouseUp={(e) => { const el = e.currentTarget; el.dataset.dragging = '0'; el.style.cursor = 'grab'; el.style.userSelect = ''; }} onMouseLeave={(e) => { const el = e.currentTarget; el.dataset.dragging = '0'; el.style.cursor = 'grab'; el.style.userSelect = ''; }} onClickCapture={(e) => { if (e.currentTarget.dataset.moved === '1') { e.stopPropagation(); e.preventDefault(); e.currentTarget.dataset.moved = '0'; } }}>
                {cameraTemplates.map((cam) => {
                  const isSelected = selectedMotions.includes(cam.id);
                  const hasPreview = cam.preview_video_url;
                  const thumbUrl = cam.preview_thumbnail_url || (hasPreview ? cam.preview_video_url.replace('.mp4', '_thumb.jpg') : null);
                  
                  if (hasPreview) {
                    return (
                      <div
                        key={cam.id}
                        onClick={() => toggleMotion(cam.id)}
                        className={`flex-shrink-0 w-24 snap-start rounded-xl overflow-hidden cursor-pointer transition-all active:scale-95 ${
                          isSelected
                            ? 'ring-2 ring-[#10B981] ring-offset-2 ring-offset-black'
                            : 'ring-1 ring-white/20 hover:ring-white/40'
                        }`}
                      >
                        <div className="relative h-32 bg-black/50">
                          <LazyChipVideo
                            src={cam.preview_proxy_url || cam.preview_video_url}
                            mobileSrc={cam.preview_mobile_url}
                            poster={thumbUrl}
                            className="w-full h-full object-cover"
                          />
                          {isSelected && (
                            <div className="absolute top-1 right-1 bg-[#10B981] text-black text-[10px] font-bold px-1.5 py-0.5 rounded">✓</div>
                          )}
                        </div>
                        <div className={`p-2 text-center ${isSelected ? 'bg-[#10B981]/20' : 'bg-white/5'}`}>
                          <p className="text-white text-xs font-medium truncate">{cam.label}</p>
                        </div>
                      </div>
                    );
                  }
                  
                  // Fallback: Text-Button
                  return (
                    <button
                      key={cam.id}
                      onClick={() => toggleMotion(cam.id)}
                      className={`flex-shrink-0 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                        isSelected
                          ? 'bg-[#10B981]/30 border border-[#10B981] text-white'
                          : 'bg-white/10 border border-white/20 text-white/70 hover:bg-white/20'
                      }`}
                    >
                      {cam.label}
                    </button>
                  );
                })}
              </div>
            </div>
            )}

            {/* ═══ 4. TREND EFFEKTE mit Video-Previews ═══ */}
            {selectedModel !== 'cinematic_video' && (!activeModel?.category || activeModel?.category === 'video') && !activeModel?.needsCameraMovement && (
            <div className="space-y-2">
              <span className="text-white/60 text-xs font-medium uppercase tracking-wider">
                {sectionTitles.trends || "🔥 Trend Effekte"}
              </span>
              <div className="flex gap-3 overflow-x-auto py-2 px-1 snap-x" style={{ WebkitOverflowScrolling: 'touch', cursor: 'grab' }} onMouseDown={(e) => { const el = e.currentTarget; el.dataset.dragging = '1'; el.dataset.moved = '0'; el.dataset.startX = e.pageX; el.dataset.scrollLeft = el.scrollLeft; el.style.cursor = 'grabbing'; el.style.userSelect = 'none'; }} onMouseMove={(e) => { const el = e.currentTarget; if (el.dataset.dragging !== '1') return; e.preventDefault(); const dx = e.pageX - parseFloat(el.dataset.startX); if (Math.abs(dx) > 5) el.dataset.moved = '1'; el.scrollLeft = parseFloat(el.dataset.scrollLeft) - dx; }} onMouseUp={(e) => { const el = e.currentTarget; el.dataset.dragging = '0'; el.style.cursor = 'grab'; el.style.userSelect = ''; }} onMouseLeave={(e) => { const el = e.currentTarget; el.dataset.dragging = '0'; el.style.cursor = 'grab'; el.style.userSelect = ''; }} onClickCapture={(e) => { if (e.currentTarget.dataset.moved === '1') { e.stopPropagation(); e.preventDefault(); e.currentTarget.dataset.moved = '0'; } }}>
                {trendEffects.map((effect) => {
                  const isSelected = selectedMotions.includes(effect.id);
                  const hasPreview = effect.preview_video_url;
                  const thumbUrl = effect.preview_thumbnail_url || (hasPreview ? effect.preview_video_url.replace('.mp4', '_thumb.jpg') : null);

                  if (hasPreview) {
                    return (
                      <div
                        key={effect.id}
                        onClick={() => toggleMotion(effect.id)}
                        className={`flex-shrink-0 w-24 snap-start rounded-xl overflow-hidden cursor-pointer transition-all active:scale-95 ${
                          isSelected
                            ? 'ring-2 ring-[#F59E0B] ring-offset-2 ring-offset-black'
                            : 'ring-1 ring-white/20 hover:ring-white/40'
                        }`}
                      >
                        <div className="relative h-32 bg-black/50">
                          <LazyChipVideo
                            src={effect.preview_proxy_url || effect.preview_video_url}
                            mobileSrc={effect.preview_mobile_url}
                            poster={thumbUrl}
                            className="w-full h-full object-cover"
                          />
                          {isSelected && (
                            <div className="absolute top-1 right-1 bg-[#F59E0B] text-black text-[10px] font-bold px-1.5 py-0.5 rounded">✓</div>
                          )}
                        </div>
                        <div className={`p-2 text-center ${isSelected ? 'bg-[#F59E0B]/20' : 'bg-white/5'}`}>
                          <p className="text-white text-xs font-medium truncate">{effect.label}</p>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <button
                      key={effect.id}
                      onClick={() => toggleMotion(effect.id)}
                      className={`flex-shrink-0 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                        isSelected
                          ? 'bg-[#F59E0B]/30 border border-[#F59E0B] text-white'
                          : 'bg-white/10 border border-white/20 text-white/70 hover:bg-white/20'
                      }`}
                    >
                      {effect.label}
                    </button>
                  );
                })}
              </div>
            </div>
            )}

            {/* ═══ 4. TEXTFELD (Was soll der Avatar sagen?) — nur Basic ═══ */}
            {selectedModel !== 'cinematic_video' && selectedMotions.includes('talking') && (
              <div className="bg-[#10B981]/10 border border-[#10B981]/30 rounded-xl p-3 space-y-2">
                <span className="text-[#10B981] text-xs font-medium uppercase tracking-wider">🎤 Was soll der Avatar sagen?</span>
                <textarea
                  value={talkingText}
                  onChange={(e) => setTalkingText(e.target.value.slice(0, 300))}
                  placeholder="Gib hier deinen Text ein... (z.B. 'Hallo, ich bin Julia von snapshotpro.ai')"
                  rows={3}
                  maxLength={300}
                  className="w-full bg-black/30 text-white rounded-lg px-3 py-2 text-sm border border-[#10B981]/30 outline-none resize-none placeholder-white/30 focus:border-[#10B981]"
                />
                <div className="flex items-center justify-between">
                  <p className="text-white/40 text-xs">{talkingText.length}/300 Zeichen</p>
                  <button
                    onClick={async () => {
                      if (!talkingText.trim()) return;
                      setIsOptimizingTTS(true);
                      try {
                        const token = getToken();
                        const res = await fetch('/api/beatleap/tts/optimize', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                          body: JSON.stringify({ text: talkingText, language: 'de' })
                        });
                        const data = await res.json();
                        if (data.success && data.optimized) {
                          setTalkingText(data.optimized);
                        }
                      } catch (e) {
                        console.error('TTS Optimize error:', e);
                      }
                      setIsOptimizingTTS(false);
                    }}
                    disabled={isOptimizingTTS || !talkingText.trim()}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-gradient-to-r from-purple-500/30 to-pink-500/30 border border-purple-400/50 text-white hover:from-purple-500/50 hover:to-pink-500/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isOptimizingTTS ? (
                      <>
                        <svg className="animate-spin w-3 h-3" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
                        Optimiere...
                      </>
                    ) : (
                      <>✨ Aussprache verbessern</>
                    )}
                  </button>
                </div>
                <p className="text-purple-300/60 text-xs">💡 Tipp: Optimiere den Text für perfekte Aussprache von Firmennamen & Zahlen</p>
              </div>
            )}

            {/* ═══ 5. KOMBINIERTER PROMPT (EDITIERBAR mit DE/EN Flagge - INSTANT SWITCH!) ═══ */}
            {selectedMotions.length > 0 && (
              <div className="bg-white/5 border border-white/10 rounded-xl p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-white/40 text-xs uppercase tracking-wider">📋 Kombinierter Prompt:</span>
                  {/* DE/EN Flagge - INSTANT SWITCH, Übersetzung nur wenn nötig */}
                  <button
                    onClick={async () => {
                      const token = getToken();
                      
                      // Generiere EN wenn noch nicht vorhanden
                      const currentEN = combinedPromptEN || (() => {
                        const parts = [];
                        // Talking-STIL (ohne Text! Text geht separat ans Backend)
                        if (selectedMotions.includes('talking')) {
                          parts.push('PERSON SPEAKING TO CAMERA WITH CLEAR LIP SYNC, lips moving naturally');
                        }
                        // Trend-Effekte
                        trendEffects.filter(t => selectedMotions.includes(t.id)).forEach(t => parts.push(t.prompt));
                        // Bewegungs-Chips
                        motionTemplates.filter(m => ['soft','smile','nod','turn','blink','wind','look','relax'].includes(m.id) && selectedMotions.includes(m.id)).forEach(m => parts.push(m.prompt));
                        // Kamera-Chips
                        cameraTemplates.filter(c => selectedMotions.includes(c.id)).forEach(c => parts.push(c.prompt));
                        // Ambient
                        if (selectedMotions.includes('ambient')) parts.push(motionTemplates.find(m => m.id === 'ambient')?.prompt);
                        return parts.filter(Boolean).join(', ');
                      })();
                      
                      if (combinedPromptLang === 'en') {
                        // Wechsel zu DE
                        if (combinedPromptDE) {
                          // DE Version existiert → sofort wechseln!
                          setCombinedPromptLang('de');
                        } else {
                          // Einmalig übersetzen
                          try {
                            const res = await fetch('/api/translate', {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                              body: JSON.stringify({ text: currentEN, source_lang: 'en', target_lang: 'de' })
                            });
                            const data = await res.json();
                            if (data.translated_text) {
                              setCombinedPromptDE(data.translated_text);
                              setCombinedPromptEN(currentEN);
                              setCombinedPromptLang('de');
                            }
                          } catch (e) { console.error('Translation error:', e); }
                        }
                      } else {
                        // Wechsel zu EN
                        if (combinedPromptEN) {
                          // EN Version existiert → sofort wechseln!
                          setCombinedPromptLang('en');
                        } else {
                          // Einmalig übersetzen (falls User auf DE editiert hat)
                          try {
                            const res = await fetch('/api/translate', {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                              body: JSON.stringify({ text: combinedPromptDE, source_lang: 'de', target_lang: 'en' })
                            });
                            const data = await res.json();
                            if (data.translated_text) {
                              setCombinedPromptEN(data.translated_text);
                              setCombinedPromptLang('en');
                            }
                          } catch (e) { console.error('Translation error:', e); }
                        }
                      }
                    }}
                    className="flex items-center gap-1 px-2 py-1 rounded-lg bg-white/10 hover:bg-white/20 transition-all text-xs"
                  >
                    {combinedPromptLang === 'en' ? '🇬🇧 EN' : '🇩🇪 DE'}
                  </button>
                </div>
                <textarea
                  value={combinedPromptLang === 'en' 
                    ? (combinedPromptEN || (() => {
                        const parts = [];
                        // Talking-STIL (ohne Text! Text bleibt Deutsch, geht separat ans Backend)
                        if (selectedMotions.includes('talking')) {
                          parts.push('PERSON SPEAKING TO CAMERA WITH CLEAR LIP SYNC, lips moving naturally');
                        }
                        // Trend-Effekte
                        trendEffects.filter(t => selectedMotions.includes(t.id)).forEach(t => parts.push(t.prompt));
                        // Bewegungs-Chips
                        motionTemplates.filter(m => ['soft','smile','nod','turn','blink','wind','look','relax'].includes(m.id) && selectedMotions.includes(m.id)).forEach(m => parts.push(m.prompt));
                        // Kamera-Chips
                        cameraTemplates.filter(c => selectedMotions.includes(c.id)).forEach(c => parts.push(c.prompt));
                        // Ambient
                        if (selectedMotions.includes('ambient')) parts.push(motionTemplates.find(m => m.id === 'ambient')?.prompt);
                        return parts.filter(Boolean).join(', ');
                      })())
                    : combinedPromptDE
                  }
                  onChange={(e) => {
                    if (combinedPromptLang === 'en') {
                      setCombinedPromptEN(e.target.value);
                      setCombinedPromptDE(''); // Reset DE wenn EN editiert wird
                    } else {
                      setCombinedPromptDE(e.target.value);
                      setCombinedPromptEN(''); // Reset EN wenn DE editiert wird
                    }
                  }}
                  placeholder="Kombinierter Prompt aus allen Chips..."
                  rows={4}
                  className="w-full px-3 py-2 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/40 focus:border-[#10B981] focus:outline-none resize-none text-sm"
                />
                <p className="text-white/30 text-xs">✏️ Du kannst den Prompt editieren und deinen eigenen Senf dazugeben!</p>
              </div>
            )}

            {/* ═══ VIDEO-MODELL (NUR für Snapshot Spezial) ═══ */}
            {/* NUR FÜR ADMINS SICHTBAR (info@snapshotpro.ai, diana@snapshotpro.ai) */}
            {false && (
            <div className="space-y-3">
              {/* Snapshot Spezial */}
              <div>
                <span className="text-white/60 text-xs font-medium uppercase tracking-wider">
                  ✨ Snapshot Spezial
                </span>
                <div className="flex gap-2 overflow-x-auto pb-1 mt-2 scrollbar-hide">
                  {Object.entries(VIDEO_MODELS).filter(([_, m]) => m.category === 'pixverse').map(([key, model]) => (
                    <button
                      key={key}
                      onClick={() => handleModelChange(key)}
                      className={`flex-shrink-0 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                        selectedModel === key
                          ? 'bg-purple-500/30 border border-purple-400 text-white'
                          : 'bg-white/10 border border-white/20 text-white/70 hover:bg-white/20'
                      }`}
                    >
                      {model.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Modell-Info Box */}
              <div className="bg-white/5 rounded-xl p-3 border border-white/10">
                <div className="flex items-center justify-between">
                  <span className="text-white font-medium">{activeModel?.label}{activeModel?.effects && <span className="text-white/40 text-xs ml-1.5">({activeModel.effects.length})</span>}</span>
                  <span className="text-[#10B981] text-sm font-medium">{activeModel?.price}</span>
                </div>
                <p className="text-white/50 text-sm mt-1">{activeModel?.description}</p>
              </div>
            </div>
            )}

            {/* ═══ MODELL-SPEZIFISCHE SETTINGS (nicht bei Kino Modus) ═══ */}
            {selectedModel !== 'cinematic_video' && <div className="grid grid-cols-2 gap-3">
              {/* Duration - Buttons bei options, Slider bei min/max, Text bei fixed */}
              <div className="bg-white/5 rounded-xl p-3">
                <label className="text-white/60 text-xs mb-2 block">Dauer</label>
                {activeModel?.duration.fixed !== undefined ? (
                  <div className="text-white font-medium text-sm">{activeModel.duration.fixed || '-'}s (fest)</div>
                ) : activeModel?.duration.options ? (
                  <div className="flex gap-1">
                    {activeModel.duration.options.map((opt) => (
                      <button
                        key={opt}
                        onClick={() => setVideoDuration(opt)}
                        className={`flex-1 py-1 rounded-lg text-sm font-medium transition-all ${
                          videoDuration === opt
                            ? 'bg-[#10B981]/30 border border-[#10B981] text-white'
                            : 'bg-white/10 border border-white/20 text-white/70'
                        }`}
                      >
                        {opt}s
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <input
                      type="range"
                      min={activeModel?.duration.min || 1}
                      max={activeModel?.duration.max || 15}
                      value={videoDuration}
                      onChange={(e) => setVideoDuration(parseInt(e.target.value))}
                      className="flex-1 h-1 bg-white/20 rounded-lg appearance-none cursor-pointer accent-[#10B981]"
                    />
                    <span className="text-white font-medium text-sm w-8">{videoDuration}s</span>
                  </div>
                )}
              </div>

              {/* Resolution Dropdown - dynamisch */}
              <div className="bg-white/5 rounded-xl p-3">
                <label className="text-white/60 text-xs mb-2 block">Auflösung</label>
                <select
                  value={videoResolution}
                  onChange={(e) => setVideoResolution(e.target.value)}
                  className="w-full bg-white/10 text-white rounded-lg px-2 py-1 text-sm border-0 outline-none"
                >
                  {activeModel?.resolutions.map((res) => (
                    <option key={res} value={res} className="bg-gray-800">
                      {res === '480p' ? '480p (schnell)' : res === '720p' ? '720p (HD)' : res === '1080p' ? '1080p (Full HD)' : res}
                    </option>
                  ))}
                </select>
              </div>
            </div>}

            {/* ═══ ASPECT RATIO (Bildformat) — nur bei Basic ═══ */}
            {selectedModel !== 'cinematic_video' && activeModel?.aspectRatios && activeModel.aspectRatios.length > 1 && (
              <div className="bg-white/5 rounded-xl p-3">
                <label className="text-white/60 text-xs mb-2 block">Format</label>
                <div className="flex gap-1 flex-wrap">
                  {activeModel.aspectRatios.map((ratio) => (
                    <button
                      key={ratio}
                      onClick={() => setVideoAspectRatio(ratio)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                        videoAspectRatio === ratio
                          ? 'bg-[#10B981]/30 border border-[#10B981] text-white'
                          : 'bg-white/10 border border-white/20 text-white/70'
                      }`}
                    >
                      {ratio === '9:16' ? '📱 9:16' : 
                       ratio === '16:9' ? '🖥️ 16:9' : 
                       ratio === '1:1' ? '⬜ 1:1' : 
                       ratio === '4:3' ? '📺 4:3' : 
                       ratio === '3:4' ? '📷 3:4' : ratio}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* ═══ END-FRAME UPLOAD (nur für PixVerse) ═══ */}
            {activeModel?.needsEndFrame && (
              <div className="bg-white/5 border border-white/10 rounded-xl p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-white/60 text-xs font-medium uppercase tracking-wider">
                    🔄 Transition-Modus
                  </span>
                </div>
                <p className="text-white/40 text-xs">
                  Start-Bild: ✓ (aktuelles Bild)
                </p>
                
                {/* End-Frame Upload */}
                {endFramePreview ? (
                  <div className="relative">
                    <img 
                      src={sanitizeUrlForImage(endFramePreview)} 
                      alt="End-Frame" 
                      className="w-full h-24 object-cover rounded-lg"
                    />
                    <button
                      onClick={() => {
                        setEndFrameUrl(null);
                        setEndFramePreview(null);
                      }}
                      className="absolute top-1 right-1 w-6 h-6 bg-black/70 rounded-full flex items-center justify-center"
                    >
                      <span className="text-white text-xs">×</span>
                    </button>
                    {isUploadingEndFrame && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-lg">
                        <Loader2 className="w-6 h-6 text-white animate-spin" />
                      </div>
                    )}
                  </div>
                ) : (
                  <label className="flex items-center justify-center gap-2 w-full h-20 border-2 border-dashed border-white/20 rounded-xl cursor-pointer hover:border-[#10B981]/50 transition-colors">
                    <Upload className="w-5 h-5 text-white/40" />
                    <span className="text-white/40 text-sm">End-Bild wählen</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleEndFrameUpload}
                      className="hidden"
                    />
                  </label>
                )}
              </div>
            )}

            {/* ═══ KAMERAFAHRT (nur für pixverse_camera) ═══ */}
            {activeModel?.needsCameraMovement && activeModel?.cameraMovements && (
              <div className="bg-white/5 border border-white/10 rounded-xl p-3 space-y-2">
                <span className="text-white/60 text-xs font-medium uppercase tracking-wider">
                  🎥 Kamerabewegung wählen
                </span>
                <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                  {activeModel.cameraMovements.map((cam) => (
                    <button
                      key={cam.id}
                      onClick={() => setSelectedCameraMovement(cam.id)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-all text-left ${
                        selectedCameraMovement === cam.id
                          ? 'bg-[#10B981]/30 border border-[#10B981] text-white'
                          : 'bg-white/10 border border-white/20 text-white/70 hover:bg-white/20'
                      }`}
                    >
                      {cam.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* ═══ SPRECHENDER AVATAR (nur für pixverse_lipsync) ═══ */}
            {activeModel?.needsText && activeModel?.needsVoice && activeModel?.voices && (
              <div className="bg-white/5 border border-white/10 rounded-xl p-3 space-y-3">
                <span className="text-white/60 text-xs font-medium uppercase tracking-wider">
                  🗣️ Avatar spricht
                </span>
                
                {/* VIDEO AUSWÄHLEN (NEU!) */}
                {activeModel?.needsVideoInput && (
                  <div>
                    <label className="text-white/40 text-xs block mb-2">1. Wähle ein Video</label>
                    {!videoInputUrl ? (
                      <div 
                        className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-hide"
                        style={{ 
                          scrollbarWidth: 'none', 
                          msOverflowStyle: 'none',
                          WebkitOverflowScrolling: 'touch'
                        }}
                      >
                        {availableVideos && availableVideos.length > 0 ? (
                          availableVideos.map((video) => (
                            <button
                              key={video.id}
                              onClick={() => setVideoInputUrl(video.video_url)}
                              className="relative flex-shrink-0 w-24 h-36 rounded-xl overflow-hidden border-2 border-white/20 hover:border-[#10B981] transition-all snap-start"
                            >
                              <img 
                                src={video.source_image_url || video.thumbnail_url} 
                                alt="" 
                                className="w-full h-full object-cover"
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent flex items-end justify-center pb-2">
                                <Play className="w-6 h-6 text-white drop-shadow-lg" />
                              </div>
                            </button>
                          ))
                        ) : (
                          <p className="text-white/40 text-xs text-center py-4 w-full">
                            Keine Videos vorhanden. Erstelle erst ein Video!
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-3">
                        <div className="relative w-20 h-32 rounded-xl overflow-hidden border-2 border-[#10B981] shadow-lg shadow-[#10B981]/20">
                          <img 
                            src={sanitizeUrlForImage(
                              availableVideos.find(v => v.video_url === videoInputUrl)?.source_image_url || videoInputUrl
                            )} 
                            className="w-full h-full object-cover" 
                            alt=""
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent flex items-end justify-center pb-2">
                            <Play className="w-5 h-5 text-white" />
                          </div>
                        </div>
                        <div className="flex-1">
                          <p className="text-[#10B981] text-sm font-medium">Video ausgewählt ✓</p>
                          <button 
                            onClick={() => setVideoInputUrl('')}
                            className="text-white/50 text-xs hover:text-white mt-1"
                          >
                            Anderes wählen
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Stimme wählen - Glasmorphism Pills mit Play/Pause */}
                <div>
                  <label className="text-white/40 text-xs block mb-2">{activeModel?.needsVideoInput ? '2. Stimme' : 'Stimme'}</label>
                  <div 
                    className="flex gap-2 overflow-x-auto pb-2 snap-x snap-mandatory"
                    style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
                  >
                    {activeModel.voices.filter(v => v.id !== 'Auto').map((voice) => (
                      <div
                        key={voice.id}
                        className={`flex-shrink-0 flex items-center gap-2 px-3 py-2 rounded-xl backdrop-blur border transition-all snap-start ${
                          selectedVoice === voice.id
                            ? 'bg-[#10B981]/20 border-[#10B981] text-white'
                            : 'bg-white/10 border-white/20 text-white/70 hover:bg-white/15'
                        }`}
                      >
                        {/* Play/Pause Button */}
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (playingVoice === voice.id) {
                              // Pause
                              if (voiceAudioRef.current) {
                                voiceAudioRef.current.pause();
                              }
                              setPlayingVoice(null);
                            } else {
                              // Stop andere und Play diese
                              if (voiceAudioRef.current) {
                                voiceAudioRef.current.pause();
                              }
                              setPlayingVoice(voice.id);
                              setSelectedVoice(voice.id);
                              
                              // TTS Preview laden und abspielen
                              const voiceName = voice.label.split(' ')[0]; // "Emily (weiblich)" -> "Emily"
                              const audioUrl = `/api/beatleap/voice-preview/${voiceName}`;
                              
                              if (!voiceAudioRef.current) {
                                voiceAudioRef.current = new Audio();
                              }
                              voiceAudioRef.current.src = audioUrl;
                              voiceAudioRef.current.onended = () => setPlayingVoice(null);
                              voiceAudioRef.current.onerror = () => setPlayingVoice(null);
                              try {
                                await voiceAudioRef.current.play();
                              } catch (err) {
                                console.error('Audio play error:', err);
                                setPlayingVoice(null);
                              }
                            }
                          }}
                          className={`w-7 h-7 rounded-full flex items-center justify-center transition-all ${
                            playingVoice === voice.id
                              ? 'bg-[#10B981] text-black'
                              : 'bg-white/20 text-white hover:bg-white/30'
                          }`}
                        >
                          {playingVoice === voice.id ? (
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                            </svg>
                          ) : (
                            <svg className="w-3 h-3 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M8 5v14l11-7z" />
                            </svg>
                          )}
                        </button>
                        {/* Voice Name - klickbar zum Auswählen */}
                        <button
                          onClick={() => setSelectedVoice(voice.id)}
                          className="text-sm font-medium whitespace-nowrap"
                        >
                          {voice.label.split(' ')[0]}
                        </button>
                      </div>
                    ))}
                  </div>
                  <p className="text-white/30 text-xs mt-1">
                    Ausgewählt: <span className="text-[#10B981]">{activeModel.voices.find(v => v.id === selectedVoice)?.label || 'Automatisch'}</span>
                  </p>
                </div>
                
                {/* Text eingeben */}
                <div>
                  <label className="text-white/40 text-xs block mb-1">{activeModel?.needsVideoInput ? '3. Was soll der Avatar sagen?' : 'Was soll der Avatar sagen?'}</label>
                  <textarea
                    value={lipsyncText}
                    onChange={(e) => setLipsyncText(e.target.value.slice(0, 200))}
                    placeholder="Gib hier deinen Text ein..."
                    rows={3}
                    maxLength={200}
                    className="w-full bg-white/10 text-white rounded-lg px-3 py-2 text-sm border border-white/20 outline-none resize-none placeholder-white/30"
                  />
                  <p className="text-white/30 text-xs mt-1">{lipsyncText.length}/200 Zeichen (max.)</p>
                </div>
                
                {/* Schnell-Aktionen für Lipsync */}
                <div>
                  <label className="text-white/40 text-xs block mb-2">{activeModel?.needsVideoInput ? '4. Bewegung (optional)' : 'Bewegung (optional)'}</label>
                  <div 
                    className="flex gap-2 overflow-x-auto pb-2"
                    style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
                  >
                    {motionTemplates.map((motion) => (
                      <button
                        key={motion.id}
                        onClick={() => toggleMotion(motion.id)}
                        className={`flex-shrink-0 px-3 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                          selectedMotions.includes(motion.id)
                            ? 'bg-[#10B981]/30 border border-[#10B981] text-white'
                            : 'bg-white/10 border border-white/20 text-white/60 hover:text-white'
                        }`}
                      >
                        {motion.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ═══ TIKTOK EFFEKTE (nur für pixverse_effects) ═══ */}
            {activeModel?.needsEffect && activeModel?.effects && (
              <div className="bg-white/5 border border-white/10 rounded-xl p-3 space-y-2">
                <span className="text-white/60 text-xs font-medium uppercase tracking-wider">
                  ✨ Effekt wählen
                </span>
                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                  {activeModel.effects.map((effect) => (
                    <button
                      key={effect.id}
                      onClick={() => setSelectedEffect(effect.id)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-all text-left ${
                        selectedEffect === effect.id
                          ? 'bg-[#10B981]/30 border border-[#10B981] text-white'
                          : 'bg-white/10 border border-white/20 text-white/70 hover:bg-white/20'
                      }`}
                    >
                      {effect.label}
                    </button>
                  ))}
                </div>
                <p className="text-white/30 text-xs">
                  Kategorie: {activeModel.effects.find(e => e.id === selectedEffect)?.category || '-'}
                </p>
              </div>
            )}

            {/* ═══ FACE-SWAP (nur für pixverse_swap) ═══ */}
            {activeModel?.needsVideoUrl && activeModel?.swapModes && (
              <div className="bg-white/5 border border-white/10 rounded-xl p-3 space-y-3">
                <span className="text-white/60 text-xs font-medium uppercase tracking-wider">
                  🎭 Face-Swap in Video
                </span>
                
                {/* Swap-Modus */}
                <div className="flex gap-2">
                  {activeModel.swapModes.map((mode) => (
                    <button
                      key={mode.id}
                      onClick={() => setSelectedSwapMode(mode.id)}
                      className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        selectedSwapMode === mode.id
                          ? 'bg-[#10B981]/30 border border-[#10B981] text-white'
                          : 'bg-white/10 border border-white/20 text-white/70 hover:bg-white/20'
                      }`}
                    >
                      {mode.label}
                    </button>
                  ))}
                </div>
                
                {/* Video-URL eingeben */}
                <div>
                  <label className="text-white/40 text-xs block mb-1">Video-URL (TikTok, YouTube, etc.)</label>
                  <input
                    type="url"
                    value={videoInputUrl}
                    onChange={(e) => setVideoInputUrl(e.target.value)}
                    placeholder="https://..."
                    className="w-full bg-white/10 text-white rounded-lg px-3 py-2 text-sm border border-white/20 outline-none placeholder-white/30"
                  />
                  <p className="text-white/30 text-xs mt-1">Dein Avatar-Gesicht wird in dieses Video eingefügt</p>
                </div>
              </div>
            )}

            {/* ═══ KI-ANALYSE BEREICH - NUR FÜR ADMINS ═══ */}
            {false && (
              <>
                {/* KI-Optimierer Status */}
                {isOptimizing && (
                  <div className="flex flex-col items-center justify-center gap-2 py-6">
                    <Loader2 className="w-8 h-8 animate-spin text-[#10B981]" />
                    <span className="text-white/70">KI analysiert Bild...</span>
                    <span className="text-white/40 text-xs">Bildanalyse + Bewegungs-Prompt</span>
                  </div>
                )}

                {/* BEIDE Felder anzeigen nach Optimierung */}
                {!isOptimizing && (
                  <>
                    {/* ═══ BILDANALYSE ═══ */}
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-white/60 text-xs font-medium uppercase tracking-wider">
                      Bildanalyse
                    </span>
                    <span className="text-white/40 text-xs">
                      {imageAnalysis ? `${imageAnalysis.split(' ').length} Wörter` : ''}
                    </span>
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-xl p-3 max-h-32 overflow-y-auto">
                    <p className="text-white/70 text-xs leading-relaxed">
                      {imageAnalysis || 'Drücke "KI-Prompt generieren" für Analyse'}
                    </p>
                  </div>
                </div>

                {/* Status Message */}
                    {statusMessage && (
                      <p className="text-[#10B981] text-xs text-center">{statusMessage}</p>
                    )}
                  </>
                )}
              </>
            )}
            {/* ═══ ENDE KI-ANALYSE BEREICH ═══ */}

            {/* Generate Buttons - Zwei Optionen (nur Basic, nicht Kino-Modus) */}
            {selectedModel !== 'cinematic_video' && <div className="space-y-2">
              {/* Option 1: KI-Prompt generieren - NUR FÜR ADMINS */}
              {false && (
                <button
                  onClick={runKiOptimizer}
                  disabled={isOptimizing}
                  className="w-full py-3 rounded-xl text-white/70 font-medium text-sm backdrop-blur-md transition-all bg-white/5 hover:bg-white/10 border border-white/10"
                >
                  {isOptimizing ? 'KI analysiert...' : '+ KI-Prompt generieren (optional)'}
                </button>
              )}
              
              {/* Option 2: Video erstellen - Validierung je nach Modell */}
              {(() => {
                // Prüfe ob ein Bild ausgewählt ist
                const hasImage = selectedSourceImage || image?.url;

                // Validierung basierend auf Modell-Typ
                const isStandardValid = selectedMotions.length > 0 || customPrompt;
                const isEffectValid = activeModel?.needsEffect && selectedEffect;
                const isCameraValid = activeModel?.needsCameraMovement && selectedCameraMovement;
                const isLipsyncValid = activeModel?.needsText && lipsyncText?.trim().length > 0;
                const isSwapValid = (activeModel?.needsVideoUrl || activeModel?.needsVideoInput) && videoInputUrl?.trim().length > 0;
                const isEndFrameValid = !activeModel?.needsEndFrame || endFrameUrl;

                // Button aktiv wenn Bild vorhanden UND mindestens eine Bedingung erfüllt
                const isValid = hasImage && (isStandardValid || isEffectValid || isCameraValid || isLipsyncValid || isSwapValid) && isEndFrameValid;

                // Button-Text basierend auf fehlender Eingabe
                let buttonText = 'Video erstellen';
                let missingTarget = null; // Wohin scrollen bei Klick
                if (isOptimizing) buttonText = 'KI arbeitet...';
                else if (!hasImage) { buttonText = 'Foto wählen'; missingTarget = 'photo-slider'; }
                else if (activeModel?.needsEndFrame && !endFrameUrl) buttonText = 'End-Bild wählen';
                else if (activeModel?.needsText && !lipsyncText?.trim()) buttonText = 'Text eingeben';
                else if (activeModel?.needsVideoUrl && !videoInputUrl?.trim()) buttonText = 'Video-URL eingeben';
                else if (!isStandardValid && !isEffectValid && !isCameraValid && !isLipsyncValid && !isSwapValid) { buttonText = 'Regieanweisung eingeben'; missingTarget = 'prompt-area'; }

                const canGenerate = isValid && !isOptimizing && !isGenerating;

                return (
                  <button
                    onClick={() => {
                      if (canGenerate) {
                        handleGenerate();
                      } else if (missingTarget === 'photo-slider' && photoSliderRef.current) {
                        photoSliderRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      } else if (missingTarget === 'prompt-area') {
                        const promptEl = document.getElementById('video-prompt-textarea');
                        if (promptEl) {
                          promptEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          promptEl.focus();
                        }
                      }
                    }}
                    className={`w-full py-4 rounded-xl text-white font-bold text-lg backdrop-blur transition-all ${
                      canGenerate
                        ? 'bg-[#10B981]/70 hover:bg-[#10B981]/80'
                        : 'bg-white/20'
                    }`}
                  >
                    {isGenerating ? (
                      <span className="flex items-center justify-center gap-3">
                        <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Video wird erstellt...
                      </span>
                    ) : buttonText}
                  </button>
                );
              })()}
            </div>}
          </div>
        )}

        {/* After Generation - Minimale Controls */}
        {generatedVideoUrl && (
          <div className="space-y-3">
            {/* Speed Controls + Sound Button */}
            <div className="flex items-center justify-center gap-2">
              {[0.5, 1, 1.25, 1.5, 2].map((rate) => (
                <button
                  key={rate}
                  onClick={() => {
                    setPlaybackRate(rate);
                    if (videoRef.current) videoRef.current.playbackRate = rate;
                  }}
                  className={`px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                    playbackRate === rate
                      ? 'bg-[#10B981] text-black'
                      : 'bg-white/10 text-white/70'
                  }`}
                >
                  {rate}x
                </button>
              ))}
              {/* Sound Toggle — rechtsbündig neben Speed-Chips */}
              <button
                onClick={() => {
                  setIsMuted(!isMuted);
                  if (videoRef.current) videoRef.current.muted = !isMuted;
                }}
                className={`w-10 h-10 rounded-xl flex items-center justify-center ml-auto ${
                  isMuted ? 'bg-white/10' : 'bg-[#10B981]'
                }`}
              >
                {isMuted ? (
                  <svg className="w-5 h-5 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                  </svg>
                )}
              </button>
            </div>
            
            {/* Timeline */}
            <div className="flex items-center gap-3 px-1">
              <span className="text-white/40 text-xs font-mono w-9 text-right">
                {formatTime(currentTime)}
              </span>
              <div 
                className="flex-1 h-1 bg-white/20 rounded-full cursor-pointer"
                onClick={(e) => {
                  if (videoRef.current && duration) {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const percent = (e.clientX - rect.left) / rect.width;
                    videoRef.current.currentTime = percent * duration;
                  }
                }}
              >
                <div 
                  className="h-full bg-white/60 rounded-full"
                  style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
                />
              </div>
              <span className="text-white/40 text-xs font-mono w-9">
                {formatTime(duration)}
              </span>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 justify-center">
              <button
                className="flex-1 h-12 rounded-xl bg-white/10 text-white font-medium flex items-center justify-center gap-2"
                onClick={async () => {
                  try {
                    const response = await fetch(generatedVideoUrl);
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `snapshotpro-video-${Date.now()}.mp4`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                  } catch (err) {
                    console.error('Download error:', err);
                  }
                }}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Speichern
              </button>
              <button
                className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center"
                onClick={async () => {
                  if (!generatedVideoUrl) return;
                  const ss = await getShareSettings();
                  const { shareVideo } = await import('../../utils/shareMedia');
                  await shareVideo({ videoUrl: generatedVideoUrl, text: ss.video_share_text });
                }}
              >
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-500/20 border border-red-500/50 rounded-xl p-4 text-red-400 relative">
            {/* Dismiss button */}
            <button
              onClick={() => setError(null)}
              className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full bg-red-500/20 hover:bg-red-500/30 text-red-400 transition-all"
              aria-label="Schließen"
            >
              ×
            </button>
            <p className="text-center pr-6">{error}</p>
            {/* Credit-specific hint: show buy-credits link */}
            {(error.toLowerCase().includes('snap') || error.toLowerCase().includes('credit') || error.toLowerCase().includes('genügend') || error.toLowerCase().includes('charge')) && (
              <div className="mt-3 text-center">
                <p className="text-red-300/70 text-xs mb-2">Lade dein Guthaben auf und versuche es erneut.</p>
                <a
                  href="/credits"
                  className="inline-block px-4 py-2 bg-[#10B981] text-black text-sm font-bold rounded-lg hover:bg-[#059669] transition-all"
                >
                  + SNAPs kaufen
                </a>
              </div>
            )}
          </div>
        )}
        </div>{/* Ende innerer responsive Container */}
      </div>{/* Ende äußerer Gradient Container */}
    </div>,
    document.body
  );
};

// ========== VIDEO STUDIO SECTION (GROK-Style) ==========
const VideoStudioSection = ({ generations, videos, pendingVideos, onSelectImage, setActionSheetImage, avatarId, favorites, toggleFavorite, selectedVideos, setSelectedVideos, onDeleteVideos, isDeletingVideos, onVideoDeleted, onVideoCompleted, readOnly = false }) => {
  const [fullscreenImage, setFullscreenImage] = useState(null);
  const [fullscreenVideo, setFullscreenVideo] = useState(null); // Existierendes Video in VideoGeneratorFullscreen
  const [selectedTemplate, setSelectedTemplate] = useState(null); // Gewählte Vorlage
  const [showImagePicker, setShowImagePicker] = useState(false); // Bildauswahl-Modal

  // Body Scroll Lock für Bildauswahl-Modal
  useEffect(() => {
    if (showImagePicker) {
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${scrollY}px`;

      return () => {
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.width = '';
        document.body.style.top = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [showImagePicker]);

  // Alle Bilder aus Generations extrahieren
  const allImages = (generations || []).flatMap(gen =>
    (gen.image_urls || [gen.image_url])
      .filter(url => url && url.trim())
      .map((url, idx) => ({
        url,
        id: `${gen.id}_${idx}`,
        genId: gen.id,
        prompt: gen.prompt_text || gen.prompt
      }))
  );

  return (
    <div className="space-y-6">
      {/* 1. DEINE BILDER - Horizontal Scroll — AUSGEBLENDET (Funktion jetzt über Studio) */}
      {false && (
      <div>
        <h3 className="text-white font-semibold mb-3">
          Wähle ein Bild zum Animieren
        </h3>
        {(allImages?.length || 0) === 0 ? (
          <div className="bg-white/5 rounded-xl p-6 text-center">
            <p className="text-white/40 text-sm">Noch keine Bilder vorhanden</p>
          </div>
        ) : (
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {(allImages || []).map((img) => (
              <div
                key={img.id}
                onClick={() => readOnly ? onSelectImage(img) : setFullscreenImage(img)}
                className={`flex-shrink-0 w-20 h-28 rounded-xl overflow-hidden border-2 border-transparent hover:border-[#10B981] transition-all ${readOnly ? 'cursor-default' : 'cursor-pointer'}`}
              >
                <img src={img.url} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" />
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      {/* 2. DEINE VIDEOS */}
      <div>
        <h3 className="text-white font-semibold mb-3">
          Deine Videos <span className="text-white/40 text-xs font-normal">({Object.keys(videos || {}).length + (pendingVideos?.length || 0)})</span>
        </h3>
        {Object.keys(videos || {}).length === 0 && (pendingVideos?.length || 0) === 0 ? (
          <div className="bg-white/5 rounded-xl p-8 text-center">
            <Video className="w-12 h-12 mx-auto mb-3 text-white/20" />
            <p className="text-white/40 text-sm">Wähle oben ein Bild + Bewegung</p>
          </div>
        ) : (
          <div className="flex gap-1">
            {(() => {
              const numCols = typeof window !== 'undefined' && window.innerWidth >= 1024 ? 3 : 2;
              const allItems = [
                ...(pendingVideos || []).map(pv => ({ type: 'pending', data: pv })),
                ...Object.values(videos || {}).map(v => ({ type: 'video', data: v.data || v }))
              ];
              const cols = Array.from({ length: numCols }, () => []);
              allItems.forEach((item, idx) => { cols[idx % numCols].push(item); });
              return cols.map((colItems, colIdx) => (
                <div key={colIdx} className="flex-1 flex flex-col gap-1">
                  {colItems.map((item) => {
                    if (item.type === 'pending') {
                      const pv = item.data;
                      return (
                        <PendingVideoCard key={pv.id} startTime={new Date(pv.created_at).getTime()} sourceImageUrl={pv.source_image_url} aspect={pv.video_aspect_ratio} />
                      );
                    }
                    // Video
                    const videoData = item.data;
                    const posterUrl = videoData.poster_thumbnail_url || (videoData.source_image_url ? (videoData.source_image_url.startsWith('/') || videoData.source_image_url.includes(window.location.host) ? videoData.source_image_url : `/image-proxy?url=${encodeURIComponent(videoData.source_image_url)}`) : '');
                    return (
                      <div key={videoData.id} className="mb-1">
                        <div className="relative rounded-xl overflow-hidden group">
                          {posterUrl && <img src={posterUrl} alt="" className="w-full h-full object-cover" />}
                          <div onClick={(e) => { e.stopPropagation(); setSelectedVideos(prev => { const s = new Set(prev); s.has(videoData.id) ? s.delete(videoData.id) : s.add(videoData.id); return s; }); }}
                            className={`absolute top-2 left-2 w-8 h-8 rounded-full flex items-center justify-center cursor-pointer z-20 transition-all ${selectedVideos?.has(videoData.id) ? 'bg-red-500 border-2 border-red-500' : 'bg-black/50 border-2 border-white/40'}`}>
                            {selectedVideos?.has(videoData.id) && <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                          </div>
                          <button onClick={() => {
                            if (selectedVideos?.size > 0) {
                              setSelectedVideos(prev => { const s = new Set(prev); s.has(videoData.id) ? s.delete(videoData.id) : s.add(videoData.id); return s; });
                            } else {
                              setFullscreenVideo({ id: videoData.id, video_url: videoData.video_url, video_prompt: videoData.user_prompt || videoData.video_prompt || videoData.text || videoData.prompt || '', user_prompt: videoData.user_prompt || videoData.video_prompt || videoData.text || videoData.prompt || '', image_analysis: videoData.image_analysis || '', source_image_url: posterUrl, original_speed: videoData.original_speed || 1, created_at: videoData.created_at, motion_tags: videoData.motion_tags || [], cost_snaps: videoData.cost_snaps, video_aspect_ratio: videoData.video_aspect_ratio, video_resolution: videoData.video_resolution });
                            }
                          }}
                            className="absolute inset-0 flex items-center justify-center">
                            <div className="w-12 h-12 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center transition-colors">
                              <Play className="w-6 h-6 text-white ml-1" fill="white" />
                            </div>
                          </button>
                          {favorites?.has(videoData.id) && <div className="absolute top-2.5 left-12"><Star className="w-5 h-5 text-yellow-400 fill-yellow-400 drop-shadow-lg" /></div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ));
            })()}
          </div>
        )}
      </div>

      {/* Fullscreen Video Generator */}
      {fullscreenImage && (
        <VideoGeneratorFullscreen
          image={fullscreenImage}
          onClose={() => setFullscreenImage(null)}
          avatarId={avatarId}
          preloadedPhotos={(generations || []).flatMap(gen =>
            (gen.image_urls || [gen.image_url]).filter(Boolean).map((url, idx) => ({
              id: `${gen.id}_${idx}`,
              url: url,
              prompt: gen.prompt,
              created_at: gen.created_at
            }))
          ).filter(p => !hiddenImageIds.has(p.id))}
          onVideoCompleted={onVideoCompleted}
          onVideoReady={(videoData) => {
            setFullscreenImage(null); // Generator schließen
            setFullscreenVideo(videoData); // VideoActionSheet öffnen
          }}
        />
      )}

      {/* Existierendes Video im neuen VideoActionSheet (Foto-Style!) */}
      {fullscreenVideo && (
        <VideoActionSheet
          isOpen={true}
          video={fullscreenVideo}
          videos={Object.values(videos)}
          onClose={(closedVideoId) => {
            try {
              const videoId = closedVideoId || fullscreenVideo?.id;
              setFullscreenVideo(null);
              if (videoId) {
                setTimeout(() => {
                  const el = document.getElementById(`video-card-${videoId}`);
                  if (el) el.scrollIntoView({ behavior: 'instant', block: 'center' });
                }, 100);
              }
            } catch (e) {
              console.error('onClose error:', e);
              setFullscreenVideo(null);
            }
          }}
          onToggleFavorite={toggleFavorite}
          favorites={favorites}
          onDelete={async (video) => {
            const token = getToken();
            await fetch(`/api/video/${video.id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
            removeFromCache('video', video.id);
            if (onVideoDeleted) onVideoDeleted(video.id);
          }}
        />
      )}

      {/* Multi-Select Lösch-Leiste für Videos */}
      {selectedVideos?.size > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-black/95 border-t border-white/10 p-4 z-50 safe-area-bottom">
          <div className="flex items-center justify-between max-w-lg mx-auto">
            <span className="text-white font-medium">{selectedVideos.size} ausgewählt</span>
            <div className="flex gap-3">
              <button
                onClick={() => setSelectedVideos(new Set())}
                className="px-4 py-2 rounded-xl bg-white/10 text-white font-medium"
              >
                Abbrechen
              </button>
              <button
                onClick={onDeleteVideos}
                disabled={isDeletingVideos}
                className="px-4 py-2 rounded-xl bg-red-500 text-white font-medium flex items-center gap-2 disabled:opacity-50"
              >
                {isDeletingVideos ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                {isDeletingVideos ? 'Lösche...' : 'Löschen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ========== TIKTOK-STYLE VIDEO PREVIEW ==========
// ZERO LATENCY: Sofort-Start beim Erscheinen, keine Texte, keine Verzögerung
const VideoPreview = ({ videoUrl, previewUrl, posterUrl, onClick }) => {
  const displayUrl = previewUrl || videoUrl;
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoReady, setVideoReady] = useState(false); // 🖼️ Zeige Poster bis Video bereit

  // SINGLE Intersection Observer - Preload + Play in einem
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          const visible = entry.isIntersecting;
          setIsVisible(visible);

          if (visible && videoRef.current) {
            // SOFORT abspielen - nicht auf ready warten!
            videoRef.current.play().catch(() => {});
            setIsPlaying(true);
          } else if (!visible && videoRef.current) {
            videoRef.current.pause();
            videoRef.current.currentTime = 0;
            setIsPlaying(false);
          }
        });
      },
      {
        threshold: 0.1, // 10% sichtbar = sofort starten
        rootMargin: '500px' // 500px früher laden für Zero-Latency
      }
    );

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Desktop Hover - zusätzlich zu Auto-Play
  const handleMouseEnter = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.play().catch(() => {});
      setIsPlaying(true);
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    // Auf Mobile nicht pausieren (Auto-Play bleibt)
    if (window.innerWidth >= 768 && videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  }, []);

  return (
    <div
      ref={containerRef}
      className="relative cursor-pointer rounded-xl overflow-hidden border-2 border-white/10 hover:border-[#10B981]/50 transition-all"
      onClick={onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{ transform: 'translateZ(0)', willChange: 'transform' }}
    >
      {/* 🖼️ Poster zuerst zeigen, Video versteckt laden */}
      {displayUrl && posterUrl && !videoReady && (
        <img
          src={posterUrl}
          alt=""
          className="w-full h-auto"
          loading="eager"
        />
      )}

      {/* Video - versteckt laden bis bereit, dann anzeigen */}
      {displayUrl && (
        <video
          ref={videoRef}
          src={displayUrl}
          poster={posterUrl}
          muted
          loop
          playsInline
          preload="auto"
          className={`w-full h-auto ${videoReady ? '' : 'hidden'}`}
          style={{ transform: 'translateZ(0)' }}
          onCanPlay={() => setVideoReady(true)}
        />
      )}

      {/* Fallback nur wenn KEIN Video */}
      {!displayUrl && posterUrl && (
        <img src={posterUrl} alt="" className="w-full h-auto" loading="eager" />
      )}

      {!displayUrl && !posterUrl && (
        <div className="w-full aspect-video bg-[#10B981]/10 flex items-center justify-center">
          <Video className="w-16 h-16 text-[#10B981]/30" />
        </div>
      )}

      {/* Dezenter Play-Button - nur wenn NICHT spielt */}
      <div className={`absolute bottom-3 right-3 w-10 h-10 bg-black/50 rounded-full flex items-center justify-center transition-opacity duration-200 ${isPlaying ? 'opacity-0' : 'opacity-70'}`}>
        <svg className="w-5 h-5 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M8 5v14l11-7z"/>
        </svg>
      </div>
    </div>
  );
};
import StarRating from '../shared/StarRating';
import ShareModal from '../modals/ShareModal';
import Lightbox from './Lightbox';
import ImageRemixFullscreen from './ImageRemixFullscreen';
import ModifyModal from '../modals/ModifyModal';
import VideoModal from './VideoModal';
import ImageActionSheet from './ImageActionSheet';  // Pinterest-Style Bottom Sheet
import { apiClient, getToken } from '../../utils/apiClient';
import { snapText } from '../../utils/snapFormatter';  // NEU: SNAP-Formatierung
// Video-Vorlagen entfernt - KI-Analyse ist besser!


// ═══════════════════════════════════════════════════════════════
// 🖼️ THUMBNAIL HELPER - Verwendet Thumbnails in Galerie-Übersicht
// ═══════════════════════════════════════════════════════════════
const getDisplayUrls = (gen) => {
  const originals = gen.image_urls || [gen.image_url];
  const thumbnails = gen.thumbnail_urls || [];
  
  // Galerie: Thumbnails wenn vorhanden, sonst Fallback auf Originale
  return originals.map((url, idx) => {
    if (thumbnails[idx] && thumbnails[idx].startsWith("/storage/")) {
      return thumbnails[idx];
    }
    return url;
  }).filter(Boolean);
};

const Gallery = ({
  generations, loading, onDelete, avatarId, onUsePrompt, onRemix, onModify, isGenerating, pendingImageJobs = [], numPendingImages = 1, readOnly = false,
  // 🎬 Video Queue Props (von IDCardDashboard)
  assemblyQueue = [], addToQueue = () => {}, removeFromQueue = () => {}, isInQueue = () => false,
  // 🎬 Tab Control (von IDCardDashboard) - für Scroll nach Generierung
  galleryTab = 'images', setGalleryTab = null,
  // 🎬 Studio von außen öffnen (Video erstellen Accordion)
  openStudio = false, onStudioClose = null,
  // 🎬 Video-Accordion öffnen (von Bild → Video Button)
  onOpenVideoAccordion = null,
  // 🎬 Video von außen im VideoActionSheet öffnen
  externalVideo = null, onExternalVideoClosed = null,
  // 🔄 Galerie neu laden bei Delete-Fehler (optimistic rollback)
  onRefresh = null,
  // 🎬 Videos aus dem Showcase (Skip-Seite, readOnly) — wird statt API-Fetch verwendet
  showcaseVideos = null,
  showcasePendingVideos = []
}) => {
  const navigate = useNavigate();
  
  // Mobil: Einzelansicht (1), Desktop: Zweier-Ansicht (2)
  const [gridView, setGridView] = useState(
    typeof window !== 'undefined' && window.innerWidth < 640 ? 1 : 2
  );
  const [shareImage, setShareImage] = useState(null);
  const [lightboxImages, setLightboxImages] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [recentDeletes, setRecentDeletes] = useState(new Set());
  const [expandedGeneration, setExpandedGeneration] = useState(null);
  const [remixImage, setRemixImage] = useState(null); // Remix state
  const [modifyImage, setModifyImage] = useState(null); // Modify state
  const [videoImage, setVideoImage] = useState(null); // Video state - NEU!
  const [actionSheetImage, setActionSheetImage] = useState(null); // Pinterest Action Sheet
  const [pinterestMode, setPinterestMode] = useState(true); // Pinterest Layout aktiviert
  const [masonryCols, setMasonryCols] = useState(typeof window !== 'undefined' && window.innerWidth >= 1024 ? 3 : 2);
  const [fullscreenIndex, setFullscreenIndex] = useState(null); // Fullscreen Viewer
  const [favorites, setFavorites] = useState(new Set());
  const [selectedForDelete, setSelectedForDelete] = useState(new Set()); // Multi-Select für Löschen (Bilder)
  const [selectedVideos, setSelectedVideos] = useState(new Set()); // Multi-Select für Löschen (Videos)
  const [isDeletingImages, setIsDeletingImages] = useState(false);
  const [isDeletingVideos, setIsDeletingVideos] = useState(false);
  const [hiddenImageIds, setHiddenImageIds] = useState(new Set()); // Sofort ausgeblendete Bilder
  const [hiddenVideoIds, setHiddenVideoIds] = useState(new Set()); // Sofort ausgeblendete Videos
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null); // { type: 'images'|'videos', count: N }
  // debugMode entfernt — Debug wird jetzt serverseitig über /api/system/status gesteuert

  // 🎬 Video von außen öffnen (Kino Modus completed)
  useEffect(() => {
    if (externalVideo) {
      setFullscreenVideo(externalVideo);
      setActiveTab('videos');
    }
  }, [externalVideo]);

  // Responsive Masonry Spaltenanzahl
  useEffect(() => {
    const handleResize = () => setMasonryCols(window.innerWidth >= 1024 ? 3 : 2);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 🎬 Studio von außen öffnen/schließen (Video erstellen Accordion)
  useEffect(() => {
    if (openStudio && !videoImage) {
      setVideoImage({ url: null, studioMode: true });
    } else if (!openStudio && videoImage?.studioMode) {
      setVideoImage(null);
    }
  }, [openStudio]);

  // Load favorites from API when component mounts
  useEffect(() => {
    const loadFavorites = async () => {
      try {
        const token = getToken();
        if (!token) return;
        const response = await fetch('/api/favorites/list', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          // Combine images and videos into one Set
          const allFavorites = [...(data.images || []), ...(data.videos || [])];
          setFavorites(new Set(allFavorites));
        }
      } catch (e) {
        console.error('Failed to load favorites:', e);
      }
    };
    loadFavorites();
  }, []);

  // 📌 URL-Sync für ActionSheet - DEAKTIVIERT (verursachte Re-Open Bug)
  // Die URL-Persistenz wird später mit besserer Logik wieder eingebaut
  const [videos, setVideos] = useState({}); // Map: "genId_imgIdx" -> video data

  // 🎬 ReadOnly (Skip-Seite): Videos aus showcaseVideos-Prop befüllen statt API-Call
  useEffect(() => {
    if (!readOnly) return;
    if (!Array.isArray(showcaseVideos)) return;
    const videoMap = {};
    showcaseVideos.forEach(v => {
      if (!v || !v.video_url) return;
      const key = v.is_animation
        ? `animation_${v.id}`
        : (v.video_model === 'kino_modus' || v.video_model === 'hollywood' || !v.source_generation_id)
          ? `video_${v.id}`
          : `${v.source_generation_id}_${v.image_index ?? 0}`;
      videoMap[key] = { ...v, status: v.status || 'completed' };
    });
    setVideos(videoMap);
  }, [readOnly, showcaseVideos]);

  // Pending Generations (Bilder) - vom Backend geholt
  const [pendingGeneration, setPendingGeneration] = useState(null);
  const [generationStartTime, setGenerationStartTime] = useState(null);

  // Reformat-Pending: lokale Platzhalter für laufende Format-Änderungen
  // (Outpaint oder Crop). ImageActionSheet feuert reformatStart/Done Events.
  const [reformatPendingJobs, setReformatPendingJobs] = useState([]);
  const [reformatErrorModal, setReformatErrorModal] = useState(null);
  useEffect(() => {
    const onStart = (e) => {
      const id = e?.detail?.id || `reformat_${Date.now()}`;
      setReformatPendingJobs(prev => [...prev, { id, startTime: Date.now() }]);
    };
    const onDone = (e) => {
      const id = e?.detail?.id;
      setReformatPendingJobs(prev => id ? prev.filter(j => j.id !== id) : []);
      if (onRefresh) onRefresh();
    };
    const onError = (e) => {
      const message = e?.detail?.message || 'Sorry, das hat leider nicht geklappt. Versuch es bitte noch einmal.';
      setReformatErrorModal({
        title: 'Formatänderung fehlgeschlagen',
        message,
      });
    };
    window.addEventListener('reformatStart', onStart);
    window.addEventListener('reformatDone', onDone);
    window.addEventListener('reformatError', onError);
    return () => {
      window.removeEventListener('reformatStart', onStart);
      window.removeEventListener('reformatDone', onDone);
      window.removeEventListener('reformatError', onError);
    };
  }, [onRefresh]);

  // Setze startTime wenn isGenerating sich ändert
  useEffect(() => {
    if (isGenerating && !generationStartTime) {
      setGenerationStartTime(Date.now());
    } else if (!isGenerating) {
      setGenerationStartTime(null);
    }
  }, [isGenerating, generationStartTime]);

  // Pending Videos - vom Backend geholt (nicht localStorage - unzuverlässig!)
  const [pendingVideos, setPendingVideos] = useState([]);
  const [serverPendingVideos, setServerPendingVideos] = useState([]); // server-seitig laufende Videos (z.B. Hollywood status='processing') als Spinner
  const [fullscreenVideo, setFullscreenVideo] = useState(null); // Video im VideoGeneratorFullscreen Modal
  const [generatorSourceImage, setGeneratorSourceImage] = useState(null); // 🎬 Bild für Rückkehr zum Generator
  const [expandedVideoId, setExpandedVideoId] = useState(null); // Akkordeon für Video-Karten
  // 🎬 Tab: Props haben Vorrang, sonst lokaler State
  const [localTab, setLocalTab] = useState('images');
  const activeTab = setGalleryTab ? galleryTab : localTab;
  const setActiveTab = setGalleryTab || setLocalTab;

  // Sprechender Avatar Videos
  const [lipsyncVideos, setLipsyncVideos] = useState([]);
  const fetchLipsyncVideos = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/lipsync/list');
      if (res.ok) {
        const data = await res.json();
        const items = Array.isArray(data) ? data : data.lipsync_videos || [];
        setLipsyncVideos(items.filter(v => v.status === 'completed' && v.video_url));
      }
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!readOnly) fetchLipsyncVideos();
  }, [readOnly, fetchLipsyncVideos]);

  // Nahtloser Übergang: Fertiges Video direkt einfügen, Pending entfernen
  useEffect(() => {
    const handler = (e) => {
      const video = e.detail;
      if (video && video.id && video.video_url) {
        // 1. Fertiges Video in die Map einfügen (kein Neuladen der gesamten Liste)
        setVideos(prev => {
          const key = video.video_model === 'kino_modus' || video.video_model === 'hollywood' || !video.source_generation_id
            ? `video_${video.id}`
            : `${video.source_generation_id}_${video.image_index || 0}`;
          return { ...prev, [key]: video };
        });
        // 2. Dieses Video aus Pending entfernen
        setPendingVideos(prev => prev.filter(p => p.id !== video.id && p.id !== 'instant_pending'));
      } else {
        // Fallback: Alles neu laden wenn keine Video-Daten im Event
        fetchLipsyncVideos();
      }
    };
    window.addEventListener('lipsyncVideoCompleted', handler);
    return () => window.removeEventListener('lipsyncVideoCompleted', handler);
  }, [fetchLipsyncVideos]);

  // 🎬 ASSEMBLY QUEUE - State kommt jetzt von IDCardDashboard als Props!
  // assemblyQueue, addToQueue, removeFromQueue, isInQueue sind Props

  // 🎬 ASSEMBLED VIDEOS (Meine Videos)
  const [assemblies, setAssemblies] = useState([]);
  const [loadingAssemblies, setLoadingAssemblies] = useState(true);
  const [playingAssembly, setPlayingAssembly] = useState(null);

  // Check if avatarId is valid (not a placeholder like "avatar_1")
  const isValidAvatarId = avatarId && !avatarId.startsWith('avatar_') && avatarId.length > 10;

  // Fetch pending generations from backend (survives page refresh!)
  useEffect(() => {
    const fetchPendingGeneration = async () => {
      if (!isValidAvatarId || readOnly) return;
      try {
        const response = await apiClient.get(`/api/generations/pending?avatar_id=${avatarId}`);
        if (response.ok) {
          const data = await response.json();
          if (data.pending && data.generation) {
            console.log('🖼️ Pending generation found:', data.generation.id);
            setPendingGeneration(data.generation);
          } else {
            setPendingGeneration(null);
          }
        }
      } catch (e) {
        // Netzwerk-Fehler beim Polling sind normal (z.B. kurzer Timeout) - nicht rot loggen
      }
    };

    fetchPendingGeneration();
    const interval = setInterval(fetchPendingGeneration, 2000); // 2s für schnelles Update
    return () => clearInterval(interval);
  }, [isValidAvatarId, (generations || []).length]);

  // Fetch pending videos from backend (survives page refresh!)
  useEffect(() => {
    if (readOnly) return; // Skip API calls in readOnly mode

    // Avatar gewechselt — alte pending Videos nur entfernen wenn wirklich ein anderer Avatar
    setPendingVideos(prev => prev.length > 0 ? [] : prev);

    const fetchPendingVideos = async () => {
      if (!isValidAvatarId || readOnly) return;
      // Nur pollen wenn Video-Tab aktiv ist (vermeidet Re-Renders im Foto-Tab)
      const currentTab = setGalleryTab ? galleryTab : localTab;
      if (currentTab !== 'videos') return;
      try {
        const response = await apiClient.get(`/api/videos/pending?avatar_id=${avatarId}`);
        if (response.ok) {
          const data = await response.json();
          if (data.pending && data.videos?.length > 0) {
            // Nur updaten wenn sich die IDs geändert haben (vermeidet Flicker)
            setPendingVideos(prev => {
              const prevIds = prev.map(p => p.id).sort().join(',');
              const newIds = data.videos.map(v => v.id).sort().join(',');
              if (prevIds === newIds) return prev; // Keine Änderung → kein Re-Render
              console.log('🎬 Pending videos update:', data.videos.length);
              return data.videos;
            });
          } else {
            // Keine echten Pending-Videos — instant_pending beibehalten falls vorhanden
            setPendingVideos(prev => {
              if (prev.length === 0) return prev; // Schon leer → kein Re-Render
              const hasInstant = prev.some(p => p.id === 'instant_pending');
              if (hasInstant) return prev;
              return [];
            });
          }
        }
      } catch (e) {
        // Netzwerk-Fehler beim Polling sind normal
      }
    };

    fetchPendingVideos();
    const interval = setInterval(fetchPendingVideos, 2000); // 2s für schnelles Update

    // Event-Listener: Sofort pending Videos holen wenn Generierung startet
    const handleFetchNow = () => fetchPendingVideos();
    window.addEventListener('fetchPendingVideos', handleFetchNow);

    // Event-Listener: SOFORT lokalen Pending-Eintrag anzeigen (kein Warten auf Backend)
    let instantPendingTimeout = null;
    const handleInstantPending = () => {
      setPendingVideos(prev => {
        if (prev.length > 0 && prev[0].id === 'instant_pending') return prev;
        return [{ id: 'instant_pending', status: 'processing', created_at: new Date().toISOString(), source_image_url: null }, ...prev];
      });
      // Nach 10s darf der Platzhalter entfernt werden (genug Zeit für DB-Insert)
      if (instantPendingTimeout) clearTimeout(instantPendingTimeout);
      instantPendingTimeout = setTimeout(() => {
        setPendingVideos(prev => prev.filter(p => p.id !== 'instant_pending'));
      }, 10000);
    };
    window.addEventListener('instantPendingVideo', handleInstantPending);

    return () => {
      clearInterval(interval);
      window.removeEventListener('fetchPendingVideos', handleFetchNow);
      window.removeEventListener('instantPendingVideo', handleInstantPending);
    };
  }, [isValidAvatarId, avatarId, readOnly, galleryTab, localTab, setGalleryTab]); // Re-trigger bei Avatar-Wechsel oder Tab-Wechsel

  // 🚀 SWR: Fetch videos with cache-first strategy
  useEffect(() => {
    if (readOnly) return;
    if (!isValidAvatarId) return;

    // 1️⃣ SOFORT aus Cache anzeigen (kein Flackern!)
    const cached = getCachedVideos(avatarId);
    if (cached && Object.keys(cached).length > 0) {
      setVideos(cached);
      console.log('🚀 Videos aus Cache:', Object.keys(cached).length);
    } else {
      // Avatar gewechselt oder kein Cache — alte Videos sofort entfernen!
      setVideos({});
    }

    // 🚀 Pre-fetch ID-Card Daten im Hintergrund
    const token = getToken();
    prefetchIdCard(avatarId, token);

    // 2️⃣ Im Hintergrund revalidieren (nicht im readOnly-Modus)
    const fetchVideos = async () => {
      if (readOnly) return;
      try {
        const response = await apiClient.get(`/api/videos/list?avatar_id=${avatarId}&limit=500`);
        if (response.ok) {
          const data = await response.json();
          const videoMap = {};
          const pendingList = [];
          (data.videos || []).forEach(v => {
            if (v.status === 'completed' && v.video_url) {
              // Eindeutiger Key: Animation, Kino-Modus (eigene ID), oder Standard (generation_id + index)
              const key = v.is_animation ? `animation_${v.id}`
                : (v.video_model === 'kino_modus' || v.video_model === 'hollywood' || !v.source_generation_id) ? `video_${v.id}`
                : `${v.source_generation_id}_${v.image_index}`;
              videoMap[key] = v;
            } else if (v.status && v.status !== 'failed') {
              // In Generierung (z.B. Hollywood 'processing') → Spinner-Platzhalter bis fertig
              pendingList.push({ id: v.id, created_at: v.created_at, source_image_url: v.source_image_url || '', video_model: v.video_model, video_aspect_ratio: v.video_aspect_ratio });
            }
          });
          setVideos(videoMap);
          setServerPendingVideos(pendingList);
          setCachedVideos(avatarId, videoMap); // Cache aktualisieren
          console.log('🎬 Videos geladen:', Object.keys(videoMap).length, '| pending:', pendingList.length);
        }
      } catch (err) {
        console.error('Failed to load videos:', err);
      }
    };

    // IMMER revalidieren (SWR): Cache dient nur dem sofortigen Anzeigen. Sonst bleibt
    // eine leere/veraltete Video-Liste hängen — z.B. wenn der Cache {} war, bevor ein
    // (Hollywood-)Video fertig wurde → fertige Videos tauchten nie auf.
    fetchVideos();

    // Polling nur wenn Video-Tab aktiv UND pending videos existieren
    const currentTab = setGalleryTab ? galleryTab : localTab;
    if (currentTab !== 'videos') return; // Kein Video-Polling im Foto-Tab
    const intervalTime = (pendingVideos.length + serverPendingVideos.length) > 0 ? 2000 : 60000; // 2s wenn pending, 60s sonst
    const interval = setInterval(fetchVideos, intervalTime);
    return () => clearInterval(interval);
  }, [isValidAvatarId, avatarId, pendingVideos.length, serverPendingVideos.length, readOnly, galleryTab, localTab, setGalleryTab]);

  // 🎬 Listen auf refreshVideoGallery Event (von IDCardDashboard nach Video-Completion)
  useEffect(() => {
    const handleRefresh = () => {
      if (readOnly) return;
      const fetchNow = async () => {
        try {
          const response = await apiClient.get(`/api/videos/list?avatar_id=${avatarId}&limit=500`);
          if (response.ok) {
            const data = await response.json();
            const videoMap = {};
            const pendingList = [];
            (data.videos || []).forEach(v => {
              if (v.status === 'completed' && v.video_url) {
                const key = v.is_animation ? `animation_${v.id}` : (v.video_model === 'kino_modus' || v.video_model === 'hollywood' || !v.source_generation_id) ? `video_${v.id}` : `${v.source_generation_id}_${v.image_index}`;
                videoMap[key] = v;
              } else if (v.status && v.status !== 'failed') {
                pendingList.push({ id: v.id, created_at: v.created_at, source_image_url: v.source_image_url || '', video_model: v.video_model, video_aspect_ratio: v.video_aspect_ratio });
              }
            });
            setVideos(videoMap);
            setServerPendingVideos(pendingList);
            setCachedVideos(avatarId, videoMap);
          }
        } catch (e) {}
      };
      fetchNow();
    };
    window.addEventListener('refreshVideoGallery', handleRefresh);
    return () => window.removeEventListener('refreshVideoGallery', handleRefresh);
  }, [avatarId, readOnly]);

  // 🎬 Videos-Tab aktivieren + zur Video-Galerie scrollen (wird aus handleGenerate gefeuert)
  useEffect(() => {
    const handleScrollToVideos = () => {
      if (typeof setActiveTab === 'function') setActiveTab('videos');
      requestAnimationFrame(() => {
        setTimeout(() => {
          const target =
            document.getElementById('video-gallery-section') ||
            document.querySelector('[data-testid="gallery-section"]');
          if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 150);
      });
    };
    window.addEventListener('galleryScrollToVideos', handleScrollToVideos);
    return () => window.removeEventListener('galleryScrollToVideos', handleScrollToVideos);
  }, [setActiveTab]);

  // 🎬 FETCH ASSEMBLED VIDEOS (User-Level, nicht Avatar-Level!)
  const fetchAssemblies = async () => {
    try {
      const response = await apiClient.get('/api/assemblies/list');
      if (response.ok) {
        const data = await response.json();
        setAssemblies(data.assemblies || []);
        console.log('🎬 Assemblies loaded:', data.assemblies?.length);
      }
    } catch (err) {
      console.error('Failed to load assemblies:', err);
    } finally {
      setLoadingAssemblies(false);
    }
  };

  useEffect(() => {
    if (readOnly) return; // Skip API calls in readOnly mode
    fetchAssemblies();
  }, [readOnly]);

  // URL-Wrapper: Externe Bild-URLs durch SNAPSHOT-Proxy ersetzen
  // Kein Proxy — alle Medien liegen lokal
  const getProxyUrl = (externalUrl) => externalUrl || '';

  // Scroll Detection
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Load ratings from localStorage
  const getRating = (imageId) => {
    const ratings = JSON.parse(localStorage.getItem('snapshot_ratings') || '{}');
    return ratings[imageId] || 0;
  };

  const handleRatingChange = () => {
    setRefreshKey(prev => prev + 1);
  };

  // 🎬 ASSEMBLY QUEUE FUNCTIONS - jetzt als Props von IDCardDashboard
  // addToQueue, removeFromQueue, isInQueue sind Props

  // 🎬 DELETE ASSEMBLED VIDEO
  const deleteAssembly = async (assemblyId) => {
    if (!window.confirm('Video wirklich löschen?')) return;

    try {
      const response = await apiClient.delete(`/api/assembly/${assemblyId}`);
      if (response.ok) {
        setAssemblies(prev => prev.filter(a => a.id !== assemblyId));
        console.log('🗑️ Assembly deleted:', assemblyId);
      }
    } catch (err) {
      console.error('Failed to delete assembly:', err);
    }
  };

  const openLightbox = (images, index) => {
    setLightboxImages(images);
    setLightboxIndex(index);
  };

  const closeLightbox = (finalIndex) => {
    // Finde heraus, zu welcher Generation das Bild gehört
    if (finalIndex !== undefined && lightboxImages && lightboxImages[finalIndex]) {
      const currentImage = lightboxImages[finalIndex];
      // Extract generation ID from image ID (format: "gen_id_imageIndex")
      const genId = currentImage.id.split('_')[0];

      console.log('🔍 Lightbox closed at image:', currentImage.id, 'Generation:', genId);

      // Scroll zur Generation-Card
      setTimeout(() => {
        const genCard = document.querySelector(`[data-generation-id="${genId}"]`);
        if (genCard) {
          genCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
          console.log('✅ Scrolled to generation card:', genId);
        }
      }, 100);
    }

    setLightboxImages(null);
  };

  // Delete image direkt (für ActionSheet - ohne confirm/spinner/toast)
  const deleteImageDirect = async (imageId, generationId) => {
    console.log('🗑️ Direct deleting image:', imageId);

    const user = JSON.parse(localStorage.getItem('snapshot_user') || '{}');
    const userId = user.id;

    if (!userId) {
      throw new Error('Benutzer nicht angemeldet');
    }

    let deleteUrl = `/api/generations/${imageId}`;
    if (avatarId) {
      deleteUrl += `?avatar_id=${avatarId}`;
    }

    // Optimistisch: sofort aus UI entfernen (vor API-Call)
    // hiddenImageIds versteckt die Kachel in der Galerie OHNE generations prop zu ändern → kein Flackern
    setHiddenImageIds(prev => { const n = new Set(prev); n.add(imageId); return n; });
    if (onDelete) {
      onDelete(imageId);
    }

    const response = await apiClient.delete(deleteUrl);

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = 'Delete failed';
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.detail || errorMessage;
      } catch (e) {
        errorMessage = errorText || errorMessage;
      }
      throw new Error(errorMessage);
    }

    // Cache chirurgisch aktualisieren (kein voller Cache-Reset)
    const parts = imageId.split('_');
    const imgIdx = parseInt(parts.pop());
    const genId = parts.join('_');
    removeImageFromCache(genId, imgIdx);

    // Hintergrund-Revalidierung (ohne Delay, Grid ist bereits korrekt durch hiddenImageIds-Filter)
    if (onRefresh) {
      onRefresh();
    }

    return true;
  };

  // Toggle Favorite - mit API Speicherung
  const toggleFavorite = useCallback(async (itemId, itemType = 'image') => {
    // Optimistic update
    setFavorites(prev => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(itemId)) {
        newFavorites.delete(itemId);
      } else {
        newFavorites.add(itemId);
      }
      return newFavorites;
    });

    // API call
    try {
      const token = getToken();
      await fetch('/api/favorites/toggle', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ item_id: itemId, item_type: itemType })
      });
    } catch (e) {
      console.error('Failed to toggle favorite:', e);
      // Revert on error
      setFavorites(prev => {
        const newFavorites = new Set(prev);
        if (newFavorites.has(itemId)) {
          newFavorites.delete(itemId);
        } else {
          newFavorites.add(itemId);
        }
        return newFavorites;
      });
    }
  }, []);

  // Multi-Select für Löschen (mit Debounce gegen doppelte Touch/Click Events)
  const lastToggleRef = useRef({});
  const toggleSelectForDelete = useCallback((imageId, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }

    // Debounce: Ignoriere wenn < 300ms seit letztem Toggle für diese ID
    const now = Date.now();
    if (lastToggleRef.current[imageId] && now - lastToggleRef.current[imageId] < 300) {
      return;
    }
    lastToggleRef.current[imageId] = now;

    setSelectedForDelete(prev => {
      const newSet = new Set(prev);
      if (newSet.has(imageId)) {
        newSet.delete(imageId);
      } else {
        newSet.add(imageId);
      }
      return newSet;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedForDelete(new Set());
  }, []);

  const confirmDeleteImages = useCallback(() => {
    if (selectedForDelete.size === 0) return;
    setShowDeleteConfirm({ type: 'images', count: selectedForDelete.size });
  }, [selectedForDelete]);

  const executeDeleteImages = useCallback(async () => {
    setShowDeleteConfirm(null);
    if (selectedForDelete.size === 0) return;

    const idsToDelete = Array.from(selectedForDelete);
    const token = getToken();

    // 1. SOFORT: Bilder ausblenden + Auswahl leeren
    setHiddenImageIds(prev => new Set([...prev, ...idsToDelete]));
    setSelectedForDelete(new Set());

    // Video-Slider: IDCardDashboard informieren → pendingDeleteIds aktualisieren
    if (onDelete) {
      onDelete(idsToDelete);
    }

    // 2. HINTERGRUND: API-Calls (User merkt nichts davon)
    const grouped = {};
    for (const imageId of idsToDelete) {
      const parts = imageId.split('_');
      const imgIdx = parseInt(parts.pop());
      const genId = parts.join('_');
      if (!grouped[genId]) grouped[genId] = [];
      grouped[genId].push(imgIdx);
    }

    const genPromises = Object.entries(grouped).map(async ([genId, indexes]) => {
      indexes.sort((a, b) => b - a);
      for (const imgIdx of indexes) {
        try {
          const params = new URLSearchParams();
          if (avatarId) params.append('avatar_id', avatarId);
          params.append('image_index', imgIdx);
          await fetch(`/api/generations/${genId}?${params}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
        } catch (err) {
          console.error(`❌ Delete error: ${genId}_${imgIdx}`, err);
        }
      }
    });

    await Promise.allSettled(genPromises);

    // Cache chirurgisch aktualisieren
    for (const imageId of idsToDelete) {
      const parts = imageId.split('_');
      const imgIdx = parseInt(parts.pop());
      const genId = parts.join('_');
      removeImageFromCache(genId, imgIdx);
    }

    // Hintergrund-Revalidierung
    if (onRefresh) {
      onRefresh();
    }
  }, [selectedForDelete, avatarId, onRefresh]);

  // Delete selected videos - Parallel mit Promise.allSettled
  const confirmDeleteVideos = useCallback(() => {
    if (selectedVideos.size === 0) return;
    setShowDeleteConfirm({ type: 'videos', count: selectedVideos.size });
  }, [selectedVideos]);

  const executeDeleteVideos = useCallback(async () => {
    setShowDeleteConfirm(null);
    if (selectedVideos.size === 0) return;

    const token = getToken();
    const idsToDelete = Array.from(selectedVideos);

    // 1. SOFORT: Videos ausblenden + Auswahl leeren
    setHiddenVideoIds(prev => new Set([...prev, ...idsToDelete]));
    setSelectedVideos(new Set());

    // 2. HINTERGRUND: API-Calls
    const deletePromises = idsToDelete.map(async (videoId) => {
      const res = await fetch(`/api/video/${videoId}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
      if (!res.ok) throw new Error(`Delete failed: ${videoId}`);
      return videoId;
    });

    await Promise.allSettled(deletePromises);

    // Cache chirurgisch aktualisieren
    for (const videoId of idsToDelete) {
      removeFromCache('video', videoId);
    }
  }, [selectedVideos]);

  // Delete image (mit confirm für alte Galerie-Buttons)
  const deleteImage = async (imageId, event, generationId, totalImagesInGen) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (deleting === imageId) {
      console.log('⚠️ Delete already in progress for:', imageId);
      return;
    }

    if (recentDeletes.has(imageId)) {
      console.log('⚠️ Image recently deleted, ignoring duplicate call:', imageId);
      return;
    }

    console.log('🗑️ Deleting image:', imageId);

    const user = JSON.parse(localStorage.getItem('snapshot_user') || '{}');
    const userId = user.id;

    if (!userId) {
      console.error('❌ No user ID found');
      alert('Fehler: Benutzer nicht angemeldet');
      return;
    }

    // Kurzer, eleganter Lösch-Dialog
    if (!window.confirm('Bild unwiderruflich löschen?')) {
      console.log('❌ User cancelled deletion');
      return;
    }

    setDeleting(imageId);
    setRecentDeletes(prev => new Set([...prev, imageId]));

    // Spinner zeigen (wie beim Download)
    const overlay = showLoadingOverlay("Wird gelöscht...");

    // Sofort optimistic aus UI entfernen (vor API-Call)
    setHiddenImageIds(prev => { const n = new Set(prev); n.add(imageId); return n; });
    if (onDelete) {
      console.log('🔄 Optimistic UI update: removing image immediately...');
      onDelete(imageId);
    }

    try {
      let deleteUrl = `/api/generations/${imageId}`;
      if (avatarId) {
        deleteUrl += `?avatar_id=${avatarId}`;
        console.log(`🔒 Avatar-ID attached for security: ${avatarId}`);
      }

      console.log(`🌐 DELETE ${deleteUrl}`);

      const response = await apiClient.delete(deleteUrl);

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = 'Delete failed';
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.detail || errorMessage;
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log('✅ Image deleted successfully:', result);

      // Cache chirurgisch aktualisieren
      const parts = imageId.split('_');
      const imgIdx = parseInt(parts.pop());
      const genId = parts.join('_');
      removeImageFromCache(genId, imgIdx);

      // Spinner entfernen
      hideLoadingOverlay();

      // Success Toast
      showSafeToast("Bild gelöscht!");

      // Hintergrund-Revalidierung
      if (onRefresh) {
        onRefresh();
      }

      setTimeout(() => {
        setRecentDeletes(prev => {
          const newSet = new Set(prev);
          newSet.delete(imageId);
          return newSet;
        });
      }, 3000);

    } catch (error) {
      console.error('❌ Error deleting image:', error);
      hideLoadingOverlay();
      alert(`Fehler beim Löschen: ${error.message}`);

      // Bei Fehler Galerie neu laden (optimistic update rückgängig)
      if (onRefresh) onRefresh();

      setRecentDeletes(prev => {
        const newSet = new Set(prev);
        newSet.delete(imageId);
        return newSet;
      });
    } finally {
      setDeleting(null);
    }
  };

  // Delete ALL images in a generation (whole card)
  const deleteAllImagesInGeneration = async (generation) => {
    const images = generation.image_urls || [generation.image_url];
    const validImages = images.filter(url => url && url.trim());

    if (validImages.length === 0) {
      console.log('⚠️ No images to delete');
      return;
    }

    const confirmed = window.confirm(
      `🗑️ Komplette Generation löschen?\n\n` +
      `${validImages.length} Bild${validImages.length > 1 ? 'er' : ''} und der Prompt werden unwiderruflich gelöscht.\n\n` +
      `Wirklich fortfahren?`
    );

    if (!confirmed) {
      console.log('❌ User cancelled bulk deletion');
      return;
    }

    console.log(`🗑️ Deleting all ${validImages.length} images in generation ${generation.id}`);
    setDeleting(`gen_${generation.id}`);

    // Sofort optimistic aus UI entfernen (vor API-Loop)
    if (onDelete) {
      console.log('🔄 Optimistic UI update: removing generation immediately...');
      onDelete(`gen_${generation.id}`);
    }

    try {
      // Delete all images from LAST to FIRST (wichtig! Index ändert sich nach jedem Löschen)
      for (let i = validImages.length - 1; i >= 0; i--) {
        const imageId = `${generation.id}_${i}`;
        let deleteUrl = `/api/generations/${imageId}`;
        if (avatarId) {
          deleteUrl += `?avatar_id=${avatarId}`;
        }

        console.log(`🌐 DELETE ${validImages.length - i}/${validImages.length}: ${deleteUrl}`);
        const response = await apiClient.delete(deleteUrl);

        if (!response.ok) {
          console.error(`❌ Failed to delete image index ${i}`);
          // Continue anyway to delete as many as possible
        }

        // Kleine Pause zwischen Löschungen
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      console.log('✅ All images deleted');

    } catch (error) {
      console.error('❌ Error in bulk delete:', error);
      alert(`Fehler beim Löschen: ${error.message}`);
      // Bei Fehler Galerie neu laden (optimistic update rückgängig)
      if (onRefresh) onRefresh();
    } finally {
      setDeleting(null);
    }
  };

  // Delete a video
  const deleteVideo = async (videoId) => {
    if (deleting === `video_${videoId}`) {
      console.log('⚠️ Delete already in progress for video:', videoId);
      return;
    }

    const confirmed = window.confirm(
      '🎬 Video wirklich löschen?\n\nDiese Aktion kann nicht rückgängig gemacht werden.'
    );

    if (!confirmed) {
      console.log('❌ User cancelled video deletion');
      return;
    }

    console.log('🗑️ Deleting video:', videoId);
    setDeleting(`video_${videoId}`);

    try {
      let deleteUrl = `/api/video/${videoId}`;
      if (avatarId) {
        deleteUrl += `?avatar_id=${avatarId}`;
      }

      console.log(`🌐 DELETE ${deleteUrl}`);
      const response = await apiClient.delete(deleteUrl);

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = 'Delete failed';
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.detail || errorMessage;
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      console.log('✅ Video deleted successfully');

      // Entferne Video aus Cache
      removeFromCache('video', videoId);

      // Entferne Video aus lokalem State
      setVideos(prev => {
        const newVideos = { ...prev };
        // Finde und lösche den Eintrag
        Object.keys(newVideos).forEach(key => {
          if (newVideos[key].id === videoId) {
            delete newVideos[key];
          }
        });
        return newVideos;
      });

      // Toast
      const toast = document.createElement('div');
      toast.textContent = '✅ Video gelöscht';
      toast.className = 'fixed top-20 left-1/2 transform -translate-x-1/2 bg-red-500 text-white px-6 py-3 rounded-xl font-bold shadow-xl z-50';
      document.body.appendChild(toast);
      setTimeout(() => toast.remove(), 2000);

    } catch (error) {
      console.error('❌ Error deleting video:', error);
      alert(`Fehler beim Löschen: ${error.message}`);
    } finally {
      setDeleting(null);
    }
  };

  const scrollToTop = () => {
    const gallerySection = document.querySelector('[data-testid="gallery-section"]');
    if (gallerySection) {
      gallerySection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Label Mapping (same as PromptHistoryPanel)
  const getLabelForValue = (category, value) => {
    const labels = {
      time_of_day: {
        'sunrise': 'Sonnenaufgang',
        'morning': 'Morgen',
        'noon': 'Mittag',
        'afternoon': 'Nachmittag',
        'sunset': 'Sonnenuntergang',
        'golden_hour': 'Goldene Stunde',
        'blue_hour': 'Blaue Stunde',
        'evening': 'Abend',
        'night': 'Nacht',
        'midnight': 'Mitternacht'
      },
      weather: {
        'clear': 'Klar',
        'cloudy': 'Bewölkt',
        'overcast': 'Bedeckt',
        'foggy': 'Nebelig',
        'rainy': 'Regnerisch',
        'stormy': 'Stürmisch',
        'snowy': 'Schneefall'
      },
      camera: {
        '24mm': '24mm',
        '35mm': '35mm',
        '50mm': '50mm',
        '85mm': '85mm',
        'drone': 'Drohne',
        'top': 'Von oben',
        'low': 'Von unten'
      },
      style: {
        'cinematic': 'Cinematic',
        'action': 'Action',
        'editorial': 'Editorial',
        'vintage': 'Vintage',
        'documentary': 'Doku',
        'fashion': 'Fashion',
        'artistic': 'Künstlerisch'
      },
      substyle: {
        'standard': 'Standard',
        'epic': 'Episch',
        'moody': 'Moody',
        'bright': 'Hell',
        'dramatic': 'Dramatisch'
      },
      image_format: {
        'square_1_1': 'Quadrat 1:1',
        'portrait_9_16': 'Portrait 9:16',
        'portrait_4_5': 'Portrait 4:5',
        'landscape_16_9': 'Landscape 16:9',
        'landscape_3_2': 'Landscape 3:2'
      }
    };

    return labels[category]?.[value] || value;
  };

  if (loading) {
    return (
      <div className="bg-black border-2 border-white/10 rounded-2xl p-8 text-center">
        <Loader2 className="w-12 h-12 mx-auto mb-4 text-[#10B981] animate-spin" />
        <p className="text-white/70">Lade Galerie...</p>
      </div>
    );
  }

  const galleryUser = JSON.parse(localStorage.getItem('snapshot_user') || '{}');
  const isMasterAdmin = galleryUser.email === 'info@snapshotpro.ai';

  const gridClass = pinterestMode
    ? '' // Pinterest mode verwendet columns statt grid
    : {
        1: 'grid-cols-1',
        2: 'grid-cols-2',
        4: 'grid-cols-2 sm:grid-cols-4'
      }[gridView];

  // ========== KOMBINIERTE CHRONOLOGISCHE LISTE ==========
  // Alle Assets (Bilder + Videos + Pending) zusammenführen und nach Erstellungszeit sortieren
  const allAssets = [
    // Pending Generations — ein Spinner pro laufendem Job
    ...(pendingImageJobs || []).map(job => ({
      type: 'pending_generation',
      data: { id: job.id },
      created_at: new Date(job.startTime),
      id: job.id,
      startTime: job.startTime,
      sourceImageUrl: null
    })),
    // Reformat-Pending (Format ändern aus Fullscreen)
    ...reformatPendingJobs.map(job => ({
      type: 'pending_generation',
      data: { id: job.id },
      created_at: new Date(job.startTime),
      id: job.id,
      startTime: job.startTime,
      sourceImageUrl: null
    })),
    // Fallback: Legacy pendingGeneration vom Backend Polling
    ...(pendingGeneration && !(pendingImageJobs || []).length ? [{
      type: 'pending_generation',
      data: pendingGeneration,
      created_at: new Date(pendingGeneration.created_at || Date.now()),
      id: pendingGeneration.id || 'pending',
      startTime: generationStartTime || Date.now(),
      sourceImageUrl: pendingGeneration.source_image_url || null
    }] : []),
    // Bild-Generations - Jedes Bild einzeln mit imageIndex (versteckte ausfiltern)
    ...(generations || []).flatMap(gen => {
      const images = gen.image_urls || [gen.image_url];
      return images.filter(url => url && url.trim()).map((url, idx) => ({
        type: 'image',
        data: gen,
        created_at: new Date(gen.created_at),
        id: `${gen.id}_${idx}`,
        imageIndex: idx,
        imageUrl: url
      })).filter(item => !hiddenImageIds.has(item.id));
    }),
    // Fertige Videos (versteckte ausfiltern)
    ...(Object.values(videos || {})).filter(vid => !hiddenVideoIds.has(vid.id)).map(vid => ({
      type: 'video',
      data: vid,
      created_at: new Date(vid.created_at),
      id: vid.id
    })),
    // Pending Videos (in Generierung) - zeigen Spinner (client-seitig + server-seitig, z.B. Hollywood)
    ...(() => {
      const seen = new Set();
      const merged = [];
      for (const pv of [...(pendingVideos || []), ...(serverPendingVideos || []), ...(showcasePendingVideos || [])]) {
        if (!pv || !pv.id || seen.has(pv.id)) continue;
        seen.add(pv.id);
        merged.push({ type: 'pending_video', data: pv, created_at: new Date(pv.created_at), id: pv.id });
      }
      return merged;
    })()
  ].sort((a, b) => b.created_at - a.created_at); // Neueste zuerst

  // Model-Label Funktion (für Videos)
  const getVideoModelLabel = (model) => {
    const labels = {
      'kling25': 'Kling 2.5', 'kling26_pro': 'Kling 2.6 Pro',
      'kling30_standard': 'Kling 3.0', 'kling30_pro': 'Kling 3.0 Pro',
      'seedance_720p': 'Seedance 720p', 'seedance_1080p': 'Seedance 1080p',
      'seedance_480p': 'Seedance 480p', 'seedance_pro_fast_720p': 'Seedance Fast',
      'hailuo02_720p': 'Hailuo', // 'veo31_fast_720p': 'Veo 3.1',
      'ltx2_fast_1080p': 'LTX 1080p', 'ltx2_fast_2160p': 'LTX 4K',
      'runway_gen3': '🪄 Runway',
      'kling30_pro': '🎬 Kling 3.0',
    };
    return labels[model] || model;
  };

  // ⭐ Count favorites that exist in current avatar's assets (not all user favorites)
  const favoritesCount = allAssets.filter(asset => {
    if (hiddenImageIds.has(asset.id)) return false;
    if (asset.type === 'video') return favorites.has(asset.data?.id);
    if (asset.type === 'image') return favorites.has(`${asset.data?.id}_${asset.imageIndex}`);
    return false;
  }).length;

  // 🎬 Filter assets by active tab
  const filteredAssets = activeTab === 'avatar' ? [] : allAssets.filter(asset => {
    if (activeTab === 'images') {
      return asset.type === 'image' || asset.type === 'pending_generation';
    } else if (activeTab === 'favorites') {
      if (asset.type === 'video') {
        return favorites.has(asset.data?.id);
      } else if (asset.type === 'image') {
        return favorites.has(`${asset.data?.id}_${asset.imageIndex}`);
      }
      return false;
    } else {
      return asset.type === 'video' || asset.type === 'pending_video';
    }
  });

  return (
    <div className="space-y-6" data-testid="gallery-section">

      {/* 🎬 TAB BUTTONS - Bilder / Videos / Studio */}
      <div className="flex gap-1.5 justify-center">
        <button
          onClick={() => setActiveTab('images')}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'images'
              ? 'bg-[#10B981] text-white'
              : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
          }`}
        >
          <ImageIcon className="w-4 h-4" />
          <span className="hidden sm:inline">Bilder</span>
          <span className={`text-xs px-1.5 py-0.5 rounded-full ${
            activeTab === 'images' ? 'bg-white/20' : 'bg-white/10'
          }`}>
            {loading ? '…' : (generations || []).reduce((count, gen) => {
              const images = gen.image_urls || [gen.image_url];
              return count + images.filter((url, idx) => url && url.trim() && !hiddenImageIds.has(`${gen.id}_${idx}`)).length;
            }, 0)}
          </span>
        </button>
        <button
          onClick={() => setActiveTab('videos')}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'videos'
              ? 'bg-[#10B981] text-white'
              : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
          }`}
        >
          <Video className="w-4 h-4" />
          <span className="hidden sm:inline">Videos</span>
          <span className={`text-xs px-1.5 py-0.5 rounded-full ${
            activeTab === 'videos' ? 'bg-white/20' : 'bg-white/10'
          }`}>
            {allAssets.filter(a => a.type === 'video' || a.type === 'pending_video').length}
          </span>
        </button>
        {/* Avatar Tab - ausgeblendet (Funktion jetzt im Kino Modus) */}
        <button
          onClick={() => setActiveTab('favorites')}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'favorites'
              ? 'bg-yellow-500 text-white'
              : 'bg-white/5 text-white/60 hover:bg-yellow-500/20 hover:text-yellow-400'
          }`}
        >
          <Star className="w-4 h-4" fill={activeTab === 'favorites' ? 'currentColor' : 'none'} />
          <span className={`text-xs px-1.5 py-0.5 rounded-full ${
            activeTab === 'favorites' ? 'bg-white/20' : 'bg-white/10'
          }`}>
            {favoritesCount}
          </span>
        </button>
        {/* Studio Button - ausgeblendet (Funktion jetzt über Video erstellen Accordion) */}

        {/* 📑 Grid-Mode Toggle - NUR für Master-Admin (SnapCut) */}
        {isMasterAdmin && (
        <button
          onClick={() => setPinterestMode(!pinterestMode)}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
            !pinterestMode
              ? 'bg-blue-500 text-white'
              : 'bg-white/5 text-white/60 hover:bg-blue-500/20 hover:text-blue-400'
          }`}
          title={pinterestMode ? 'Miniatur-Ansicht' : 'Pinterest-Ansicht'}
        >
          {pinterestMode ? <Grid3x3 className="w-4 h-4" /> : <Grid2x2 className="w-4 h-4" />}
        </button>
        )}

      </div>

      {/* ========== SPRECHENDER AVATAR - nur bei Avatar Tab ========== */}
      {activeTab === 'avatar' ? (
        <div className="space-y-4">
          {lipsyncVideos.length === 0 ? (
            <div className="bg-black border-2 border-white/10 rounded-2xl p-16 text-center">
              <Mic className="w-16 h-16 mx-auto mb-4 text-white/20" />
              <p className="text-white/60 text-lg mb-2">Noch keine Avatar-Videos</p>
              <p className="text-white/40 text-sm">Erstelle dein erstes Video unter "Sprechender Avatar"</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {lipsyncVideos.map((item) => {
                const itemId = item.id || item._id;
                return (
                  <div key={itemId} className="bg-white/5 border border-white/10 rounded-xl overflow-hidden hover:border-indigo-500/30 transition-all group">
                    <div className="relative aspect-video bg-black">
                      <video
                        src={item.video_url}
                        controls
                        playsInline
                        preload="metadata"
                        className="w-full h-full object-contain"
                        poster={item.source_image_url}
                      />
                    </div>
                    <div className="p-3">
                      <p className="text-white/70 text-xs line-clamp-2 mb-2">"{item.text}"</p>
                      <div className="flex items-center justify-between">
                        <span className="text-white/30 text-xs">
                          {item.created_at ? new Date(item.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' }) : ''}
                        </span>
                        <div className="flex gap-1.5">
                          <a href={item.video_url} download={`avatar_${itemId}.mp4`}
                            className="w-8 h-8 bg-white/10 hover:bg-white/20 text-white/60 rounded-lg flex items-center justify-center transition-all">
                            <Download className="w-3.5 h-3.5" />
                          </a>
                          <button onClick={async () => { try { const { shareVideo } = await import('../../utils/shareMedia'); const ss = await getShareSettings(); await shareVideo({ videoUrl: item.video_url, text: ss.video_share_text }); } catch(e){} }}
                            className="w-8 h-8 bg-white/10 hover:bg-white/20 text-white/60 rounded-lg flex items-center justify-center transition-all">
                            <Share2 className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={async () => {
                            try {
                              const res = await apiClient.delete(`/api/lipsync/${itemId}`);
                              if (res.ok) setLipsyncVideos(prev => prev.filter(v => (v.id || v._id) !== itemId));
                            } catch(e){}
                          }}
                            className="w-8 h-8 bg-red-500/10 hover:bg-red-500/30 text-red-400/60 hover:text-red-400 rounded-lg flex items-center justify-center transition-all">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : activeTab === 'videos' ? (
        <VideoStudioSection
          generations={generations}
          videos={(filteredAssets || []).filter(a => a.type === 'video')}
          pendingVideos={(filteredAssets || []).filter(a => a.type === 'pending_video')}
          readOnly={readOnly}
          onSelectImage={(img) => {
            if (readOnly) {
              navigate('/welcome');
              setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
              return;
            }
            setActiveTab('videos');
          }}
          setActionSheetImage={setActionSheetImage}
          avatarId={avatarId}
          favorites={favorites}
          toggleFavorite={toggleFavorite}
          selectedVideos={selectedVideos}
          setSelectedVideos={setSelectedVideos}
          onDeleteVideos={confirmDeleteVideos}
          isDeletingVideos={isDeletingVideos}
          onVideoDeleted={(videoId) => {
            setVideos(prev => {
              const updated = { ...prev };
              Object.keys(updated).forEach(k => { if (updated[k]?.id === videoId) delete updated[k]; });
              return updated;
            });
          }}
          onVideoCompleted={async () => {
            console.log('🎬 Video completed in VideoStudio! Refreshing...');
            try {
              const token = getToken();
              const response = await fetch(`/api/videos/list?avatar_id=${avatarId}&limit=500`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              if (response.ok) {
                const data = await response.json();
                const videoMap = {};
                (data.videos || []).forEach(v => {
                  if (v.status === 'completed' && v.video_url) {
                    const key = v.is_animation ? `animation_${v.id}` : (v.video_model === 'kino_modus' || v.video_model === 'hollywood' || !v.source_generation_id) ? `video_${v.id}` : `${v.source_generation_id}_${v.image_index}`;
                    videoMap[key] = v;
                  }
                });
                setVideos(videoMap);
                setCachedVideos(avatarId, videoMap);
                console.log('🎬 Videos aktualisiert:', Object.keys(videoMap).length);
              }
            } catch (err) {
              console.error('Video refresh error:', err);
            }
          }}
        />
      ) : (filteredAssets?.length || 0) === 0 ? (
        loading ? (
          /* Lade-Zustand: Skeleton statt "0 Bilder" */
          <div className="columns-2 gap-2 space-y-2" style={{ columnGap: '8px' }}>
            {[...Array(6)].map((_, i) => (
              <div key={i} className="break-inside-avoid mb-2">
                <div className="rounded-xl overflow-hidden bg-zinc-800/50 animate-pulse" style={{ height: `${180 + (i % 3) * 60}px` }} />
              </div>
            ))}
          </div>
        ) : (
        <div className="bg-black border-2 border-white/10 rounded-2xl p-16 text-center">
          {activeTab === 'favorites' ? (
            <>
              <Star className="w-16 h-16 mx-auto mb-4 text-white/20" />
              <p className="text-white/60 text-lg mb-2">Noch keine Favoriten ⭐</p>
              <p className="text-white/40 text-sm">Tippe auf den Stern bei Bildern oder Videos</p>
            </>
          ) : (
            <>
              <ImageIcon className="w-16 h-16 mx-auto mb-4 text-white/20" />
              <p className="text-white/60 text-lg mb-2">Noch keine Bilder 📸</p>
              <p className="text-white/40 text-sm">Starte mit "Bild erstellen" oben</p>
            </>
          )}
        </div>
        )
      ) : (
        /* ========== PHOTOS/FAVORITES TAB ========== */
        <div className="space-y-6">
          {/* 1. DEINE BILDER - Horizontal Scroll — AUSGEBLENDET (Funktion jetzt über Video erstellen) */}
          {false && activeTab === 'images' && (() => {
            // Gleiche Logik wie in VideoStudioSection
            const allImages = (generations || []).flatMap(gen =>
              (gen.image_urls || [gen.image_url])
                .filter(url => url && url.trim())
                .map((url, idx) => ({
                  url,
                  id: `${gen.id}_${idx}`,
                  genId: gen.id,
                  prompt: gen.prompt_text || gen.prompt
                }))
            );
            
            return (
              <div>
                <h3 className="text-white font-semibold mb-3">
                  Wähle ein Bild zum Animieren
                </h3>
                {(allImages?.length || 0) === 0 ? (
                  <div className="bg-white/5 rounded-xl p-6 text-center">
                    <p className="text-white/40 text-sm">Noch keine Bilder vorhanden</p>
                  </div>
                ) : (
                  <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                    {(allImages || []).map((img) => (
                      <div
                        key={img.id}
                        onClick={() => {
                          if (readOnly) {
                            navigate('/welcome');
                            setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
                            return;
                          }
                          setActiveTab('videos');
                        }}
                        className={`flex-shrink-0 w-20 h-28 rounded-xl overflow-hidden border-2 border-transparent hover:border-[#10B981] transition-all ${readOnly ? 'cursor-default' : 'cursor-pointer'}`}
                      >
                        <img src={img.url} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
          
          {/* MINIATUR-GRID (wenn !pinterestMode) - NUR für Master-Admin */}
          {isMasterAdmin && !pinterestMode && filteredAssets && filteredAssets.length > 0 && (
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-1 p-2">
              {(filteredAssets || []).filter(a => a && (a.type === 'image' || a.type === 'video')).map((asset) => {
                if (!asset) return null;
                const isVideo = asset.type === 'video';
                const thumb = isVideo 
                  ? (asset.data?.source_image_url || '') 
                  : (asset.imageUrl || asset.data?.image_urls?.[0] || asset.data?.image_url || asset.data?.url || asset.url || '');
                if (!thumb) return null;
                return (
                  <div key={`mini-${asset.id}`} className="relative aspect-square overflow-hidden rounded-md bg-white/5">
                    <img src={thumb} alt="" className="w-full h-full object-cover" loading="lazy" />
                    {isVideo && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                        <Play className="w-3 h-3 text-white/80" fill="white" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* 2. Masonry Layout — "Kürzeste Spalte zuerst" Verteilung */}
          {pinterestMode && (() => {
            // Geschätzte relative Höhe (Höhe pro Einheit Breite) aus aspect_ratio
            const parseAR = (ar) => {
              if (typeof ar !== 'string' || !ar.includes(':')) return null;
              const [w, h] = ar.split(':').map(Number);
              return (w > 0 && h > 0) ? h / w : null;
            };
            const formatToRatio = {
              landscape_16_9: 9 / 16, portrait_9_16: 16 / 9,
              square_1_1: 1, portrait_2_3: 3 / 2, landscape_3_2: 2 / 3,
              portrait_3_4: 4 / 3, landscape_4_3: 3 / 4,
            };
            const getItemHeight = (asset) => {
              if (asset.type === 'pending_generation') return 1.33;
              if (asset.type === 'pending_video') return 1.77;
              const data = asset.data || {};
              const gs = data.generation_settings || {};
              if (gs.width && gs.height && gs.width > 0) return gs.height / gs.width;
              if (gs.image_format && formatToRatio[gs.image_format]) return formatToRatio[gs.image_format];
              const ar = parseAR(data.aspect_ratio) ?? parseAR(data.video_aspect_ratio);
              if (ar) return ar;
              if (data.width && data.height && data.width > 0) return data.height / data.width;
              return 1;
            };

            // Jedes Item kommt in die aktuell kürzeste Spalte (bei Gleichstand: linkste).
            // Höhen aller Items (auch hidden) fließen ein, damit hidden-Toggle die Verteilung nicht verschiebt → kein Flackern.
            const cols = Array.from({ length: masonryCols }, () => []);
            const colHeights = new Array(masonryCols).fill(0);
            (filteredAssets || []).forEach((asset, idx) => {
              let shortest = 0;
              for (let c = 1; c < masonryCols; c++) {
                if (colHeights[c] < colHeights[shortest]) shortest = c;
              }
              cols[shortest].push({ asset, originalIndex: idx });
              colHeights[shortest] += getItemHeight(asset);
            });

            // Render-Funktion für einzelnes Asset
            const renderAsset = (asset, assetIndex) => {
            // Gelöschte Bilder: null rendern statt aus filteredAssets entfernen.
            // So bleibt die Round-Robin-Spaltenverteilung stabil (keine Umverteilung → kein Flackern).
            if (hiddenImageIds.has(asset.id)) return null;

            // ========== PENDING GENERATION (Pinterest) - Mit Prozent-Kreis ==========
            if (asset.type === 'pending_generation') {
              return (
                <PendingGenerationCard
                  key={`pending-${asset.id}`}
                  startTime={asset.startTime}
                  sourceImageUrl={asset.sourceImageUrl}
                  cardId={String(asset.id || '').startsWith('demo-pending-image') ? 'demo-pending-image-card' : null}
                />
              );
            }

            // ========== PENDING VIDEO (Pinterest) - GROK-Style Pixel Animation ==========
            if (asset.type === 'pending_video') {
              const pv = asset.data;
              return (
                <PendingVideoCard
                  key={`pending-video-${asset.id}`}
                  startTime={new Date(pv.created_at).getTime()}
                  sourceImageUrl={pv.source_image_url}
                  aspect={pv.video_aspect_ratio}
                  cardId={String(asset.id || '').startsWith('demo-pending-video') ? 'demo-pending-video-card' : null}
                />
              );
            }

            // ========== VIDEO (Pinterest) ==========
            if (asset.type === 'video') {
              const videoData = asset.data;
              const posterUrl = videoData.poster_thumbnail_url || (videoData.source_image_url?.includes('snapshotpro.ai') ? videoData.source_image_url : videoData.source_image_url ? `/image-proxy?url=${encodeURIComponent(videoData.source_image_url)}` : '');
              return (
                <div key={`video-${asset.id}`} id={`video-card-${videoData.id}`} className="mb-1">
                  <div
                    className="relative rounded-xl overflow-hidden cursor-pointer group bg-zinc-900"
                    onClick={() => setFullscreenVideo({
                      id: videoData.id,
                      video_url: videoData.video_url,
                      video_prompt: videoData.user_prompt || videoData.video_prompt || videoData.text || videoData.prompt || '',
                      user_prompt: videoData.user_prompt || videoData.video_prompt || videoData.text || videoData.prompt || '',
                      image_analysis: videoData.image_analysis || '',
                      source_image_url: posterUrl,
                      original_speed: videoData.original_speed || 1,
                      created_at: videoData.created_at,
                      motion_tags: videoData.motion_tags || [],
                      cost_snaps: videoData.cost_snaps,
                      video_aspect_ratio: videoData.video_aspect_ratio,
                      video_resolution: videoData.video_resolution
                    })}
                  >
                    {posterUrl && <img src={posterUrl} alt="" className="w-full h-auto" loading="lazy" />}
                    {/* Multi-Select Checkbox */}
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedVideos(prev => {
                          const newSet = new Set(prev);
                          if (newSet.has(videoData.id)) {
                            newSet.delete(videoData.id);
                          } else {
                            newSet.add(videoData.id);
                          }
                          return newSet;
                        });
                      }}
                      className={`absolute top-2 left-2 w-8 h-8 rounded-full flex items-center justify-center cursor-pointer z-20 transition-all ${
                        selectedVideos.has(videoData.id)
                          ? 'bg-red-500 border-2 border-red-500'
                          : 'bg-black/50 border-2 border-white/40'
                      }`}
                    >
                      {selectedVideos.has(videoData.id) && (
                        <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="w-12 h-12 bg-black/50 rounded-full flex items-center justify-center">
                        <Play className="w-6 h-6 text-white ml-1" fill="white" />
                      </div>
                    </div>
                    {/* Favoriten-Stern - neben der Checkbox */}
                    {favorites.has(videoData.id) && (
                      <div className="absolute top-2.5 left-12">
                        <Star className="w-5 h-5 text-yellow-400 fill-yellow-400 drop-shadow-lg" />
                      </div>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setFullscreenVideo(videoData);
                      }}
                      className="absolute bottom-2 right-2 p-2 bg-black/50 hover:bg-black/70 rounded-full opacity-70 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                    >
                      <MoreHorizontal className="w-5 h-5 text-white" />
                    </button>
                  </div>
                </div>
              );
            }

            // ========== BILDER (Pinterest) ==========
            if (asset.type === 'image') {
              const gen = asset.data;
              // FIX: Asset hat bereits imageUrl - nutze das direkt (keine Doppel-Render!)
              const url = asset.imageUrl;
              const imgIdx = asset.imageIndex || 0;

              // Safety: Skip if no gen.id or no url
              if (!gen.id || !url) {
                console.warn("⚠️ Generation ohne ID oder URL übersprungen:", gen.id, url);
                return null;
              }
              
              // Thumbnail URL ermitteln (falls vorhanden)
              const thumbnails = gen.thumbnail_urls || [];
              const displayUrl = (thumbnails[imgIdx] && thumbnails[imgIdx].startsWith("/storage/")) 
                ? thumbnails[imgIdx] 
                : url;
              
              const imageId = `${gen.id}_${imgIdx}`;
              const isDemoGenerated = String(gen.id || '').startsWith('demo-generation-') && imgIdx === 0;
                return (
                  <div key={imageId} id={isDemoGenerated ? 'demo-generated-image-card' : `image-card-${imageId}`} data-image-card={`image-card-${imageId}`} className="mb-1" style={{ contain: 'layout' }}>
                    <div
                      className="relative rounded-xl overflow-hidden cursor-pointer group bg-zinc-900"
                      style={{ minHeight: '120px' }}
                      onMouseEnter={() => { if (displayUrl !== url) { const p = new Image(); p.src = url; } }}
                      onTouchStart={() => { if (displayUrl !== url) { const p = new Image(); p.src = url; } }}
                      onClick={() => {
                        // Im Auswahlmodus: Bild togglen statt öffnen
                        if (selectedForDelete.size > 0) {
                          toggleSelectForDelete(imageId);
                          return;
                        }
                        // Fullscreen Viewer öffnen
                        const allImages = (generations || []).flatMap((g) =>
                          (g.image_urls || [g.image_url])
                            .map((imgUrl, idx) => {
                              if (!imgUrl) return null;
                              return {
                                id: `${g.id}_${idx}`,
                                url: imgUrl,
                                genId: g.id,
                                generationId: g.id,
                                imageIndex: idx,
                                prompt: g.prompt_text || g.prompt,
                                date: g.created_at,
                                cost: g.cost_credits,
                                generation: g
                              };
                            })
                            .filter(Boolean)
                        );
                        const flatIndex = allImages.findIndex(img => img.id === imageId);
                        setActionSheetImage({ images: allImages, index: flatIndex >= 0 ? flatIndex : 0 });
                      }}
                    >
                      <img
                        src={displayUrl}
                        alt=""
                        className="w-full h-auto opacity-0 transition-opacity duration-300"
                        loading="lazy"
                        onLoad={(e) => { e.target.classList.remove('opacity-0'); }}
                        onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                      />
                      {/* Multi-Select Checkbox oben links */}
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          toggleSelectForDelete(imageId, e);
                        }}
                        onTouchEnd={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          toggleSelectForDelete(imageId, e);
                        }}
                        className={`absolute top-2 left-2 w-7 h-7 rounded-full flex items-center justify-center cursor-pointer z-20 transition-all ${
                          selectedForDelete.has(imageId)
                            ? 'bg-red-500 border-2 border-red-500'
                            : 'bg-black/50 border-2 border-white/40'
                        }`}
                      >
                        {selectedForDelete.has(imageId) && (
                          <span className="text-white text-xs font-bold">✓</span>
                        )}
                      </div>
                      {/* Favoriten-Stern in Galerie - neben Checkbox */}
                      {favorites.has(imageId) && (
                        <div className="absolute top-2 left-10 p-1.5 bg-black/40 rounded-full">
                          <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                        </div>
                      )}
                      {/* 3-Punkte Button entfernt - Klick aufs Bild öffnet jetzt direkt ActionSheet */}
                    </div>
                  </div>
                );
            }

            return null;
            };

            return (
              <div className="flex gap-1">
                {cols.map((colItems, colIdx) => (
                  <div key={colIdx} className="flex-1 flex flex-col gap-1">
                    {colItems.map(({ asset, originalIndex }) => renderAsset(asset, originalIndex))}
                  </div>
                ))}
              </div>
            );
          })()}

      {/* Classic Layout entfernt - Mini-Grid ist jetzt der einzige alternative Mode */}
      {false && (
        <>
        {/* ========== KLASSISCHES LAYOUT (DEAKTIVIERT) ========== */}
        {(filteredAssets || []).map((asset, assetIndex) => {
          const globalNumber = filteredAssets.length - assetIndex; // Nummerierung innerhalb des Tabs

          // ========== PENDING GENERATION CARD (Bild wird erstellt) ==========
          if (asset.type === 'pending_generation') {
            const pendingGen = asset.data;
            const createdAt = pendingGen.created_at ? new Date(pendingGen.created_at + (String(pendingGen.created_at).includes('Z') || String(pendingGen.created_at).includes('+') ? '' : 'Z')).toLocaleString('de-DE', {
              day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
            }) : '';
            const numImages = pendingGen.num_images || 1;
            const costCredits = pendingGen.cost_credits || 0;

            return (
              <div
                key={`pending-gen-${pendingGen.id}`}
                className="bg-black border-4 border-emerald-500/50 rounded-2xl p-6 space-y-4 animate-pulse"
              >
                {/* === HEADER === */}
                <div className="flex items-center justify-between pb-3 border-b border-emerald-500/30">
                  <div>
                    <h4 className="text-white font-bold text-lg flex items-center gap-2">
                      <span className="px-2 py-0.5 bg-emerald-500 text-white text-xs font-bold rounded animate-pulse">🖼️</span>
                      Bild wird erstellt...
                    </h4>
                    <p className="text-white/50 text-sm">{createdAt}</p>
                    <p className="text-emerald-400/80 text-xs mt-1">
                      {numImages} {numImages === 1 ? 'Bild' : 'Bilder'} • {costCredits} SNAPs
                    </p>
                  </div>
                  {/* Live-Indikator */}
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/20 border border-emerald-500/30 rounded-full">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full animate-ping"></div>
                    <span className="text-emerald-300 text-xs font-medium">Generiert...</span>
                  </div>
                </div>

                {/* === SPINNER AREA — ein Platzhalter pro Bild === */}
                <div className={`grid gap-2 ${numImages >= 4 ? 'grid-cols-2' : numImages >= 2 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                  {Array.from({ length: numImages }, (_, i) => (
                    <div key={i} className="relative rounded-xl overflow-hidden border-2 border-emerald-500/30 bg-emerald-500/5 aspect-[3/4]">
                      <PendingGenerationCard startTime={pendingGen.created_at ? new Date(pendingGen.created_at).getTime() : Date.now()} sourceImageUrl={null} />
                    </div>
                  ))}
                </div>

                {/* === PROMPT PREVIEW === */}
                {pendingGen.prompt_text && (
                  <div className="text-white/60 text-sm italic line-clamp-2 px-2">
                    "{pendingGen.prompt_text}"
                  </div>
                )}
              </div>
            );
          }

          // ========== VIDEO CARD ==========
          if (asset.type === 'video') {
            const videoData = asset.data;
            const modelLabel = getVideoModelLabel(videoData.video_model);
            const costSnaps = videoData.cost_snaps || 0;
            const createdAt = videoData.created_at ? new Date(videoData.created_at + (String(videoData.created_at).includes('Z') || String(videoData.created_at).includes('+') ? '' : 'Z')).toLocaleString('de-DE', {
              day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
            }) : '';
            const posterUrl = videoData.source_image_url || '';
            const isVideoExpanded = expandedVideoId === videoData.id;

            return (
              <div
                key={`video-card-${videoData.id}`}
                className="bg-black border-4 border-[#10B981]/30 rounded-2xl p-6 space-y-4"
              >
                {/* === HEADER (globale Nummerierung!) === */}
                <div className="flex items-center justify-between pb-3 border-b border-white/10">
                  <div>
                    <h4 className="text-white font-bold text-lg flex items-center gap-2">
                      {/* 🎬 Badge bleibt BLAU zur Video-Identifizierung */}
                      <span className="px-2 py-0.5 bg-blue-500 text-white text-xs font-bold rounded">🎬</span>
                      Generation #{globalNumber}
                    </h4>
                    <p className="text-white/50 text-sm">{createdAt}</p>
                    <p className="text-[#10B981]/80 text-xs mt-1">
                      {costSnaps > 0 ? (
                        <span>💰 {costSnaps} SNAPs • {modelLabel} • 5 Sek</span>
                      ) : (
                        <span className="text-emerald-400">🎁 Gratis • {modelLabel} • 5 Sek</span>
                      )}
                    </p>
                  </div>

                  {/* Delete Button für Videos - nur wenn nicht readOnly */}
                  {!readOnly && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteVideo(videoData.id);
                      }}
                      disabled={deleting === `video_${videoData.id}`}
                      className={`p-2 rounded-lg transition-all ${
                        deleting === `video_${videoData.id}`
                          ? 'bg-red-500/30 text-red-300 cursor-not-allowed'
                          : 'bg-red-500/20 text-red-400 hover:bg-red-500/40 hover:text-red-300'
                      }`}
                      title="Video löschen"
                    >
                      {deleting === `video_${videoData.id}` ? (
                        <div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                  )}

                  {/* Add to Queue Button - ENTFERNT (jetzt über Snap Studio) */}
                </div>

                {/* === SMART VIDEO PREVIEW (Hover on Desktop, InView on Mobile) === */}
                {/* previewUrl = Low-Res Proxy (100-200 KB), videoUrl = Original HD */}
                <VideoPreview
                  videoUrl={videoData.video_url}
                  previewUrl={videoData.preview_url}
                  posterUrl={posterUrl}
                  onClick={() => setFullscreenVideo({
                    id: videoData.id,
                    video_url: videoData.video_url,
                    video_prompt: videoData.user_prompt || videoData.video_prompt || videoData.text || videoData.prompt || '',
                    user_prompt: videoData.user_prompt || videoData.video_prompt || videoData.text || videoData.prompt || '',
                    image_analysis: videoData.image_analysis || '',
                    source_image_url: posterUrl,
                    original_speed: videoData.original_speed || 1,
                    created_at: videoData.created_at,
                    motion_tags: videoData.motion_tags || [],
                    cost_snaps: videoData.cost_snaps,
                    video_aspect_ratio: videoData.video_aspect_ratio,
                    video_resolution: videoData.video_resolution
                  })}
                />

                {/* === PROMPT DETAILS ACCORDION (GRÜN wie bei Bildern!) === */}
                <div className="border-t-4 border-[#10B981]/30 pt-4">
                  <button
                    onClick={() => setExpandedVideoId(isVideoExpanded ? null : videoData.id)}
                    className="w-full flex items-center justify-between p-4 bg-white/5 hover:bg-white/10 rounded-xl transition-all"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#10B981]/20 rounded-xl flex items-center justify-center">
                        <svg className="w-5 h-5 text-[#10B981]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                      </div>
                      <div className="text-left">
                        <h3 className="text-white font-bold text-base">Prompt Details</h3>
                        <p className="text-white/50 text-xs">Motion-Prompt & Einstellungen</p>
                      </div>
                    </div>
                    {isVideoExpanded ? (
                      <ChevronUp className="w-5 h-5 text-[#10B981]" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-white/50" />
                    )}
                  </button>

                  {/* Expanded Content */}
                  {isVideoExpanded && (
                    <div className="mt-4 p-6 bg-gradient-to-br from-white/5 to-white/[0.02] border-2 border-[#10B981]/30 rounded-xl space-y-4" style={{ overscrollBehavior: 'contain' }}>
                      {/* METADATEN: Datum & Uhrzeit */}
                      <div className="flex items-center justify-between pb-3 border-b border-white/10">
                        <div>
                          <p className="text-white/50 text-xs font-semibold uppercase tracking-wider">Generiert am</p>
                          <p className="text-white font-medium">
                            {videoData.created_at ? new Date(videoData.created_at + (String(videoData.created_at).includes('Z') || String(videoData.created_at).includes('+') ? '' : 'Z')).toLocaleString('de-DE', {
                              day: '2-digit', month: '2-digit', year: 'numeric',
                              hour: '2-digit', minute: '2-digit', second: '2-digit'
                            }) : 'Unbekannt'}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-white/50 text-xs font-semibold uppercase tracking-wider">Dauer</p>
                          <p className="text-white font-medium">{videoData.duration || 5}s</p>
                        </div>
                      </div>

                      {/* VERWENDETE CHIPS (motion_tags) */}
                      {videoData.motion_tags && videoData.motion_tags.length > 0 && (
                        <div>
                          <p className="text-white/50 text-xs mb-2 font-semibold uppercase tracking-wider">🏷️ Verwendete Chips</p>
                          <div className="flex flex-wrap gap-2">
                            {videoData.motion_tags.map((tag, idx) => (
                              <span key={idx} className="px-2 py-1 bg-[#F59E0B]/20 border border-[#F59E0B]/30 rounded-lg text-[#F59E0B] text-xs font-medium">
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Motion Prompt - SCROLLBAR (max 150px, dann scrollen) */}
                      {(videoData.user_prompt || videoData.video_prompt || videoData.text || videoData.prompt) ? (
                        <div>
                          <p className="text-white/50 text-xs mb-2 font-semibold uppercase tracking-wider">📝 Dein Prompt</p>
                          <div
                            className="bg-white/5 p-3 rounded-lg overflow-y-scroll"
                            style={{
                              maxHeight: '150px',
                              overscrollBehavior: 'contain',
                              WebkitOverflowScrolling: 'touch',
                              touchAction: 'pan-y'
                            }}
                          >
                            <p className="text-white/90 text-sm leading-relaxed whitespace-pre-wrap">
                              {videoData.user_prompt || videoData.video_prompt || videoData.text || videoData.prompt}
                            </p>
                          </div>
                        </div>
                      ) : (
                        <p className="text-white/40 text-sm">Kein Motion-Prompt verwendet</p>
                      )}

                      {/* Talking Text (falls vorhanden) */}
                      {videoData.talking_text && (
                        <div>
                          <p className="text-white/50 text-xs mb-2 font-semibold uppercase tracking-wider">🎤 Gesprochener Text</p>
                          <p className="text-white/90 text-sm leading-relaxed bg-purple-500/10 border border-purple-500/20 p-3 rounded-lg italic">
                            "{videoData.talking_text}"
                          </p>
                        </div>
                      )}

                      {/* Video Settings Chips - ALLE GRÜN */}
                      <div>
                        <p className="text-white/50 text-xs mb-3 font-semibold uppercase tracking-wider">⚙️ Einstellungen</p>
                        <div className="flex flex-wrap gap-2">
                          <span className="px-3 py-1.5 bg-[#10B981]/20 border border-[#10B981]/30 rounded-lg text-[#10B981] text-xs font-medium">
                            {modelLabel}
                          </span>
                          <span className="px-3 py-1.5 bg-white/5 border border-white/20 rounded-lg text-white/90 text-xs font-medium">
                            {videoData.resolution || '720p'}
                          </span>
                          <span className="px-3 py-1.5 bg-emerald-500/20 border border-emerald-500/30 rounded-lg text-emerald-300 text-xs font-medium">
                            {costSnaps > 0 ? `${costSnaps} SNAPs` : 'Gratis'}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          }

          // ========== PENDING VIDEO CARD (Spinner während Generierung) ==========
          if (asset.type === 'pending_video') {
            const pendingData = asset.data;
            const modelLabel = getVideoModelLabel(pendingData.video_model);
            const costSnaps = pendingData.cost_snaps || 0;
            const createdAt = pendingData.created_at ? new Date(pendingData.created_at + (String(pendingData.created_at).includes('Z') || String(pendingData.created_at).includes('+') ? '' : 'Z')).toLocaleString('de-DE', {
              day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
            }) : '';
            const posterUrl = pendingData.source_image_url || '';

            return (
              <div
                key={`pending-video-${pendingData.id}`}
                className="bg-black border-4 border-blue-500/50 rounded-2xl p-6 space-y-4 animate-pulse"
              >
                {/* === HEADER === */}
                <div className="flex items-center justify-between pb-3 border-b border-blue-500/30">
                  <div>
                    <h4 className="text-white font-bold text-lg flex items-center gap-2">
                      <span className="px-2 py-0.5 bg-blue-500 text-white text-xs font-bold rounded animate-pulse">🎬</span>
                      Video wird erstellt...
                    </h4>
                    <p className="text-white/50 text-sm">{createdAt}</p>
                    <p className="text-blue-400/80 text-xs mt-1">
                      {modelLabel} • {costSnaps} SNAPs
                    </p>
                  </div>
                  {/* Live-Indikator */}
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-500/20 border border-blue-500/30 rounded-full">
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-ping"></div>
                    <span className="text-blue-300 text-xs font-medium">Generiert...</span>
                  </div>
                </div>

                {/* === BILD MIT GROK-STYLE PIXEL ANIMATION === */}
                <div className="relative rounded-xl overflow-hidden border-2 border-blue-500/30">
                  {posterUrl ? (
                    <>
                      {/* Blurred Background */}
                      <div
                        className="absolute inset-0"
                        style={{
                          backgroundImage: `url(${posterUrl})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                          filter: 'blur(12px)',
                          transform: 'scale(1.1)'
                        }}
                      />
                      {/* Original Image (slightly faded) */}
                      <img
                        src={posterUrl}
                        alt="Video wird erstellt..."
                        className="w-full h-auto relative z-10 opacity-70"
                      />
                    </>
                  ) : (
                    <div className="w-full aspect-video bg-blue-500/10 flex items-center justify-center">
                      <Video className="w-16 h-16 text-blue-500/30" />
                    </div>
                  )}

                  {/* Dark Overlay */}
                  <div className="absolute inset-0 bg-black/40 z-20" />
                  
                  {/* Feines Dot Grid Overlay - Animated */}
                  <div
                    className="absolute inset-0 pointer-events-none z-30 animate-pulse"
                    style={{
                      backgroundImage: `radial-gradient(circle, rgba(255,255,255,0.4) 1px, transparent 1px)`,
                      backgroundSize: '5px 5px'
                    }}
                  />

                  {/* Status Text Overlay mit Prozentzahl */}
                  <VideoProgressOverlay startTime={pendingData.created_at ? new Date(pendingData.created_at).getTime() : Date.now()} />
                </div>

                {/* Fortschritts-Info */}
                <div className="flex items-center justify-center gap-2 text-blue-300/80 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Erscheint automatisch wenn fertig</span>
                </div>
              </div>
            );
          }

          // ========== BILD GENERATION CARD ==========
          if (asset.type === 'image') {
            const gen = asset.data;
            const images = gen.image_urls || [gen.image_url];

            // Überspringe nur wenn WIRKLICH keine Bilder da sind
            const validImages = images.filter(url => url && url.trim());
            if (validImages.length === 0) {
              return null;
            }

            const isExpanded = expandedGeneration === gen.id;
            const settings = gen.generation_settings || gen.settings || {};
            const promptText = gen.prompt_text || gen.prompt || '';

            return (
              <div
                key={gen.id}
                className="bg-black border-4 border-[#10B981]/30 rounded-2xl p-6 space-y-4"
                data-generation-id={gen.id}
              >
                {/* Generation Header with Grid Toggle */}
                <div className="flex items-center justify-between pb-3 border-b border-white/10">
                  <div>
                    <h4 className="text-white font-bold text-lg">Generation #{globalNumber}</h4>
                  <p className="text-white/50 text-sm">
                    {new Date(gen.created_at + (String(gen.created_at).includes('Z') || String(gen.created_at).includes('+') ? '' : 'Z')).toLocaleString('de-DE', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                  {/* Kosten-Anzeige - immer anzeigen */}
                  <p className="text-[#10B981]/80 text-xs mt-1">
                    {gen.cost_credits === 0 || gen.cost_credits === null ? (
                      <span className="text-emerald-400">🎁 Gratis</span>
                    ) : (
                      <span>💰 {snapText(gen.cost_credits)} bezahlt</span>
                    )}
                  </p>
                </div>

                {/* Delete All & Grid Toggle Buttons */}
                <div className="flex items-center gap-2">
                  {/* Remix Badge */}
                  {gen.is_remix && (
                    <div
                      className="flex items-center gap-1 px-2 py-1 bg-[#10B981]/20 border border-[#10B981]/50 rounded-lg"
                      title="Remix Generation"
                    >
                      <Sparkles className="w-3.5 h-3.5 text-[#10B981]" />
                      <span className="text-[#10B981] text-xs font-semibold">Remix</span>
                    </div>
                  )}
                  {/* Delete All Button - nur wenn nicht readOnly */}
                  {!readOnly && (
                    <button
                      onClick={() => deleteAllImagesInGeneration(gen)}
                      disabled={deleting === `gen_${gen.id}`}
                      className={`p-2 rounded-lg transition-all ${
                        deleting === `gen_${gen.id}`
                          ? 'bg-red-500/30 text-red-300 cursor-not-allowed'
                          : 'bg-red-500/20 text-red-400 hover:bg-red-500/40 hover:text-red-300'
                      }`}
                      title="Alle Bilder dieser Generation löschen"
                    >
                      {deleting === `gen_${gen.id}` ? (
                        <div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                  )}

                  {/* Desktop: 3 Buttons (1/2/4) */}
                  <div className="hidden sm:flex items-center gap-2">
                    <button
                      onClick={() => setGridView(1)}
                      className={`p-2 rounded-lg transition-all ${
                        gridView === 1
                          ? 'bg-[#10B981] text-black'
                          : 'bg-white/5 text-white/60 hover:bg-white/10'
                      }`}
                      title="Einzelansicht"
                    >
                      <Square className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setGridView(2)}
                      className={`p-2 rounded-lg transition-all ${
                        gridView === 2
                          ? 'bg-[#10B981] text-black'
                          : 'bg-white/5 text-white/60 hover:bg-white/10'
                      }`}
                      title="2 Bilder"
                    >
                      <Grid2x2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setGridView(4)}
                      className={`p-2 rounded-lg transition-all ${
                        gridView === 4
                          ? 'bg-[#10B981] text-black'
                          : 'bg-white/5 text-white/60'
                      }`}
                      title="4 Bilder (2x2)"
                    >
                      <Grid3x3 className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Mobile: 2 Buttons (1/4) */}
                  <div className="flex sm:hidden items-center gap-2">
                    <button
                      onClick={() => setGridView(1)}
                      className={`p-2 rounded-lg transition-all ${
                        gridView === 1
                          ? 'bg-[#10B981] text-black'
                          : 'bg-white/5 text-white/60'
                      }`}
                      title="Einzelansicht"
                    >
                      <Square className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setGridView(4)}
                      className={`p-2 rounded-lg transition-all ${
                        gridView === 4
                          ? 'bg-[#10B981] text-black'
                          : 'bg-white/5 text-white/60'
                      }`}
                      title="4 Bilder (2x2)"
                    >
                      <Grid3x3 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Image Grid - NUR Bilder (Videos sind jetzt eigene Karten!) */}
              <div className={`grid ${gridClass} gap-4`} key={refreshKey}>
                {/* Bilder der Generation */}
                {images.map((url, imgIdx) => {
                  const imageId = `${gen.id}_${imgIdx}`;

                  if (!url) return null;

                  return (
                    <div
                      key={imageId}
                      className="relative group overflow-hidden rounded-xl border-2 border-white/10 hover:border-[#10B981]/50 transition-all cursor-pointer"
                      onClick={() => {
                        const allImages = (generations || []).flatMap((g) =>
                          (g.image_urls || [g.image_url])
                            .map((imgUrl, idx) => {
                              const imgId = `${g.id}_${idx}`;
                              if (!imgUrl) return null;
                              return {
                                id: imgId,
                                url: imgUrl,
                                prompt: g.prompt_text || g.prompt,
                                rating: getRating(imgId)
                              };
                            })
                            .filter(Boolean)
                        );
                        const flatIndex = allImages.findIndex(img => img.url === url);
                        openLightbox(allImages, flatIndex);
                      }}
                    >
                      <img
                        src={url}
                        alt={`Generated ${imgIdx + 1}`}
                        className="w-full h-auto"
                        loading="eager"
                        decoding="async"
                        fetchpriority="high"
                        onError={(e) => {
                          // iOS Safari Fix: Verstecke broken images
                          e.target.style.display = 'none';
                          e.target.parentElement.style.display = 'none';
                        }}
                      />

                      {/* Delete Button - Ausgeblendet im ReadOnly */}
                      {!readOnly && (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          deleteImage(imageId, e, gen.id, images.filter(Boolean).length);
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        onTouchStart={(e) => e.stopPropagation()}
                        disabled={deleting === imageId}
                        className={`absolute top-2 left-2 p-2 backdrop-blur-sm border rounded-full transition-all pointer-events-auto z-20 text-white shadow-lg ${
                          deleting === imageId
                            ? 'bg-white/20 border-white/30 cursor-not-allowed'
                            : 'bg-red-500/90 border-red-600 hover:bg-red-600'
                        }`}
                        title="Bild löschen"
                        type="button"
                      >
                        {deleting === imageId ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                      )}

                      {/* Set as Profile Picture Button - Ausgeblendet im ReadOnly */}
                      {!readOnly && (
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();

                          try {
                            // API-Call statt localStorage
                            const token = getToken();
                            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/user/profile-image`, {
                              method: 'POST',
                              headers: {
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'application/json'
                              },
                              body: JSON.stringify({
                                image_url: url,
                                avatar_id: avatarId
                              })
                            });

                            if (!res.ok) throw new Error('Fehler beim Speichern');

                            // Event dispatchen für sofortige Aktualisierung (kein Reload)
                            window.dispatchEvent(new CustomEvent('profileImageUpdated', {
                              detail: { imageUrl: url }
                            }));

                            const toast = document.createElement('div');
                            toast.textContent = '✅ Ist jetzt dein Profilbild';
                            toast.className = 'fixed top-20 left-1/2 transform -translate-x-1/2 bg-[#10B981] text-black px-6 py-3 rounded-xl font-bold shadow-xl z-50';
                            document.body.appendChild(toast);
                            setTimeout(() => toast.remove(), 2000);
                          } catch (error) {
                            console.error('Profilbild-Fehler:', error);
                            alert('Fehler beim Setzen des Profilbildes');
                          }
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        onTouchStart={(e) => e.stopPropagation()}
                        className="absolute top-2 right-2 p-2 bg-[#10B981]/90 backdrop-blur-sm border border-[#10B981] rounded-full hover:bg-[#10B981] transition-all pointer-events-auto z-20 text-black shadow-lg"
                        title="Als Profilbild setzen"
                        type="button"
                        data-testid={`set-profile-pic-${imageId}`}
                      >
                        <User className="w-4 h-4" />
                      </button>
                      )}

                      {/* Remix Button - NUR für eingeloggte User */}
                      {!readOnly && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setRemixImage({
                            url: url,
                            imageId: imageId,
                            prompt: promptText,
                            settings: settings,
                            generationId: gen.id
                          });
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        onTouchStart={(e) => e.stopPropagation()}
                        className="absolute top-14 right-2 p-2 bg-[#10B981]/90 backdrop-blur-sm border border-[#10B981] rounded-full hover:bg-[#10B981] transition-all pointer-events-auto z-20 text-white shadow-lg"
                        title="Bild remixen"
                        type="button"
                        data-testid={`remix-${imageId}`}
                      >
                        <Sparkles className="w-4 h-4" />
                      </button>
                      )}

                      {/* 🎬 Video Button - AKTIV */}
                      {!readOnly && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onOpenVideoAccordion) {
                            onOpenVideoAccordion(url);
                          } else {
                            setActiveTab('videos');
                          }
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        onTouchStart={(e) => e.stopPropagation()}
                        className="absolute top-14 left-2 p-2 bg-blue-500/90 backdrop-blur-sm border border-blue-500 rounded-full hover:bg-blue-500 transition-all pointer-events-auto z-20 text-white shadow-lg"
                        title="Video erstellen"
                        type="button"
                        data-testid={`video-${imageId}`}
                      >
                        <Video className="w-4 h-4" />
                      </button>
                      )}

                      {/* Add to Queue Button - ENTFERNT (jetzt über Snap Studio) */}

                      {/* Modify Button - AUSGEBLENDET (später aktivieren) */}
                      {/* <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setModifyImage({
                            url: url,
                            imageId: imageId,
                            prompt: promptText,
                            settings: settings,
                            generationId: gen.id
                          });
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        onTouchStart={(e) => e.stopPropagation()}
                        className="absolute top-[104px] right-2 p-2 bg-orange-500/90 backdrop-blur-sm border border-orange-500 rounded-full hover:bg-orange-500 transition-all pointer-events-auto z-20 text-white shadow-lg"
                        title="Bild modifizieren"
                        type="button"
                        data-testid={`modify-${imageId}`}
                      >
                        <Wrench className="w-4 h-4" />
                      </button> */}

                      {/* Star Rating */}
                      <div
                        className="absolute bottom-2 left-1/2 transform -translate-x-1/2 bg-black/90 backdrop-blur-sm px-3 py-1.5 rounded-lg pointer-events-auto z-10"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <StarRating imageId={imageId} initialRating={getRating(imageId)} disabled={readOnly} />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Accordion: Prompt Details */}
              <div className="border-t-4 border-[#10B981]/30 pt-4">
                <button
                  onClick={() => setExpandedGeneration(isExpanded ? null : gen.id)}
                  className="w-full flex items-center justify-between p-4 bg-white/5 hover:bg-white/10 rounded-xl transition-all"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#10B981]/20 rounded-xl flex items-center justify-center">
                      <svg className="w-5 h-5 text-[#10B981]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                    </div>
                    <div className="text-left">
                      <h3 className="text-white font-bold text-base">Prompt Details</h3>
                      <p className="text-white/50 text-xs">Einstellungen anzeigen & übernehmen</p>
                    </div>
                  </div>
                  {isExpanded ? (
                    <ChevronUp className="w-5 h-5 text-[#10B981]" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-white/50" />
                  )}
                </button>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="mt-4 p-6 bg-gradient-to-br from-white/5 to-white/[0.02] border-2 border-[#10B981]/30 rounded-xl space-y-4">
                    {/* Prompt Text */}
                    {promptText && (
                                  <div>
                                    <p className="text-white/50 text-xs mb-2 font-semibold uppercase tracking-wider">Dein Prompt</p>
                                    <p className="text-white font-semibold text-base leading-relaxed">
                                      {promptText.length > 200
                                        ? `${promptText.substring(0, 200)}...`
                                        : promptText}
                                    </p>
                                  </div>
                    )}

                    {/* Settings Chips */}
                    {settings && Object.keys(settings).length > 0 && (
                      <div>
                        <p className="text-white/50 text-xs mb-3 font-semibold uppercase tracking-wider">Einstellungen</p>
                        <div className="flex flex-wrap gap-2">
                          {settings.time_of_day && (
                            <span className="px-3 py-1.5 bg-white/5 backdrop-blur-sm border border-white/20 rounded-lg text-white/90 text-xs font-medium hover:bg-white/10 transition-all">
                              {getLabelForValue('time_of_day', settings.time_of_day)}
                            </span>
                          )}
                          {settings.weather && (
                            <span className="px-3 py-1.5 bg-white/5 backdrop-blur-sm border border-white/20 rounded-lg text-white/90 text-xs font-medium hover:bg-white/10 transition-all">
                              {getLabelForValue('weather', settings.weather)}
                            </span>
                          )}
                          {settings.camera && (
                            <span className="px-3 py-1.5 bg-white/5 backdrop-blur-sm border border-white/20 rounded-lg text-white/90 text-xs font-medium hover:bg-white/10 transition-all">
                              {getLabelForValue('camera', settings.camera)}
                            </span>
                          )}
                          {settings.style && (
                            <span className="px-3 py-1.5 bg-white/5 backdrop-blur-sm border border-white/20 rounded-lg text-white/90 text-xs font-medium hover:bg-white/10 transition-all">
                              {getLabelForValue('style', settings.style)}
                            </span>
                          )}
                          {settings.substyle && (
                            <span className="px-3 py-1.5 bg-white/5 backdrop-blur-sm border border-white/20 rounded-lg text-white/90 text-xs font-medium hover:bg-white/10 transition-all">
                              {getLabelForValue('substyle', settings.substyle)}
                            </span>
                          )}
                          {settings.image_format && (
                            <span className="px-3 py-1.5 bg-white/5 backdrop-blur-sm border border-white/20 rounded-lg text-white/90 text-xs font-medium hover:bg-white/10 transition-all">
                              {getLabelForValue('image_format', settings.image_format)}
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Übernehmen Button */}
                    {onUsePrompt && (
                      <button
                        onClick={() => {
                          onUsePrompt({
                            user_prompt: promptText,
                            settings: settings
                          });
                          setExpandedGeneration(null); // Close accordion
                        }}
                        className="w-full py-3 px-6 bg-[#10B981] hover:bg-[#059669] text-black font-bold text-sm rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg shadow-[#10B981]/20"
                      >
                        <CheckCircle className="w-5 h-5" />
                        Prompt übernehmen
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
            );
          }

          // Fallback für unbekannte Typen
          return null;
        })}
        </>
      )}
        </div>
      )}

      {/* Scroll to Top Button - Nicht im ReadOnly (SkipPage hat eigenen) */}
      {!readOnly && showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 p-4 bg-[#10B981] hover:bg-[#059669] text-black rounded-full shadow-2xl transition-all z-40 animate-bounce"
          title="Nach oben scrollen"
        >
          <ChevronUp className="w-6 h-6" />
        </button>
      )}

      {/* Lightbox */}
      {lightboxImages && (
        <Lightbox
          images={lightboxImages}
          initialIndex={lightboxIndex}
          onClose={closeLightbox}
          onRatingChange={handleRatingChange}
          readOnly={readOnly}
        />
      )}

      {/* Delete Confirm Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[99999] flex items-center justify-center p-4" onClick={() => setShowDeleteConfirm(null)}>
          <div className="bg-[#1A1A1A] border border-white/10 rounded-2xl p-6 max-w-sm w-full" onClick={e => e.stopPropagation()}>
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 rounded-full bg-red-500/20 flex items-center justify-center">
                <Trash2 className="w-7 h-7 text-red-400" />
              </div>
            </div>
            <h3 className="text-white text-lg font-bold text-center mb-2">
              {showDeleteConfirm.count} {showDeleteConfirm.type === 'videos' ? 'Video' : 'Bild'}{showDeleteConfirm.count > 1 ? (showDeleteConfirm.type === 'videos' ? 's' : 'er') : ''} löschen?
            </h3>
            <p className="text-white/50 text-sm text-center mb-6">
              Diese Aktion kann nicht rückgängig gemacht werden. Die Dateien werden unwiderruflich gelöscht.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setShowDeleteConfirm(null)} className="flex-1 py-3 rounded-xl bg-white/10 text-white font-medium hover:bg-white/20 transition-all">
                Abbrechen
              </button>
              <button onClick={() => showDeleteConfirm.type === 'videos' ? executeDeleteVideos() : executeDeleteImages()} className="flex-1 py-3 rounded-xl bg-red-500 text-white font-bold hover:bg-red-600 transition-all">
                Endgültig löschen
              </button>
            </div>
          </div>
        </div>
      )}

      {reformatErrorModal && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[99999] flex items-center justify-center p-4"
          onClick={() => setReformatErrorModal(null)}
        >
          <div
            className="bg-[#161616] border border-white/10 rounded-2xl p-6 max-w-sm w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 rounded-full bg-amber-500/15 flex items-center justify-center">
                <Sparkles className="w-7 h-7 text-amber-300" />
              </div>
            </div>
            <h3 className="text-white text-lg font-bold text-center mb-2">
              {reformatErrorModal.title}
            </h3>
            <p className="text-white/60 text-sm text-center mb-6 leading-relaxed">
              {reformatErrorModal.message}
            </p>
            <div className="flex justify-center">
              <button
                onClick={() => setReformatErrorModal(null)}
                className="px-5 py-3 rounded-xl bg-white text-black font-semibold hover:bg-white/90 transition-all"
              >
                Verstanden
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {shareImage && (
        <ShareModal
          image={shareImage}
          onClose={() => setShareImage(null)}
        />
      )}

      {/* Remix Fullscreen - FLUX LoRA Edit */}
      {remixImage && (
        <ImageRemixFullscreen
          isOpen={true}
          onClose={() => setRemixImage(null)}
          sourceImage={{
            url: remixImage.url,
            id: remixImage.generationId,
            prompt_text: remixImage.prompt
          }}
          avatarId={avatarId}
          trainingId={avatarId}
          onRemixComplete={(newImage) => {
            console.log('✅ Remix complete:', newImage);
            // Cache clearen
            clearCache();
            // Event dispatchen um IDCardDashboard zu triggern die Galerie neu zu laden
            window.dispatchEvent(new CustomEvent('galleryRefresh', { detail: { source: 'remix', newImage: newImage } }));
            console.log('📸 Remix fertig - galleryRefresh Event dispatched');
          }}
          onVideoGenerate={(img) => {
            // Remix schließen und Video-Modal mit dem Bild öffnen
            setRemixImage(null);
            setVideoImage({
              url: img.url,
              generationId: img.id,
              prompt: img.prompt_text
            });
          }}
        />
      )}

      {/* Modify Modal */}
      {modifyImage && onModify && (
        <ModifyModal
          isOpen={true}
          onClose={() => setModifyImage(null)}
          originalImage={modifyImage.url}
          originalPrompt={modifyImage.prompt}
          onModify={(modifyData) => {
            onModify({
              ...modifyData,
              imageUrl: modifyImage.url,
              generationId: modifyImage.generationId
            });
            setModifyImage(null);
          }}
          isGenerating={isGenerating}
        />
      )}

      {/* 🎬 Video Modal - AKTIV (nur für eingeloggte User) */}
      {videoImage && !readOnly && (
        <VideoGeneratorFullscreen
          image={videoImage}
          avatarId={avatarId}
          preloadedPhotos={(generations || []).flatMap(gen =>
            (gen.image_urls || [gen.image_url]).filter(Boolean).map((url, idx) => ({
              id: `${gen.id}_${idx}`,
              url: url,
              prompt: gen.prompt,
              created_at: gen.created_at
            }))
          ).filter(p => !hiddenImageIds.has(p.id))}
          onGenerate={() => {
            // Videos-Tab öffnen + sanft zur Video-Galerie scrollen
            setActiveTab('videos');
            requestAnimationFrame(() => {
              setTimeout(() => {
                const target =
                  document.getElementById('video-gallery-section') ||
                  document.querySelector('[data-testid="gallery-section"]');
                if (target) {
                  target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }, 150);
            });
          }}
          onClose={() => {
            setVideoImage(null);
            setActiveTab('videos');
            if (onStudioClose) onStudioClose();
          }}
          onVideoReady={(videoData) => {
            setGeneratorSourceImage(videoImage); // 🎬 Bild speichern für Rückkehr
            setVideoImage(null); // Generator schließen
            setFullscreenVideo(videoData); // VideoActionSheet öffnen
            setActiveTab('videos');
          }}
          onVideoCompleted={async () => {
            console.log('🎬 Video completed from ActionSheet! Refreshing...');
            try {
              const token = getToken();
              const response = await fetch(`/api/videos/list?avatar_id=${avatarId}&limit=500`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              if (response.ok) {
                const data = await response.json();
                const videoMap = {};
                (data.videos || []).forEach(v => {
                  if (v.status === 'completed' && v.video_url) {
                    const key = v.is_animation ? `animation_${v.id}` : (v.video_model === 'kino_modus' || v.video_model === 'hollywood' || !v.source_generation_id) ? `video_${v.id}` : `${v.source_generation_id}_${v.image_index}`;
                    videoMap[key] = v;
                  }
                });
                setVideos(videoMap);
                setCachedVideos(avatarId, videoMap);
              }
            } catch (err) {
              console.error('Video refresh error:', err);
            }
          }}
        />
      )}

      {/* VideoActionSheet für neu generierte Videos */}
      {fullscreenVideo && (
        <VideoActionSheet
          isOpen={true}
          video={fullscreenVideo}
          onClose={() => {
            setFullscreenVideo(null);
            // 🎬 Zurück zum Generator wenn von dort gekommen
            if (generatorSourceImage) {
              setVideoImage(generatorSourceImage);
              setGeneratorSourceImage(null);
            }
          }}
          onToggleFavorite={toggleFavorite}
          favorites={favorites}
          onDelete={async (video) => {
            const token = getToken();
            await fetch(`/api/video/${video.id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
            // Video aus Galerie entfernen
            setHiddenVideoIds(prev => new Set([...prev, video.id]));
            removeFromCache('video', video.id);
            setVideos(prev => {
              const updated = { ...prev };
              Object.keys(updated).forEach(k => { if (updated[k]?.id === video.id) delete updated[k]; });
              return updated;
            });
            setFullscreenVideo(null);
            setGeneratorSourceImage(null);
          }}
        />
      )}

      {/* 📌 Pinterest Action Sheet - mit Swipe Navigation */}
      <ImageActionSheet
        isOpen={!!actionSheetImage}
        onClose={(closedIndex, options = {}) => {
          try {
            const images = actionSheetImage?.images || [];
            const idx = typeof closedIndex === 'number' ? closedIndex : (actionSheetImage?.index || 0);
            const img = images[idx];
            const imageId = img?.id;
            setActionSheetImage(null);
            setTimeout(() => {
              if (options?.scrollToGalleryTop) {
                const gallerySection = document.querySelector('[data-testid="gallery-section"]');
                if (gallerySection) {
                  gallerySection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  return;
                }
                window.scrollTo({ top: 0, behavior: 'smooth' });
                return;
              }
              if (imageId) {
                const el = document.getElementById(`image-card-${imageId}`);
                if (el) el.scrollIntoView({ behavior: 'instant', block: 'center' });
              }
            }, 100);
          } catch (e) {
            console.error('onClose error:', e);
            setActionSheetImage(null);
          }
        }}
        image={actionSheetImage?.images ? null : actionSheetImage}
        images={actionSheetImage?.images || []}
        initialIndex={actionSheetImage?.index || 0}
        favorites={favorites}
        onToggleFavorite={toggleFavorite}
        onRefresh={onRefresh}
        onSetProfileImage={async (imageUrl, croppedBlob) => {
          try {
            const token = getToken();
            let resultUrl = imageUrl;

            if (croppedBlob) {
              // Zugeschnittenes Bild hochladen
              const formData = new FormData();
              formData.append('file', croppedBlob, 'profile.jpeg');
              formData.append('avatar_id', avatarId);
              const uploadRes = await fetch(`${window.API_URL || ''}/api/user/profile-image/upload`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
              });
              if (!uploadRes.ok) throw new Error('Upload failed');
              const uploadData = await uploadRes.json();
              resultUrl = uploadData.profile_image_url;
            } else {
              // Fallback: URL direkt setzen (ohne Crop)
              const response = await fetch(`${window.API_URL || ''}/api/user/profile-image`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ image_url: imageUrl, avatar_id: avatarId })
              });
              if (!response.ok) throw new Error('Failed to save');
            }

            window.dispatchEvent(new CustomEvent('profileImageUpdated', {
              detail: { imageUrl: resultUrl, avatarId }
            }));
            return true;
          } catch (err) {
            console.error('Profile image save failed:', err);
            throw err;
          }
        }}
        onShare={(img) => {
          setShareImage(img.url);
          setActionSheetImage(null);
        }}
        onPlay={(img) => {
          // Video abspielen - öffnet VideoGeneratorFullscreen mit existingVideo
          if (img.type === 'video' && img.data?.video_url) {
            setFullscreenVideo({
              id: img.data.id,
              video_url: img.data.video_url,
              video_prompt: img.data.video_prompt || '',
              user_prompt: img.data.user_prompt || img.data.video_prompt || '',
              image_analysis: img.data.image_analysis || '',
              source_image_url: img.data.source_image_url || img.thumbnail || '',
              original_speed: img.data.original_speed || 1,
              created_at: img.data.created_at,
              motion_tags: img.data.motion_tags || [],
              cost_snaps: img.data.cost_snaps,
              video_aspect_ratio: img.data.video_aspect_ratio,
              video_resolution: img.data.video_resolution
            });
          }
          setActionSheetImage(null);
        }}
        onVideo={readOnly ? null : (img) => {
          setActionSheetImage(null);
          if (onOpenVideoAccordion) {
            onOpenVideoAccordion(img?.url);
          } else {
            setActiveTab('videos');
          }
        }}
        onRemix={(img) => {
          // Für Videos: Nutze Quellbild, für Bilder: das Bild selbst
          const sourceUrl = img.type === 'video'
            ? (img.thumbnail || img.data?.source_image_url)
            : img.url;

          if (sourceUrl || img.generation) {
            // Schwarzer Overlay für butterweichen Übergang
            const overlay = document.createElement('div');
            overlay.id = 'remix-transition-overlay';
            overlay.style.cssText = 'position:fixed;inset:0;background:black;z-index:99998;';
            document.body.appendChild(overlay);
            
            setRemixImage({
              url: sourceUrl || img.url,
              prompt: img.prompt || img.data?.motion_prompt || '',
              generationId: img.genId || img.data?.source_generation_id,
              settings: img.generation?.generation_settings || {}
            });
            
            setTimeout(() => {
              setActionSheetImage(null);
              const ol = document.getElementById('remix-transition-overlay');
              if (ol) {
                ol.style.transition = 'opacity 0.15s';
                ol.style.opacity = '0';
                setTimeout(() => ol.remove(), 150);
              }
            }, 100);
          } else {
            setActionSheetImage(null);
          }
        }}
        onUsePrompt={(img) => {
          // Mehrere mögliche Prompt-Quellen prüfen
          const prompt = img.prompt || img.data?.motion_prompt || img.generation?.prompt || img.user_prompt;
          
          const showToast = (msg) => {
            const toast = document.createElement('div');
            toast.className = 'fixed top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 px-5 py-2.5 bg-black/70 backdrop-blur-md text-white rounded-full text-sm font-medium z-[999999]';
            toast.textContent = msg;
            document.body.appendChild(toast);
            setTimeout(() => toast.remove(), 2000);
          };
          
          if (onUsePrompt && prompt) {
            onUsePrompt({
              user_prompt: prompt,
              settings: img.generation?.generation_settings || {}
            });
            showToast('Prompt übernommen');
          } else if (!prompt) {
            showToast('Kein Prompt vorhanden');
          }
          setActionSheetImage(null);
        }}
        onDownload={async (img) => {
// Sofort Sheet schließen
          setActionSheetImage(null);

          // Spinner zeigen
          const overlay = showLoadingOverlay('Bild wird vorbereitet...');

          try {
            const response = await fetch(img.url);
            const blob = await response.blob();
            hideLoadingOverlay();

            // iOS: Share Sheet mit "Bild sichern" Option
            if (navigator.canShare && navigator.canShare({ files: [new File([blob], 'snapshot.jpg', { type: 'image/jpeg' })] })) {
              const file = new File([blob], `snapshot-${img.id || 'image'}.jpg`, { type: 'image/jpeg' });
              await navigator.share({
                files: [file],
                title: 'Snapshot speichern'
              });
              // Toast nach Share
              showSafeToast('Tippe auf "Bild sichern" 📸');
            } else {
              // Fallback: Klassischer Download
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = `snapshot-${img.id || 'image'}.jpg`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              window.URL.revokeObjectURL(url);

              showSafeToast('Bild heruntergeladen!');
            }
          } catch (err) {
            console.error('Download/Share error:', err);
            hideLoadingOverlay();
            if (err.name !== 'AbortError') {
              showErrorToast('Speichern fehlgeschlagen');
            }
          }
        }}
        onDelete={async (img) => {
          // Bestätigung passiert im ActionSheet - hier nur löschen
          if (img.id && img.genId) {
            await deleteImageDirect(img.id, img.genId);
          }
          // Sheet wird vom ActionSheet geschlossen nach erfolgreichem Löschen
        }}
        readOnly={readOnly}
      />

      {/* Multi-Select Bottom Bar */}
      {selectedForDelete.size > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-black/95 backdrop-blur-sm border-t border-white/10 p-4 z-50 flex items-center justify-between"
          style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
        >
          <div className="flex items-center gap-2">
            <span className="text-[#10B981] font-semibold text-lg">{selectedForDelete.size}</span>
            <span className="text-white/60 text-sm">ausgewählt</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={clearSelection}
              className="px-4 py-2 text-white/60 text-sm"
            >
              Abbrechen
            </button>
            <button
              onClick={confirmDeleteImages}
              disabled={isDeletingImages}
              className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-50"
            >
              {isDeletingImages ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
              {isDeletingImages ? 'Lösche...' : 'Löschen'}
            </button>
          </div>
        </div>
      )}

      {/* Pinterest-Style Fullscreen Viewer */}
      <ImageFullscreenViewer
        isOpen={!!fullscreenIndex}
        onClose={() => setFullscreenIndex(null)}
        images={fullscreenIndex?.images || []}
        initialIndex={fullscreenIndex?.index || 0}
        favorites={favorites}
        onToggleFavorite={toggleFavorite}
        onSetProfileImage={async (imageUrl, croppedBlob) => {
          try {
            const token = getToken();
            let resultUrl = imageUrl;

            if (croppedBlob) {
              // Zugeschnittenes Bild hochladen
              const formData = new FormData();
              formData.append('file', croppedBlob, 'profile.jpeg');
              formData.append('avatar_id', avatarId);
              const uploadRes = await fetch(`${window.API_URL || ''}/api/user/profile-image/upload`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
              });
              if (!uploadRes.ok) throw new Error('Upload failed');
              const uploadData = await uploadRes.json();
              resultUrl = uploadData.profile_image_url;
            } else {
              // Fallback: URL direkt setzen (ohne Crop)
              const response = await fetch(`${window.API_URL || ''}/api/user/profile-image`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ image_url: imageUrl, avatar_id: avatarId })
              });
              if (!response.ok) throw new Error('Failed to save');
            }

            // Event für sofortige UI-Aktualisierung
            window.dispatchEvent(new CustomEvent('profileImageUpdated', {
              detail: { imageUrl: resultUrl, avatarId }
            }));
            return true;
          } catch (err) {
            console.error('Profile image save failed:', err);
            throw err;
          }
        }}
        onDelete={onDelete ? async (genId, imageIndex) => {
          // Build image ID for API call
          const imageId = imageIndex !== undefined ? `${genId}_${imageIndex}` : `gen_${genId}`;

          console.log('🗑️ Fullscreen delete:', imageId);

          // Make actual API delete call
          try {
            await deleteImageDirect(imageId, genId);
          } catch (err) {
            console.error('Delete failed:', err);
            return; // Don't update UI if delete failed
          }

          // Update fullscreen images list - remove deleted image
          setFullscreenIndex(prev => {
            if (!prev?.images) return null;

            const deletedId = `${genId}_${imageIndex}`;
            const newImages = prev.images.filter(img => img.id !== deletedId);

            if (newImages.length === 0) {
              return null; // Close viewer if no images left
            }

            // Adjust index if needed
            let newIndex = prev.index || 0;
            if (newIndex >= newImages.length) {
              newIndex = newImages.length - 1;
            }

            return {
              ...prev,
              images: newImages,
              index: newIndex
            };
          });
        } : null}
        onVideo={readOnly ? null : (img) => {
          setFullscreenIndex(null);
          if (onOpenVideoAccordion) {
            onOpenVideoAccordion(img?.url);
          } else {
            setActiveTab('videos');
          }
        }}
      />
    </div>
  );
};

export { VideoGeneratorFullscreen };
export default Gallery;
